import React, { useState, useEffect, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import { getBytes, ref as storageRef } from "firebase/storage";
import { storage } from "../../services/firebaseConfig";
import { useParams, useNavigate } from "react-router-dom";
import { useBackToDashboard } from "../../utils/navigationUtils";
import icdCodesData from "../../data/icd.json";
import { suggestDiagnosisWithICD, reviewDiagnosesForApproval } from "../../services/diagnosisAIService";
import {
  ArrowLeft,
  Clock,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileText,
  Upload,
  Download,
  Eye,
  Plus,
  Trash2,
  Edit,
  Save,
  Send,
  MessageSquare,
  Calculator,
  DollarSign,
  Timer,
  Activity,
  User,
  Stethoscope,
  Heart,
  Loader,
  RefreshCw,
  Calendar,
  MapPin,
  Phone,
  Mail,
  CreditCard,
  Building,
  Shield,
  Award,
  Reply,
  Paperclip,
  X,
  Search,
  Filter,
  HelpCircle,
  List,
  Image,
  Type,
  File,
  FastForward,
  ThumbsUp,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ExternalLink,
  Info,
  Percent,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import {
  getPatientApplication,
  updateReviewActivity,
  createClarificationRequest,
  generateKYPLetter,
  processDischargeReview,
  submitForFinalApproval,
  uploadReviewDocument,
  getDischargeSummary,
  subscribeToApplicationUpdates,
  getApplicationDiscussions,
  sendDiscussionMessage,
  markMessagesAsRead,
  uploadDiscussionDocument,
  approveApplicationForProcess,
  saveInternalComments,
  getInternalComments,
  quitApplicationReview,
  pauseApplicationReview,
  updateMedicalData,
  setDispatchApprovalForDocument,
  setKYPApproval,
  respondToKYPChallenge,
  resetKYPApproval,
  setDischargeSettlementComments,
  setSanctionApproval,
  respondToSanctionChallenge,
  rejectApplication,
  reassignKYPForReview,
  markMedicalReviewCompleted,
  setDispatchStartApproval, // <-- new API import
  setDischargeSettlementApproval,
} from "../../services/doctorService";
import { getLeadGeneratorById } from "../../services/leadGeneratorService";
// Draft/autosave API
import { saveDischargeDraft } from "../../services/doctorService";
import { useAuth } from "../../context/AuthContext";
import KYPPreviewModal from "../../components/common/KYPPreviewModal";
import FALPreviewModal from "../../components/common/FALPreviewModal";
import { computeFalSettlement } from "../../services/falComputationService";
import { saveComputedDischargeSettlement } from "../../services/doctorService";
import { revokeFalPublication } from "../../services/doctorService";
import { generateLetter, getApplicationSanctions } from "../../services/applicationService";
import DiscussionComponent from '../../component/application/DiscussionComponent';
import { setApplicationRisk, getApplicationRisk } from "../../services/applicationService";
import DisputeManager from '../../components/common/DisputeManager';
import { checkApplicationDisputeStatus, getApplicationDisputes } from '../../services/disputeService';
import { getHospitalTariffDocuments } from '../../services/hospitalService';
import { mergeDocumentsToPdf, downloadBlob } from '../../utils/mergePdf';
import GlobalSearchBar from '../../components/common/GlobalSearchBar';
import { raiseDocumentQuery, getDocumentQueries } from '../../services/documentQueryService';

// Force file download ensuring images and cross-origin URLs don't just open in a new tab
async function downloadFile(url, filename = 'download') {
  // helper to infer mime from filename
  const inferMimeFromName = (name = '') => {
    const lower = (name || '').toLowerCase();
    if (lower.endsWith('.pdf')) return 'application/pdf';
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
    if (lower.endsWith('.png')) return 'image/png';
    if (lower.endsWith('.webp')) return 'image/webp';
    if (lower.endsWith('.csv')) return 'text/csv';
    if (lower.endsWith('.xls')) return 'application/vnd.ms-excel';
    if (lower.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    return 'application/octet-stream';
  };
  const triggerDownload = (blob, name, contentTypeHint = '') => {
    let safeName = name || 'download';
    // If no extension, append based on mime
    if (!/\.[a-z0-9]{2,}$/i.test(safeName)) {
      const type = (contentTypeHint || inferMimeFromName(name) || '').split(';')[0];
      const extMap = {
        'application/pdf': '.pdf',
        'image/jpeg': '.jpg',
        'image/png': '.png',
        'image/webp': '.webp',
        'text/csv': '.csv',
        'application/vnd.ms-excel': '.xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
      };
      const guessed = extMap[type];
      if (guessed) safeName += guessed;
    }
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = safeName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  };

  // 1) Try Firebase Storage SDK first (works reliably with Storage download URLs)
  try {
    const ref = storageRef(storage, url);
    const bytes = await getBytes(ref);
    const blob = new Blob([bytes], { type: inferMimeFromName(filename) });
    triggerDownload(blob, filename);
    return;
  } catch (_) {
    // ignore and try fetch next
  }

  // 2) Fallback to fetch -> blob with content-type inference
  try {
    const response = await fetch(url, { mode: 'cors' });
    const contentType = response.headers.get('content-type') || '';
    const blob = await response.blob();
    triggerDownload(blob, filename, contentType);
    return;
  } catch (err) {
    // 3) Final fallback: open in new tab if everything else fails (CORS or network)
    window.open(url, '_blank', 'noopener');
  }
}

const DoctorApplicationReview = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const { userData: user } = useAuth();
  const goBackToDashboard = useBackToDashboard();
  const timerRef = useRef(null);
  const activityRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const dischargeFileInputRef = useRef(null);

  // Core app state
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [isApprovedForProcess, setIsApprovedForProcess] = useState(false);
  // Effective approval flag (sticky): once approved, keep UI approved even if a later update omits the flag,
  // and also respect top-level status written by backend.
  const approvedForProcessEffective = Boolean(
    isApprovedForProcess ||
    application?.status === 'approved_for_process' ||
    application?.workflow?.medicalVerification?.approvedForProcess
  );
  // When a revised KYP is approved after reassignment, lock KYP actions and edits
  const isKypApproved = Boolean(
    application?.workflow?.medicalVerification?.kypApproval?.status === 'approved'
  );
  const isKypReassigned = Boolean(
    application?.workflow?.medicalVerification?.kypReassignment
  );
  const disableKypActions = isKypApproved && isKypReassigned;
  // Once KYP is approved, inputs should be locked (read-only)
  const isKypReadOnly = Boolean(
    application?.workflow?.medicalVerification?.kypApproval?.status === 'approved'
  );
  // Case category indicator (KYP | Scrutiny | Discharge)
  const ds = application?.workflow?.dischargeSettlement || {};
  const dischargeStarted = Boolean(
    application?.dischargeInitiated || ds?.status ||
    (Array.isArray(ds?.uploadedDocuments) && ds.uploadedDocuments.length > 0) ||
    (Array.isArray(application?.dischargeDocuments) && application.dischargeDocuments.length > 0)
  );
  const falPublished = Boolean(ds?.finalApprovalLetterUrl);
  const hasScrutinySignals = Boolean(
    falPublished || ds?.scrutinyReview || ds?.scrutinyApproval || (dischargeStarted && !falPublished)
  );
  const caseCategory = dischargeStarted ? 'Discharge' : (hasScrutinySignals ? 'Scrutiny' : 'KYP');
  const caseBadgeConfig = (
    caseCategory === 'Discharge' ? { label: 'Discharge Case', icon: '🏥', cls: 'bg-rose-50 text-rose-700 border-rose-200' } :
    caseCategory === 'Scrutiny' ? { label: 'Scrutiny Case', icon: '🧪', cls: 'bg-amber-50 text-amber-700 border-amber-200' } :
    { label: 'KYP Case', icon: '🧭', cls: 'bg-teal-50 text-teal-700 border-teal-200' }
  );
  const [actionLoading, setActionLoading] = useState({});
  const [activeTab, setActiveTab] = useState("application");

  // Discussions and messaging state
  const [discussions, setDiscussions] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Check if FAL is challenged and doctor accepted to revise
  const falId = application?.workflow?.dischargeSettlement?.finalApprovalLetterId;
  const falAck = falId ? application?.workflow?.acknowledgements?.[falId] : null;
  const isFALChallenged = falAck?.status === 'challenged';
  const doctorAcceptedFALRevision = falAck?.doctorResponse?.decision === 'accept_revision';
  
  // Lock discharge actions once Final Approval Letter is published for hospital
  // BUT allow editing if FAL is challenged and doctor accepted to revise
  const isFALPublished = Boolean(
    application?.workflow?.dischargeSettlement?.finalApprovalLetterUrl &&
    !(isFALChallenged && doctorAcceptedFALRevision)
  );
  const [newMessage, setNewMessage] = useState("");
  const [messageType, setMessageType] = useState("text"); // 'text' | 'file' | 'qna'
  const [replyingTo, setReplyingTo] = useState(null);
  const [showQnAForm, setShowQnAForm] = useState(false);
  const [qnaQuestions, setQnaQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState({
    question: "",
    required: false,
    answerType: "text",
    description: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterUser, setFilterUser] = useState("all");

  // Dispute state (scoped to medical department only)
  const [disputeStatus, setDisputeStatus] = useState({
    hasActiveDisputes: false,
    activeDisputes: [],
    canProceed: true,
  });

  // All disputes state (across all departments - used for KYP generation blocking)
  const [allDisputesStatus, setAllDisputesStatus] = useState({
    hasActiveDisputes: false,
    activeDisputes: [],
  });

  // KYP state
  const [kypData, setKypData] = useState({
    roomEligibility: "",
    coPay: "",
    preExistingConditions: "",
    recommendation: "",
    clinicalNotes: "",
    letterRemarks: "",
    // Newly enforced default validity period for KYP letters
    validityPeriod: "15 days",
    // Medical Verification Checklist (must be completed before generating KYP)
    medicalHistoryVerified: false,
    documentsAuthentic: false,
    treatmentAppropriate: false,
  });
  const [showKYPPreview, setShowKYPPreview] = useState(false);
  const [kypPreviewData, setKYPPreviewData] = useState(null);
  const [leadGeneratorData, setLeadGeneratorData] = useState(null);
  const [kypApprovalLoading, setKypApprovalLoading] = useState(false);
  const [kypChallengeActionLoading, setKypChallengeActionLoading] = useState(false);
  const [kypReassignLoading, setKypReassignLoading] = useState(false);

  // Document query state
  const [documentQueries, setDocumentQueries] = useState({});
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [queryDocumentKey, setQueryDocumentKey] = useState(null);
  const [queryComment, setQueryComment] = useState('');
  // Unified timeline state
  const [timelineEvents, setTimelineEvents] = useState([]);

  // Discharge review state
  const [dischargeItems, setDischargeItems] = useState([]);
  const [dischargeComments, setDischargeComments] = useState("");
  const [dischargeSummary, setDischargeSummary] = useState(null);
  const [dischargeSaving, setDischargeSaving] = useState(false);
  const [dischargeLastSaved, setDischargeLastSaved] = useState(null);
  // FAL state
  const [falRemarks, setFalRemarks] = useState("");
  const [falActionLoading, setFalActionLoading] = useState({});
  // Extraction review modal state
  const [showExtractModal, setShowExtractModal] = useState(false);
  const [extractedItemsReview, setExtractedItemsReview] = useState([]); // [{...mapped, selected: true}]
  const [falLetter, setFalLetter] = useState(null);
  const [showFALPreview, setShowFALPreview] = useState(false);
  // Settlement UI state (overrides and manual adjustments)
  const [falOverrides, setFalOverrides] = useState({
    copay: { enabled: false, amountOverride: "", percentOverride: "", reason: "" },
    copaySelection: {}, // Track which copays to include: { general: true, nonNetwork: false, etc. }
    sublimits: [], // entries: { key: 'sublimit_0', amountOverride: '', reason: '' }
  });
  const [falManualAdjustments, setFalManualAdjustments] = useState([]); // entries: { id, label, amount, reason }
  const [falComputedPreview, setFalComputedPreview] = useState(null);
  // isSettlementApproved persists if saved in workflow.dischargeSettlement.calculationApproved
  const [isSettlementApproved, setIsSettlementApproved] = useState(false);

  // Initialize copaySelection from policy verification when available
  useEffect(() => {
    if (application?.workflow?.policyVerification?.coPayStructure && !Object.keys(falOverrides.copaySelection).length) {
      const cp = application.workflow.policyVerification.coPayStructure;
      const initialSelection = {};
      
      // Initialize all applicable copays as included by default
      if (cp.generalCoPayApplicable && cp.generalCoPayValue > 0) {
        initialSelection.general = true;
      }
      if (cp.nonNetworkCoPayApplicable && cp.nonNetworkCoPayValue > 0) {
        initialSelection.nonNetwork = true;
      }
      if (cp.locationBasedCoPayApplicable) {
        if (cp.metroCoPayValue > 0) initialSelection.locationMetro = true;
        if (cp.nonMetroCoPayValue > 0) initialSelection.locationNonMetro = true;
      }
      if (cp.parentalCoPayApplicable && cp.parentalCoPayValue > 0) {
        initialSelection.parental = true;
      }
      if (cp.mandatoryCoPayApplicable && cp.mandatoryCoPayValue > 0) {
        initialSelection.mandatory = true;
      }
      if (cp.voluntaryCoPayApplicable && cp.voluntaryCoPayValue > 0) {
        initialSelection.voluntary = true;
      }
      if (cp.nonIntimationCoPayApplicable && cp.nonIntimationCoPayValue > 0) {
        initialSelection.nonIntimation = true;
      }
      
      if (Object.keys(initialSelection).length > 0) {
        setFalOverrides(prev => ({
          ...prev,
          copaySelection: initialSelection
        }));
      }
    }
  }, [application?.workflow?.policyVerification?.coPayStructure]);

  // Live compute preview based on current controls
  useEffect(() => {
    try {
      const overridesWithMeta = {
        ...falOverrides,
        copay: falOverrides?.copay?.enabled
          ? { ...falOverrides.copay, by: user?.uid, at: new Date().toISOString() }
          : undefined,
        copaySelection: falOverrides.copaySelection,
        sublimitSelection: falOverrides.sublimitSelection,
        sublimits: Array.isArray(falOverrides?.sublimits)
          ? falOverrides.sublimits.map((s) => ({ ...s, by: user?.uid, at: new Date().toISOString() }))
          : [],
      };
      const computed = computeFalSettlement(
        application || {},
        {
          policy: application?.workflow?.policyVerification,
          dischargeItems,
          overrides: overridesWithMeta,
          manualAdjustments: falManualAdjustments,
          rounding: 'nearest',
        }
      );
      setFalComputedPreview(computed);
    } catch (e) {
      // non-blocking
    }
  }, [application, dischargeItems, falOverrides, falManualAdjustments, user?.uid]);

  // Any change to overrides/adjustments invalidates settlement approval
  useEffect(() => {
    // Any local change to overrides or manual adjustments requires re-approval
    setIsSettlementApproved(false);
  }, [falOverrides, falManualAdjustments]);

  // Review timer state
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [reviewStartTime, setReviewStartTime] = useState(null);

  // Policy verification expandable state
  const [isPolicyExpanded, setIsPolicyExpanded] = useState(false);

  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [newDischargeItem, setNewDischargeItem] = useState({
    itemName: "",
    category: "medical",
  payableType: 'payable', // payable | non_payable | deduction
    unitRate: "",
    eligibleUnitRate: "",
    quantity: "",
    billedAmount: "",
    eligibleAmount: "",
    remarks: "",
  approved: true,
  _billedManual: false,
  _eligibleManual: false,
  });
  // Minimal validation state
  const mandatoryFields = ["itemName", "quantity", "eligibleAmount"]; // 3 required fields
  const validateItem = (it) => {
    const errs = {};
    if (!it.itemName || !it.itemName.trim()) errs.itemName = "Required";
    const qty = parseFloat(it.quantity);
    if (!(qty > 0)) errs.quantity = "> 0";
    const eligible = parseFloat(it.eligibleAmount);
    if (!(eligible >= 0)) errs.eligibleAmount = ">= 0";
    // Require remarks when non-payable or deduction selected
    if ((it.payableType === 'non_payable' || it.payableType === 'deduction') && !((it.remarks || '').trim())) {
      errs.remarks = 'Required for non-payable/deduction';
    }
    return errs;
  };
  const [itemErrors, setItemErrors] = useState({});
  const [recommendation, setRecommendation] = useState("approve");
  const [dischargeFiles, setDischargeFiles] = useState([]);
  const [lastAutoSaveAt, setLastAutoSaveAt] = useState(null);

  // Modal states
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  // Preserve scroll position when opening/closing the document panel
  const [lastScrollY, setLastScrollY] = useState(0);
  const [lastRightScrollTop, setLastRightScrollTop] = useState(0);
  const rightPanelRef = useRef(null);

  // PDF zoom state for document viewer
  const [pdfZoom, setPdfZoom] = useState(100);
  // Image zoom state for document viewer
  const [imageZoom, setImageZoom] = useState(100);

  // Excel viewer state for inline rendering
  const [excelData, setExcelData] = useState(null);
  const [excelSheets, setExcelSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelError, setExcelError] = useState(null);

  // Hospital tariff documents state
  const [tariffDocuments, setTariffDocuments] = useState(null);
  const [loadingTariffDocuments, setLoadingTariffDocuments] = useState(false);
  const [tariffError, setTariffError] = useState(null);

  // Collapsible sections state - similar to PolicyApplicationDetails
  const [collapsedSections, setCollapsedSections] = useState({
    patient: true,
    medical: true,
    policy: false,
    hospital: true,
    financial: true,
    documents: false,
  internalComments: false,  // Collapsible state for Internal Comments (expanded by default)
    rightPanelMedical: false,  // Separate state for right panel medical section
  dischargeDocs: false,      // State for discharge documents section
  dispatchDocs: false,       // State for dispatch documents section
  risk: false                // Risky Application flag section
  });

  // Discussion panel state
  const [showDiscussionPanel, setShowDiscussionPanel] = useState(false);

  // Internal comments state
  const [internalComments, setInternalComments] = useState("");
  const [savedInternalComments, setSavedInternalComments] = useState("");
  const [commentsSaving, setCommentsSaving] = useState(false);
  const [allInternalComments, setAllInternalComments] = useState([]); // Store comments from all doctors

  // Risk flag state
  const [riskInfo, setRiskInfo] = useState({ flagged: false, comment: '', flaggedByName: '', flaggedAt: null, notes: [] });
  const [riskComment, setRiskComment] = useState('');
  const [riskSaving, setRiskSaving] = useState(false);

  // Derived: is medical checklist complete
  const isMedicalChecklistComplete = Boolean(
    kypData.medicalHistoryVerified &&
    kypData.documentsAuthentic &&
    kypData.treatmentAppropriate
  );

  // Load Risk flag info for this application
  useEffect(() => {
    let cancelled = false;
    const loadRisk = async () => {
      if (!applicationId) return;
      try {
        const res = await getApplicationRisk(applicationId);
        if (res?.success && !cancelled) {
          const r = res.risk || {};
          setRiskInfo({
            flagged: !!r.flagged,
            comment: r.comment || '',
            flaggedByName: r.flaggedByName || '',
            flaggedAt: r.flaggedAt || null,
            notes: Array.isArray(r.notes) ? r.notes : []
          });
        }
      } catch (e) {
        // ignore; non-blocking
      }
    };
    loadRisk();
    return () => { cancelled = true; };
  }, [applicationId]);

  // Set/Unset Risk flag with comment
  async function handleSetRiskFlag(flag) {
    try {
      setRiskSaving(true);
      const res = await setApplicationRisk(applicationId, {
        flagged: flag,
        comment: riskComment,
        by: user?.uid,
        byName: user?.profile?.name || user?.email || 'Doctor'
      });
      if (res?.success) {
        const r = res.risk || {};
        setRiskInfo({
          flagged: !!r.flagged,
          comment: r.comment || '',
          flaggedByName: r.flaggedByName || '',
          flaggedAt: r.flaggedAt || null,
          notes: Array.isArray(r.notes) ? r.notes : []
        });
        setRiskComment('');
      }
    } catch (e) {
      // Optionally add toast later
    } finally {
      setRiskSaving(false);
    }
  }

  // Diagnosis editing state
  const [isEditingDiagnosis, setIsEditingDiagnosis] = useState(false);
  const [isAddingDiagnosis, setIsAddingDiagnosis] = useState(false);
  const [editingDiagnosisId, setEditingDiagnosisId] = useState(null);
  const [editedDiagnosis, setEditedDiagnosis] = useState("");
  const [editedTreatmentType, setEditedTreatmentType] = useState("");
  const [editedProcedureName, setEditedProcedureName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [editedIcdCode, setEditedIcdCode] = useState("");
  const [icdSearchTerm, setIcdSearchTerm] = useState("");
  const [showIcdSuggestions, setShowIcdSuggestions] = useState(false);
  const [selectedIcdIndex, setSelectedIcdIndex] = useState(-1);

  // AI Diagnosis Suggestion state (Add form)
  const [aiDiagLoadingAdd, setAiDiagLoadingAdd] = useState(false);
  const [aiDiagSuggestionsAdd, setAiDiagSuggestionsAdd] = useState([]);
  const [aiDiagErrorAdd, setAiDiagErrorAdd] = useState("");
  const [showAiPanelAdd, setShowAiPanelAdd] = useState(false);

  // AI Diagnosis Suggestion state (Edit card)
  const [aiDiagLoadingEdit, setAiDiagLoadingEdit] = useState(false);
  const [aiDiagSuggestionsEdit, setAiDiagSuggestionsEdit] = useState([]);
  const [aiDiagErrorEdit, setAiDiagErrorEdit] = useState("");
  const [showAiPanelEdit, setShowAiPanelEdit] = useState(false);

  // AI Approval Assistant (review existing diagnoses and recommend approval)
  const [aiApproveLoading, setAiApproveLoading] = useState(false);
  const [aiApproveResult, setAiApproveResult] = useState(null); // { approveRecommended, reasons, suggestionsById }
  const [aiApproveError, setAiApproveError] = useState("");
  const [showAiApprovePanel, setShowAiApprovePanel] = useState(false);

  const runAiApprovalReview = async () => {
    try {
      setAiApproveError("");
      setAiApproveLoading(true);
      setShowAiApprovePanel(true);
      const treatments = Array.isArray(application?.medical?.treatments) ? application.medical.treatments : [];
      const { success, approveRecommended, reasons, suggestionsById } = await reviewDiagnosesForApproval({
        applicationId,
        treatments: treatments.map(t => ({
          id: t.id,
          diagnosisName: t.diagnosisName,
          description: t.description,
          icdCode: t.icdCode,
          icdDescription: t.icdDescription,
          treatmentType: t.treatmentType,
          procedureName: t.procedureName,
        })),
        patient: { age: application?.patient?.age, gender: application?.patient?.gender },
        fillMissingOnly: true,
      });
      if (!success) throw new Error('AI review failed');
      setAiApproveResult({ approveRecommended, reasons, suggestionsById });
    } catch (e) {
      console.error('AI approval review error', e);
      setAiApproveError(e?.message || 'Failed to run AI review');
      setAiApproveResult(null);
    } finally {
      setAiApproveLoading(false);
    }
  };

  const applyAiApprovalSuggestions = async () => {
    if (!aiApproveResult?.suggestionsById) return;
    const map = aiApproveResult.suggestionsById || {};
    // Fill missing ICD codes and descriptions using suggestions
    const updatedTreatments = (application?.medical?.treatments || []).map(t => {
      const sug = map[String(t.id)];
      if (!sug) return t;
      const next = { ...t };
      if (!next.icdCode) next.icdCode = sug.icdCode;
      if (!next.icdDescription) next.icdDescription = sug.icdTitle || next.icdDescription;
      // If description empty, seed with reasoning
      if (!next.description || !String(next.description).trim()) {
        next.description = sug.reasoning || next.description;
      }
      return next;
    });

    try {
      setActionLoading((p) => ({ ...p, applyAiApproval: true }));
      const updatedMedicalData = { ...application.medical, treatments: updatedTreatments };
      const result = await updateMedicalData(applicationId, updatedMedicalData, user.uid);
      if (result?.success) {
        setApplication((prev) => ({ ...prev, medical: updatedMedicalData }));
        // Refresh review result to see if now ready
        await runAiApprovalReview();
        alert('Applied AI ICD suggestions to diagnoses');
      } else {
        throw new Error(result?.error || 'Failed to save');
      }
    } catch (e) {
      alert(e?.message || 'Failed to apply suggestions');
    } finally {
      setActionLoading((p) => ({ ...p, applyAiApproval: false }));
    }
  };
  // Review state (kept minimal; medical review UI is disabled but state is referenced)
  const [reviewDecision, setReviewDecision] = useState("");
  const [reviewComments, setReviewComments] = useState("");

  // Treatment types for the dropdown
  const treatmentTypes = ["medical", "surgical"];


  // Transform ICD codes data from JSON file
  const icdCodes = icdCodesData.map((item, index) => ({
    code: item.DIAG_Code, // Generate ICD codes like ICD-001, ICD-002, etc.
    description: item.DIAG_Description
  }));

  // Filter ICD codes based on search term
  const getFilteredIcdCodes = () => {
    if (!icdSearchTerm.trim()) return [];
    return icdCodes.filter(
      icd =>
        icd.code.toLowerCase().includes(icdSearchTerm.toLowerCase()) ||
        icd.description.toLowerCase().includes(icdSearchTerm.toLowerCase())
    ).slice(0, 10); // Limit to 10 suggestions
  };

  // Handle ICD code selection
  const handleIcdSelection = (icd) => {
    setEditedIcdCode(icd.code);
    setIcdSearchTerm(`${icd.code} - ${icd.description}`);
    setShowIcdSuggestions(false);
    setSelectedIcdIndex(-1);
  };

  // Fetch AI suggestions for Add Diagnosis form
  const fetchAiSuggestionsForAdd = async () => {
    try {
      setAiDiagErrorAdd("");
      setAiDiagLoadingAdd(true);
      setShowAiPanelAdd(true);
      const { success, suggestions } = await suggestDiagnosisWithICD({
        diagnosisText: editedDiagnosis,
        notesText: editedDescription,
        age: application.patient?.age,
        gender: application.patient?.gender,
        topK: 3,
      });
      setAiDiagSuggestionsAdd(success ? (suggestions || []) : []);
      if (!success || !suggestions?.length) {
        setAiDiagErrorAdd("No suggestions returned. Try adding more clinical details.");
      }
    } catch (e) {
      console.error("AI suggest (add) error", e);
      setAiDiagErrorAdd(e?.message || "Failed to get suggestions");
      setAiDiagSuggestionsAdd([]);
    } finally {
      setAiDiagLoadingAdd(false);
    }
  };

  const acceptAiSuggestionAdd = (sug) => {
    if (!sug) return;
    const icd = icdCodes.find((x) => x.code === sug.icdCode);
    setEditedDiagnosis(sug.diagnosis || editedDiagnosis);
    setEditedIcdCode(sug.icdCode || editedIcdCode);
    const icdDesc = icd?.description || sug.icdTitle || "";
    setIcdSearchTerm(sug.icdCode ? `${sug.icdCode} - ${icdDesc}` : icdDesc);
    setShowIcdSuggestions(false);
    setSelectedIcdIndex(-1);
    // If no description provided by doctor yet, seed it with AI reasoning (one-liner)
    setEditedDescription((prev) => (prev && prev.trim()) ? prev : (sug.reasoning || prev || ""));
    setShowAiPanelAdd(false);
  };

  // Fetch AI suggestions for Edit Diagnosis card
  const fetchAiSuggestionsForEdit = async () => {
    try {
      setAiDiagErrorEdit("");
      setAiDiagLoadingEdit(true);
      setShowAiPanelEdit(true);
      const { success, suggestions } = await suggestDiagnosisWithICD({
        diagnosisText: editedDiagnosis,
        notesText: editedDescription,
        age: application.patient?.age,
        gender: application.patient?.gender,
        topK: 3,
      });
      setAiDiagSuggestionsEdit(success ? (suggestions || []) : []);
      if (!success || !suggestions?.length) {
        setAiDiagErrorEdit("No suggestions returned. Try adding more clinical details.");
      }
    } catch (e) {
      console.error("AI suggest (edit) error", e);
      setAiDiagErrorEdit(e?.message || "Failed to get suggestions");
      setAiDiagSuggestionsEdit([]);
    } finally {
      setAiDiagLoadingEdit(false);
    }
  };

  const acceptAiSuggestionEdit = (sug) => {
    if (!sug) return;
    const icd = icdCodes.find((x) => x.code === sug.icdCode);
    setEditedDiagnosis(sug.diagnosis || editedDiagnosis);
    setEditedIcdCode(sug.icdCode || editedIcdCode);
    const icdDesc = icd?.description || sug.icdTitle || "";
    setIcdSearchTerm(sug.icdCode ? `${sug.icdCode} - ${icdDesc}` : icdDesc);
    setShowIcdSuggestions(false);
    setSelectedIcdIndex(-1);
    setEditedDescription((prev) => (prev && prev.trim()) ? prev : (sug.reasoning || prev || ""));
    setShowAiPanelEdit(false);
  };

  // Handle keyboard navigation for ICD suggestions
  const handleIcdKeyDown = (e) => {
    const filteredIcdCodes = icdCodes.filter(
      icd =>
        icd.code.toLowerCase().includes(icdSearchTerm.toLowerCase()) ||
        icd.description.toLowerCase().includes(icdSearchTerm.toLowerCase())
    );

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIcdIndex(prev =>
        prev < filteredIcdCodes.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIcdIndex(prev => prev > 0 ? prev - 1 : 0);
    } else if (e.key === "Enter" && selectedIcdIndex >= 0) {
      e.preventDefault();
      handleIcdSelection(filteredIcdCodes[selectedIcdIndex]);
    } else if (e.key === "Escape") {
      setShowIcdSuggestions(false);
      setSelectedIcdIndex(-1);
    }
  };

  // Initialize editing state when starting diagnosis edit
  const startDiagnosisEdit = (treatmentId) => {
    // Gate editing unless reassigned to doctor or not approved for process
    const reassignedToDoctor = Boolean(
      application?.workflow?.medicalVerification?.kypReassignment && (
        application.workflow.medicalVerification.kypReassignment.target === 'doctor' ||
        (Array.isArray(application.workflow.medicalVerification.kypReassignment.targets) && application.workflow.medicalVerification.kypReassignment.targets.includes('doctor'))
      )
    );
  if ((approvedForProcessEffective && !reassignedToDoctor) || disableKypActions) return;
    const treatment = application.medical?.treatments?.find(t => t.id === treatmentId);
    if (treatment) {
      setEditingDiagnosisId(treatmentId);
      setEditedDiagnosis(treatment.diagnosisName || "");
      setEditedTreatmentType(treatment.treatmentType || "");
      setEditedProcedureName(treatment.procedureName || "");
      setEditedDescription(treatment.description || "");
      setEditedIcdCode(treatment.icdCode || "");
      setIcdSearchTerm(
        treatment.icdCode
          ? `${treatment.icdCode} - ${treatment.icdDescription || ""}`
          : ""
      );
      setIsEditingDiagnosis(true);
    }
  };

  // Cancel diagnosis editing
  const cancelDiagnosisEdit = () => {
    setIsEditingDiagnosis(false);
    setEditingDiagnosisId(null);
    setEditedDiagnosis("");
    setEditedTreatmentType("");
    setEditedProcedureName("");
    setEditedDescription("");
    setEditedIcdCode("");
    setIcdSearchTerm("");
    setShowIcdSuggestions(false);
    setSelectedIcdIndex(-1);
  };

  // Start adding a new diagnosis
  const startAddDiagnosis = () => {
    const reassignedToDoctor = Boolean(
      application?.workflow?.medicalVerification?.kypReassignment && (
        application.workflow.medicalVerification.kypReassignment.target === 'doctor' ||
        (Array.isArray(application.workflow.medicalVerification.kypReassignment.targets) && application.workflow.medicalVerification.kypReassignment.targets.includes('doctor'))
      )
    );
  if ((approvedForProcessEffective && !reassignedToDoctor) || disableKypActions) return; // do nothing if already approved and not reassigned to doctor or KYP approved
    setIsAddingDiagnosis(true);
    // Reset any edit state and fields for fresh add
    setIsEditingDiagnosis(false);
    setEditingDiagnosisId(null);
    setEditedDiagnosis("");
    setEditedTreatmentType("");
    setEditedProcedureName("");
    setEditedDescription("");
    setEditedIcdCode("");
    setIcdSearchTerm("");
    setShowIcdSuggestions(false);
    setSelectedIcdIndex(-1);
  };

  const cancelAddDiagnosis = () => {
    setIsAddingDiagnosis(false);
    setEditedDiagnosis("");
    setEditedTreatmentType("");
    setEditedProcedureName("");
    setEditedDescription("");
    setEditedIcdCode("");
    setIcdSearchTerm("");
    setShowIcdSuggestions(false);
    setSelectedIcdIndex(-1);
  };

  const saveNewDiagnosis = async () => {
    if (!editedDiagnosis.trim()) {
      alert("Please enter a diagnosis");
      return;
    }
    // Enforce ICD code & description presence
    const selectedIcdForNew = icdCodes.find(icd => icd.code === editedIcdCode);
    if (!editedIcdCode || !editedIcdCode.trim() || !selectedIcdForNew?.description) {
      alert("ICD Code and Description are mandatory. Please select a valid ICD entry from suggestions.");
      return;
    }

    setActionLoading((prev) => ({ ...prev, saveNewDiagnosis: true }));
    try {
      const newTreatment = {
        id: Date.now(),
        diagnosisName: editedDiagnosis,
        treatmentType: editedTreatmentType,
        procedureName: editedProcedureName,
        description: editedDescription,
        icdCode: editedIcdCode,
        icdDescription: selectedIcdForNew.description,
        createdAt: new Date(),
        createdBy: user.uid,
        reviewerName: user.profile?.name || user.email
      };

      const existingTreatments = application.medical?.treatments || [];
      const updatedMedicalData = {
        ...application.medical,
        treatments: [...existingTreatments, newTreatment]
      };

      const result = await updateMedicalData(applicationId, updatedMedicalData, user.uid);

      if (result.success) {
        setApplication(prev => ({
          ...prev,
          medical: updatedMedicalData
        }));
        // Clear fields
        setEditedDiagnosis("");
        setEditedTreatmentType("");
        setEditedProcedureName("");
        setEditedDescription("");
        setEditedIcdCode("");
        setIcdSearchTerm("");
        alert("Diagnosis added successfully. You can add another or close.");
      } else {
        alert("Failed to add diagnosis: " + result.error);
      }
    } catch (error) {
      console.error("Error adding diagnosis:", error);
      alert("Failed to add diagnosis");
    } finally {
      setActionLoading((prev) => ({ ...prev, saveNewDiagnosis: false }));
    }
  };

  // Save diagnosis changes
  const saveDiagnosisChanges = async () => {
    if (!editedDiagnosis.trim()) {
      alert("Please enter a diagnosis");
      return;
    }
    // ICD enforcement for edits
    const selectedIcdForEdit = icdCodes.find(icd => icd.code === editedIcdCode);
    if (!editedIcdCode || !editedIcdCode.trim() || !selectedIcdForEdit?.description) {
      alert("ICD Code and Description are mandatory. Please select a valid ICD entry from suggestions.");
      return;
    }

    setActionLoading((prev) => ({ ...prev, saveDiagnosis: true }));
    try {

      // Update the specific treatment in the application state
      const updatedTreatments = application.medical.treatments.map(treatment =>
        treatment.id === editingDiagnosisId
          ? {
            ...treatment,
            diagnosisName: editedDiagnosis,
            treatmentType: editedTreatmentType,
            procedureName: editedProcedureName,
            description: editedDescription,
            icdCode: editedIcdCode,
            icdDescription: selectedIcdForEdit.description,
            reviewedBy: user.uid,
            reviewedAt: new Date(),
            reviewerName: user.profile?.name || user.email
          }
          : treatment
      );

      const updatedMedicalData = {
        ...application.medical,
        treatments: updatedTreatments
      };

      // Save to database
      const result = await updateMedicalData(applicationId, updatedMedicalData, user.uid);

      if (result.success) {
        // Update local state only after successful database update
        setApplication(prev => ({
          ...prev,
          medical: updatedMedicalData
        }));

        setIsEditingDiagnosis(false);
        setEditingDiagnosisId(null);
        alert("Diagnosis updated and saved successfully");
      } else {
        alert("Failed to save diagnosis: " + result.error);
      }
    } catch (error) {
      console.error("Error updating diagnosis:", error);
      alert("Failed to update diagnosis");
    } finally {
      setActionLoading((prev) => ({ ...prev, saveDiagnosis: false }));
    }
  };



  const tabs = [
    { id: "application", label: "Application Review", icon: FileText },
    { id: "documents", label: "Documents", icon: FileText },
    {
      id: "disputes",
      label: "Query",
      icon: AlertTriangle,
      count: disputeStatus.activeDisputes.length,
      alert: disputeStatus.hasActiveDisputes
    },
    {
      id: "discussions",
      label: "Discussions",
      icon: MessageSquare,
      count: unreadCount,
    },
    { id: "status", label: "Status Timeline", icon: Activity },
  ];

  useEffect(() => {
    if (user?.uid && applicationId) {
      loadApplicationData();
    }

    // Set up real-time listener
    const unsubscribe = subscribeToApplicationUpdates(
      applicationId,
      (updatedApp) => {
        setApplication(updatedApp);
  // Make approval sticky and also consider top-level status
  setIsApprovedForProcess((prev) => prev || updatedApp.workflow?.medicalVerification?.approvedForProcess || updatedApp.status === 'approved_for_process');

        // If FAL has been accepted by hospital, mark medical review as completed once
        try {
          const falId = updatedApp?.workflow?.dischargeSettlement?.finalApprovalLetterId;
          const falUrl = updatedApp?.workflow?.dischargeSettlement?.finalApprovalLetterUrl;
          const ack = falId ? updatedApp?.workflow?.acknowledgements?.[falId] : null;
          const falAccepted = Boolean(falUrl && ack?.status === 'accepted');
          if (falAccepted && updatedApp?.workflow?.medicalReview?.status !== 'completed') {
            // Fire and forget; don't block UI
            markMedicalReviewCompleted(applicationId, {
              doctorId: user?.uid,
              doctorName: user?.profile?.name || user?.email || ''
            });

            // Risk flag logic moved to component scope
          }
        } catch (e) {
          // ignore
        }

        // Check if review is still active
        if (
          updatedApp.workflow?.medicalReview?.status === "in_progress" &&
          updatedApp.workflow?.medicalReview?.reviewerId === user.uid
        ) {
          if (!isTimerActive) {
            const startTime =
              updatedApp.workflow?.medicalReview?.reviewStartTime?.toDate();
            if (startTime) {
              setReviewStartTime(startTime);
              startTimer(startTime);
            }
          }
        } else {
          stopTimer();
        }
      }
    );

    return () => {
      unsubscribe();
      stopTimer();
      if (activityRef.current) {
        clearInterval(activityRef.current);
      }
    };
  }, [applicationId, user?.uid]);

  // Fetch hospital tariff documents when application data is loaded
  useEffect(() => {
    const fetchTariffDocuments = async () => {
      if (!application?.hospitalId && !application?.hospitalName) {
        return;
      }

      setLoadingTariffDocuments(true);
      setTariffError(null);

      try {
        const result = await getHospitalTariffDocuments(application.hospitalId || application.hospitalName);
        if (result.success) {
          setTariffDocuments(result.tariffDocuments);
        } else {
          setTariffError(result.error);
        }
      } catch (error) {
        console.error('Error fetching tariff documents:', error);
        setTariffError('Failed to load tariff documents');
      } finally {
        setLoadingTariffDocuments(false);
      }
    };

    if (application) {
      fetchTariffDocuments();
    }
  }, [application?.hospitalId, application?.hospitalName]);

  useEffect(() => {
    if (activeTab === "discussions") {
      loadDiscussions();
      markAllMessagesAsRead();
    }
  }, [activeTab]);

  // Auto-select first discharge document when available
  useEffect(() => {
    if (!selectedDocument && application?.dischargeDocuments?.length > 0) {
      setSelectedDocument(application.dischargeDocuments[0]);
    }
  }, [application?.dischargeDocuments, selectedDocument]);

  useEffect(() => {
    scrollToBottom();
  }, [discussions]);

  // Handle browser navigation (tab close, page refresh, etc.)
  // Use a ref to track if navigation is confirmed via popstate handler
  const navigationConfirmedRef = useRef(false);
  
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Don't show prompt if user already confirmed navigation via back button
      if (navigationConfirmedRef.current) {
        return;
      }
      
      const isCurrentlyReviewing = application?.workflow?.medicalReview?.status === "in_progress" &&
        application?.workflow?.medicalReview?.reviewerId === user?.uid;

      if (isCurrentlyReviewing) {
        // Attempt to pause the review when user tries to leave
        pauseApplicationReview(applicationId, user.uid);

        const message = "Your review has been paused. You can resume within 15 minutes from the dashboard.";
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [application?.workflow?.medicalReview?.status, application?.workflow?.medicalReview?.reviewerId, user?.uid, applicationId]);

  // Confirm on browser back navigation in all entry modes (start/view/resume/continue)
  useEffect(() => {
    // Keep latest values in refs so the handler reads current state without re-subscribing
    const appRef = { current: application };
    const userRef = { current: user };

    const updateRefs = () => {
      appRef.current = application;
      userRef.current = user;
    };

    const refInterval = setInterval(updateRefs, 250);

    // Push a dummy state so the first Back triggers popstate here
    window.history.pushState(null, '', window.location.href);

    const handlePopState = async () => {
      // Immediately restore state so UI does not navigate before user choice
      window.history.pushState(null, '', window.location.href);
      
      // Mark navigation as confirmed BEFORE showing confirm dialog
      // This prevents beforeunload from firing during the async operations
      navigationConfirmedRef.current = true;
      
      const confirmed = window.confirm('Are you sure you want to leave this page? Unsaved changes may be lost.');
      if (!confirmed) {
        // Reset the flag if user cancels
        navigationConfirmedRef.current = false;
        return;
      }

      // If actively reviewing, gently pause before leaving
      const app = appRef.current;
      const usr = userRef.current;
      const isCurrentlyReviewing = app?.workflow?.medicalReview?.status === 'in_progress' &&
        app?.workflow?.medicalReview?.reviewerId === usr?.uid;
      try {
        if (isCurrentlyReviewing) {
          await pauseApplicationReview(applicationId, usr.uid);
          if (typeof stopTimer === 'function') {
            stopTimer();
          }
        }
      } catch (_) { /* ignore pause failures on back */ }

      // Navigate to dashboard explicitly
      navigate('/doctor/dashboard');
    };

    window.addEventListener('popstate', handlePopState);
    // Fallbacks for some browsers/environments
    const handlePageHide = (e) => {
      // Ignore if navigation already confirmed via popstate path
      // This acts mostly when the browser triggers a BFCache or pagehide without popstate
      if (document.visibilityState === 'hidden') {
        // Re-push to keep page active; user should still use back which triggers popstate
        try { window.history.pushState(null, '', window.location.href); } catch (_) {}
      }
    };
    document.addEventListener('visibilitychange', handlePageHide);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      clearInterval(refInterval);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('visibilitychange', handlePageHide);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  // Update activity every 2 minutes to prevent timeout
  useEffect(() => {
    if (isTimerActive) {
      activityRef.current = setInterval(() => {
        updateReviewActivity(applicationId, user.uid);
      }, 2 * 60 * 1000); // 2 minutes

      return () => {
        if (activityRef.current) {
          clearInterval(activityRef.current);
        }
      };
    }
  }, [isTimerActive, applicationId, user.uid]);

  const loadApplicationData = async (forceSaveFirst = true) => {
    // CRITICAL: Save any unsaved discharge items BEFORE reloading to prevent data loss
    if (forceSaveFirst && dischargeItems && dischargeItems.length > 0 && !isFALPublished) {
      try {
        await saveDischargeImmediately();
      } catch (e) {
        console.warn('Pre-reload save failed:', e);
      }
    }
    
    setLoading(true);
    try {
      const result = await getPatientApplication(applicationId);
      if (result.success) {
        setApplication(result.application);
  // Initialize sticky approved flag (includes top-level status)
  setIsApprovedForProcess((prev) => prev || result.application.workflow?.medicalVerification?.approvedForProcess || result.application.status === 'approved_for_process');

        // Check dispute status
        await checkDisputeStatus();

        // Check if review is in progress and initialize timer
        if (
          result.application.workflow?.medicalReview?.status ===
          "in_progress" &&
          result.application.workflow?.medicalReview?.reviewerId === user.uid
        ) {
          const startTime =
            result.application.workflow?.medicalReview?.reviewStartTime?.toDate();
          if (startTime) {
            setReviewStartTime(startTime);
            startTimer(startTime);
          }
        }

        // Load discharge summary if available - ONLY on initial load (preserve existing items if already loaded)
        const dischargeResult = await getDischargeSummary(applicationId);
        if (dischargeResult.success && dischargeResult.dischargeSummary) {
          setDischargeSummary(dischargeResult.dischargeSummary);
          // Only load items if they exist and current items are empty (prevents overwriting existing work)
          const draftItems = dischargeResult.dischargeSummary.items || [];
          setDischargeItems(prevItems => {
            // If we already have items in state, keep them unless the draft has newer items
            if (prevItems && prevItems.length > 0) {
              // Check if draft has more recent timestamp
              const draftTimestamp = dischargeResult.dischargeSummary.updatedAt?.toDate?.() || new Date(0);
              const stateTimestamp = lastAutoSaveAt || new Date(0);
              // Only replace if draft is newer
              if (draftTimestamp > stateTimestamp) {
                return draftItems;
              }
              return prevItems; // Keep existing items
            }
            return draftItems; // No items in state, use draft
          });
          setDischargeComments(prevComments => prevComments || dischargeResult.dischargeSummary.doctorComments || '');
          setDischargeFiles(prevFiles => prevFiles.length > 0 ? prevFiles : (dischargeResult.dischargeSummary.uploadedFiles || []));
          
          // CRITICAL FIX: Load FAL overrides and manual adjustments so other doctors can see them
          if (dischargeResult.dischargeSummary.falOverrides) {
            setFalOverrides(dischargeResult.dischargeSummary.falOverrides);
          }
          if (dischargeResult.dischargeSummary.falManualAdjustments) {
            setFalManualAdjustments(dischargeResult.dischargeSummary.falManualAdjustments);
          }
          if (dischargeResult.dischargeSummary.falComputedPreview) {
            setFalComputedPreview(dischargeResult.dischargeSummary.falComputedPreview);
          }
        }

        calculateUnreadMessages(result.application);

        // Build unified timeline (disputes + document queries + workflow + status)
        try {
          await loadUnifiedTimeline(result.application);
        } catch (_) { /* non-blocking */ }

        // Load existing sanctions to detect any prior Final Approval Letter and its acknowledgement state
        try {
          const sanRes = await getApplicationSanctions(applicationId);
          if (sanRes.success && Array.isArray(sanRes.sanctions)) {
            const latestApproval = sanRes.sanctions
              .filter(s => s.type === 'approval')
              .sort((a, b) => new Date(b.generatedAt?.toDate ? b.generatedAt.toDate() : b.generatedAt) - new Date(a.generatedAt?.toDate ? a.generatedAt.toDate() : a.generatedAt))[0];
            if (latestApproval) setFalLetter(latestApproval);
          }
        } catch (_) { /* ignore */ }

        // Extract policy verification data for KYP updates
        const policyVerification = result.application.workflow?.policyVerification;

        // Helper function to format medical data as bullet points
        const formatAsBulletPoints = (items, keyField) => {
          if (!items || !Array.isArray(items) || items.length === 0) return "";

          return items.map(item => {
            const value = item[keyField];
            return value ? `• ${value}` : '';
          }).filter(item => item).join('\n');
        };

        // Format pre-existing conditions from treatments
        const preExistingConditions = formatAsBulletPoints(
          result.application.medical?.treatments?.filter(t => t.treatmentType === 'medical') || [],
          'diagnosisName'
        );

        // Initialize settlement approval sticky state from workflow
        const medicalRecommendations = formatAsBulletPoints(
          result.application.medical?.treatments || [],
          'procedureName'
        );

        // Add general diagnosis if no treatments available
        const generalDiagnosis = result.application.medical?.diagnosis;
        const finalPreExisting = preExistingConditions || (generalDiagnosis ? `• ${generalDiagnosis}` : "");
        const finalRecommendations = medicalRecommendations || (generalDiagnosis ? `• Treatment as per diagnosis: ${generalDiagnosis}` : "");

        // Check if there's existing KYP data from previous generation/approval
        const existingKypData = result.application.workflow?.medicalVerification?.kypData;
        
        if (existingKypData) {
          // If KYP data exists, use it to populate the form
          setKypData(existingKypData);
        } else {
          // Otherwise, auto-populate with fresh data
          setKypData((prev) => ({
            ...prev,
            // Update room eligibility from policy verification if available
            roomEligibility: policyVerification?.roomEligibility?.roomType || prev.roomEligibility,
            // Update co-pay from policy verification if available  
            coPay: policyVerification?.coPayStructure?.generalCoPay ?
              `${policyVerification.coPayStructure.generalCoPay}${policyVerification.coPayStructure.coPayType === 'percentage' ? '%' : ' ₹'}` :
              prev.coPay,
            // Auto-populate medical data
            preExistingConditions: finalPreExisting,
            recommendation: finalRecommendations,
            // Auto-fill clinical notes if not already filled
            clinicalNotes: prev.clinicalNotes || `Patient diagnosed with: ${generalDiagnosis || 'Pending diagnosis'}\nEstimated treatment cost: ₹${result.application.medical?.estimatedCost?.toLocaleString('en-IN') || '0'}\nAdmission date: ${result.application.medical?.admissionDate || 'TBD'}`
          }));
        }
        // Initialize settlement approval sticky state from workflow
        try {
          const calcApproved = !!result.application?.workflow?.dischargeSettlement?.calculationApproved;
          if (calcApproved) setIsSettlementApproved(true);
        } catch (_) {}

        // Load existing internal comments from all doctors
        try {
          const commentsResult = await getInternalComments(applicationId);
          console.log('Loaded comments result:', commentsResult);

          if (commentsResult.success) {
            // Store all comments for display purposes
            setAllInternalComments(commentsResult.comments || []);

            // Initialize with the current doctor's existing comments if any
            const currentDoctorComment = commentsResult.comments?.find(comment => comment.doctorId === user?.uid);
            console.log('Current doctor comment:', currentDoctorComment);

            if (currentDoctorComment && currentDoctorComment.comments) {
              setInternalComments(currentDoctorComment.comments);
              setSavedInternalComments(currentDoctorComment.comments);
              console.log('Set internal comments to:', currentDoctorComment.comments);
            } else {
              console.log('No existing comments found for current doctor');
              // Clear any previous comments if no comments exist for this doctor
              setInternalComments("");
              setSavedInternalComments("");
            }
          } else {
            console.log('Failed to load comments:', commentsResult.error);
            setAllInternalComments([]);
          }
        } catch (error) {
          console.error("Error loading internal comments:", error);
          // Clear comments on error
          setInternalComments("");
          setSavedInternalComments("");
          setAllInternalComments([]);
        }
      }
    } catch (error) {
      console.error("Error loading application:", error);
    } finally {
      setLoading(false);
    }
  };

  // Build a unified timeline from workflow, status history, disputes and document queries
  const loadUnifiedTimeline = async (app) => {
    const safeToDate = (ts) => {
      if (!ts) return null;
      try {
        return ts?.toDate ? ts.toDate() : new Date(ts);
      } catch (_) {
        return null;
      }
    };

    const events = [];

    // Submission
    if (app?.submittedAt) {
      events.push({
        id: 'submission',
        type: 'submission',
        title: 'Application Submitted',
        description: 'Hospital submitted the patient application',
        timestamp: safeToDate(app.submittedAt),
        completed: true,
      });
    }

    // Approved for process
    const approvedForProcessAt = app?.workflow?.medicalVerification?.approvedForProcessAt;
    if (approvedForProcessAt) {
      events.push({
        id: 'approved_for_process',
        type: 'approved_for_process',
        title: 'Approved for Process',
        description: 'Approved for policy and financial review',
        timestamp: safeToDate(approvedForProcessAt),
        completed: !!app?.workflow?.medicalVerification?.approvedForProcess,
      });
    }

    // Workflow approvals
    const wf = app?.workflow || {};
    const workflowMap = [
      { key: 'medicalVerification', type: 'medical_review', title: 'Medical Review', statusKey: 'status' },
      { key: 'policyVerification', type: 'policy_review', title: 'Policy Verification', statusKey: 'status' },
      { key: 'financialVerification', type: 'financial_review', title: 'Financial Verification', statusKey: 'status' },
      { key: 'kycVerification', type: 'kyc_verification', title: 'KYC Verification', statusKey: 'status' },
    ];
    workflowMap.forEach(({ key, type, title, statusKey }) => {
      const node = wf[key];
      if (!node) return;
      const ts = node.verifiedAt || node.updatedAt || node.approvedAt;
      if (!ts) return;
      events.push({
        id: `${type}`,
        type,
        title,
        description: `${(node[statusKey] || '').toString().replace('_', ' ')}`.trim(),
        timestamp: safeToDate(ts),
        completed: node[statusKey] === 'approved',
        actor: node.verifiedBy ? { name: node.verifiedBy, team: title.includes('Medical') ? 'medical' : title.includes('Policy') ? 'policy' : title.includes('Financial') ? 'financial' : undefined } : undefined,
      });
    });

    // Status history (object map)
    if (app?.statusHistory && typeof app.statusHistory === 'object') {
      Object.entries(app.statusHistory).forEach(([status, data]) => {
        events.push({
          id: `status-${status}`,
          type: 'status_change',
          title: `Status changed to ${status.replace('_', ' ').toUpperCase()}`,
          description: data?.comments || (data?.updatedBy ? `Updated by ${data.updatedBy}` : ''),
          timestamp: safeToDate(data?.updatedAt),
          actor: data?.updatedBy ? { name: data.updatedBy } : undefined,
          completed: true,
        });
      });
    }

    // Fetch disputes and document queries in parallel
    const [disputesRes, docQueriesRes] = await Promise.all([
      getApplicationDisputes(app.applicationId),
      getDocumentQueries(app.applicationId),
    ]);

    // Disputes
    if (disputesRes?.success && Array.isArray(disputesRes.disputes)) {
      disputesRes.disputes.forEach((d) => {
        if (d.raisedAt) {
          events.push({
            id: `dispute-${d.id}-raised`,
            type: 'dispute_raised',
            title: d.title || 'Query Raised',
            description: d.description,
            timestamp: safeToDate(d.raisedAt),
            actor: d.raisedBy ? { name: d.raisedBy.userName, team: d.raisedBy.department } : undefined,
            completed: false,
          });
        }
        if (d.acknowledgedAt) {
          events.push({
            id: `dispute-${d.id}-ack`,
            type: 'acknowledged',
            title: 'Query Acknowledged',
            description: '',
            timestamp: safeToDate(d.acknowledgedAt),
            completed: false,
          });
        }
        if (d.resolvedAt) {
          events.push({
            id: `dispute-${d.id}-resolved`,
            type: 'dispute_resolved',
            title: 'Query Resolved',
            description: d.resolution || 'Resolution recorded',
            timestamp: safeToDate(d.resolvedAt),
            actor: d.resolvedBy ? { name: d.resolvedBy.userName, team: d.resolvedBy.department } : undefined,
            attachments: Array.isArray(d.resolverDocuments)
              ? d.resolverDocuments.map((f) => ({ name: f?.name || 'Document', url: f?.url })).filter(a => a.url)
              : [],
            completed: true,
          });
        }
      });
    }

    // Document queries
    if (docQueriesRes?.success && Array.isArray(docQueriesRes.queries)) {
      docQueriesRes.queries.forEach((q) => {
        if (q.raisedAt) {
          events.push({
            id: `docq-${q.id}-open`,
            type: 'doc_query_open',
            title: `Document Query: ${q.documentTitle || q.documentKey}`,
            description: q.comment,
            timestamp: safeToDate(q.raisedAt),
            actor: q.raisedBy ? { name: q.raisedBy.name, team: q.raisedBy.team } : undefined,
            completed: q.status === 'resolved',
          });
        }
        if (q.status === 'resolved' && q.resolvedAt) {
          events.push({
            id: `docq-${q.id}-resolved`,
            type: 'doc_query_resolved',
            title: `Document Updated: ${q.documentTitle || q.documentKey}`,
            description: 'Query resolved with updated document',
            timestamp: safeToDate(q.resolvedAt),
            actor: q.resolvedBy ? { name: q.resolvedBy.name, team: 'hospital' } : undefined,
            attachments: q.updatedDocumentUrl ? [{ name: q.updatedDocumentFileName || 'Updated Document', url: q.updatedDocumentUrl }] : [],
            completed: true,
          });
        }
      });
    }

    // Sort newest first
    events.sort((a, b) => {
      const at = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bt = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return bt - at;
    });

    setTimelineEvents(events);
  };

  // Check dispute status for the application
  const checkDisputeStatus = async () => {
    try {
      // Only block the doctor (medical) process if a doctor-raised query is active
      const result = await checkApplicationDisputeStatus(applicationId, { department: 'medical' });
      if (result.success) {
        setDisputeStatus({
          hasActiveDisputes: result.hasActiveDisputes,
          activeDisputes: result.activeDisputes,
          canProceed: result.canProceed
        });
      }

      // Also check ALL disputes from all departments for KYP generation blocking
      const allDisputesResult = await checkApplicationDisputeStatus(applicationId);
      if (allDisputesResult.success) {
        setAllDisputesStatus({
          hasActiveDisputes: allDisputesResult.hasActiveDisputes,
          activeDisputes: allDisputesResult.activeDisputes,
        });
      }

      return result;
    } catch (error) {
      console.error("Error checking dispute status:", error);
      return { success: false, hasActiveDisputes: false, activeDisputes: [], canProceed: false };
    }
  };

  // Handle dispute updates
  const handleDisputeUpdate = async () => {
    const status = await checkDisputeStatus();
    // If no active disputes remain and user is on Query tab, switch back to Application
    if (status?.success && !status.hasActiveDisputes && activeTab === 'disputes') {
      setActiveTab('application');
    }
    // Reload application data to reflect restored status when all disputes resolved
    loadApplicationData();
  };

  const loadDiscussions = async () => {
    try {
      const result = await getApplicationDiscussions(applicationId);
      if (result.success) {
        setDiscussions(result.discussions);
        calculateUnreadMessages({ discussions: result.discussions });
      }
    } catch (error) {
      console.error("Error loading discussions:", error);
    }
  };

  const calculateUnreadMessages = (appData) => {
    if (!appData || !appData.discussions) return;

    let unread = 0;
    appData.discussions.forEach((discussion) => {
      discussion.messages?.forEach((message) => {
        if (!message.readBy?.includes(user.uid)) {
          unread++;
        }
      });
    });
    setUnreadCount(unread);
  };

  const markAllMessagesAsRead = async () => {
    try {
      await markMessagesAsRead(applicationId, user.uid);
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const startTimer = (startTime = null) => {
    const start = startTime || reviewStartTime || new Date();
    if (!reviewStartTime && !startTime) {
      setReviewStartTime(start);
    }
    setIsTimerActive(true);

    timerRef.current = setInterval(() => {
      const now = new Date();
      const elapsed = Math.floor((now - start) / 1000);
      setElapsedTime(elapsed);
    }, 1000);
  };

  const stopTimer = () => {
    setIsTimerActive(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (activityRef.current) {
      clearInterval(activityRef.current);
      activityRef.current = null;
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "submitted":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "under_review":
      case "under_medical_review":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "approved_for_process":
        return "bg-green-100 text-green-800 border-green-200";
      case "approved":
      case "medical_approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "rejected":
      case "medical_rejected":
        return "bg-red-100 text-red-800 border-red-200";
      case "pending_documents":
      case "pending_clarification":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const toggleSection = (sectionKey) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const formatAmount = (amount) => {
    if (!amount) return "₹0";
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  // Handle early approval for process
  const handleApproveForProcess = async () => {
    // ICD validation: require at least one treatment AND every treatment must have icdCode & icdDescription
    const treatments = application?.medical?.treatments || [];
    if (treatments.length === 0) {
      alert("Cannot approve for process: please add at least one Diagnosis with ICD Code & Description.");
      return;
    }
    const allHaveIcd = treatments.every(t => (t.icdCode && t.icdCode.trim()) && (t.icdDescription && t.icdDescription.trim()));
    if (!allHaveIcd) {
      alert("Cannot approve for process: all added Diagnoses must include ICD Code and Description.");
      return;
    }
    setActionLoading((prev) => ({ ...prev, approveForProcess: true }));
    try {
      const result = await approveApplicationForProcess(
        applicationId,
        user.uid,
        {
          comments: "Direct approval - no comments required",
          approvedAt: new Date(),
        }
      );

      if (result.success) {
        setIsApprovedForProcess(true);
        setApplication((prev) => ({
          ...prev,
          status: "approved_for_process",
          workflow: {
            ...prev.workflow,
            medicalVerification: {
              ...prev.workflow?.medicalVerification,
              approvedForProcess: true,
              approvalForProcessComments: "Direct approval - no comments required",
              approvedForProcessAt: new Date(),
              approvedForProcessBy: user.uid,
            },
          },
        }));
        alert("Application approved for process. Other teams can now see this application.");
      } else {
        alert("Error approving application: " + result.error);
      }
    } catch (error) {
      console.error("Error approving application for process:", error);
      alert("Failed to approve application for process");
    } finally {
      setActionLoading((prev) => ({ ...prev, approveForProcess: false }));
    }
  };

  // Handle internal comments save
  const handleSaveInternalComments = async () => {
    if (!internalComments.trim()) {
      alert("Please enter some comments before saving");
      return;
    }

    if (!user?.uid) {
      alert("User not authenticated. Please refresh the page and try again.");
      return;
    }

    setCommentsSaving(true);
    try {
      console.log('Saving comments with user:', user);

      const saveResult = await saveInternalComments(
        applicationId,
        user.uid,
        internalComments,
        user.profile?.name || user.email || "Unknown Doctor"
      );

      if (saveResult.success) {
        setSavedInternalComments(internalComments);

        // Reload all comments to update the display
        const updatedCommentsResult = await getInternalComments(applicationId);
        if (updatedCommentsResult.success) {
          setAllInternalComments(updatedCommentsResult.comments || []);

          // Make sure the current doctor's comment state is properly updated
          const currentDoctorComment = updatedCommentsResult.comments?.find(comment => comment.doctorId === user.uid);
          if (currentDoctorComment) {
            setInternalComments(currentDoctorComment.comments);
            setSavedInternalComments(currentDoctorComment.comments);
          }
        }

        alert("Internal comments saved successfully and will be visible to all doctors");
      } else {
        throw new Error(saveResult.error || "Failed to save comments");
      }
    } catch (error) {
      console.error("Error saving internal comments:", error);
      alert("Failed to save internal comments: " + error.message);
    } finally {
      setCommentsSaving(false);
    }
  };

  // Handle file selection for messages
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  // Remove selected file
  const removeSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Send message with different types
  const handleSendMessage = async () => {
    if (messageType === "text" && !newMessage.trim()) return;
    if (messageType === "file" && selectedFiles.length === 0) return;
    if (messageType === "qna" && qnaQuestions.length === 0) return;

    setActionLoading((prev) => ({ ...prev, sendMessage: true }));
    try {
      let messageData = {
        message: newMessage || "",
        sender: user?.uid,
        senderName: user.profile?.name || user.email,
        senderRole: user.role,
        messageType: messageType,
        timestamp: new Date(),
        readBy: [user?.uid],
        replyTo: replyingTo?.id || null,
      };

      // Handle QnA type message
      if (messageType === "qna") {
        messageData.qnaQuestions = qnaQuestions;
        messageData.message = `Q&A Form: ${qnaQuestions.length} questions`;
      }

      // Upload files if any
      if (selectedFiles.length > 0) {
        const uploadPromises = selectedFiles.map((file) =>
          uploadDiscussionDocument(applicationId, file, {
            uploadedBy: user?.uid,
            uploaderName: user.profile?.name || user.email,
          })
        );

        const uploadResults = await Promise.all(uploadPromises);
        const attachments = uploadResults
          .filter((result) => result.success)
          .map((result) => result.document);

        messageData.attachments = attachments;
      }

      const result = await sendDiscussionMessage(applicationId, messageData);

      if (result.success) {
        setNewMessage("");
        setSelectedFiles([]);
        setReplyingTo(null);
        setQnaQuestions([]);
        setShowQnAForm(false);
        loadDiscussions();
      }
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, sendMessage: false }));
    }
  };

  // Add question to QnA form
  const handleAddQuestion = () => {
    if (!newQuestion.question.trim()) return;

    setQnaQuestions((prev) => [...prev, { ...newQuestion, id: Date.now() }]);
    setNewQuestion({
      question: "",
      required: false,
      answerType: "text",
      description: "",
    });
  };

  // Remove question from QnA form
  const removeQuestion = (questionId) => {
    setQnaQuestions((prev) => prev.filter((q) => q.id !== questionId));
  };

  // Handle KYP generation
  const handleGenerateKYP = async () => {
    // Check if both policy and financial verification are approved
    if (application?.workflow?.policyVerification?.status !== "approved") {
      alert('Cannot generate KYP letter. Policy verification must be approved first.');
      return;
    }

    

    // Check ALL disputes from ALL departments before proceeding with KYP generation
    // KYP requires all queries (doctor, finance, policy) to be resolved
    const allDisputesResult = await checkApplicationDisputeStatus(applicationId);
    if (allDisputesResult.success && allDisputesResult.hasActiveDisputes) {
      const departments = [...new Set(allDisputesResult.activeDisputes.map(d => d.raisedBy?.department || 'unknown'))];
      alert(`Cannot generate KYP letter while there are active queries from: ${departments.join(', ')}. All queries from all departments must be resolved before generating KYP.`);
      return;
    }

    // Enforce medical checklist completion
    if (!isMedicalChecklistComplete) {
      alert('Please complete the Medical Verification Checklist before generating the KYP letter.');
      return;
    }

    setActionLoading((prev) => ({ ...prev, kyp: true }));
    try {
      // Fetch lead generator data first
      let leadGenData = null;
      if (application?.leadGenerator?.leadGeneratorId) {
        try {
          const leadGenResult = await getLeadGeneratorById(application.leadGenerator.leadGeneratorId);
          if (leadGenResult.success) {
            leadGenData = leadGenResult.leadGenerator;
            setLeadGeneratorData(leadGenData);
          }
        } catch (error) {
          console.warn('Failed to fetch lead generator data:', error);
        }
      }

      const policyVerification = application.workflow?.policyVerification || {};

      // Enhanced KYP data with policy verification information and lead generator details
      const enhancedKypData = {
        ...kypData,
        leadGenerator: application.leadGenerator, // Include lead generator information for header
        applicationDetails: {
          applicationId: application.applicationId,
          patientName: application.patient?.name,
          diagnosis: application.medical?.treatments?.[0]?.diagnosisName || application.medical?.diagnosis,
          icdCode: application.medical?.treatments?.[0]?.icdCode || application.medical?.icdCode,
          icdDescription: application.medical?.treatments?.[0]?.icdDescription || application.medical?.icdDescription,
          estimatedCost: application.medical?.estimatedCost,
          hospitalName: application.hospitalName,
          insuranceCompany: application.policy?.insuranceCompany,
          policyNumber: policyVerification.policyDetails?.policyNumber || application.policy?.number,
          admissionDate: application.medical?.admissionDate,
          dischargeDate: application.medical?.dischargeDate || "TBD",
          treatmentType: application.medical?.treatments?.[0]?.treatmentType || application.medical?.treatmentType,
          procedureName: application.medical?.treatments?.[0]?.procedureName || application.medical?.procedureName || "As per diagnosis",
          tpaName: policyVerification.policyDetails?.tpaName || application.policy?.tpaName || "Inhouse",
          city: application.hospital?.city || "Not specified",
          relationshipWithPrimary: application.patient?.relationshipWithPrimary || "Self",
          primaryInsuredName: application.policy?.primaryInsuredName || application.patient?.name,
          // Use policy verification data for enhanced coverage details
          sumInsured: policyVerification.policyDetails?.baseSumInsured || application.policy?.coverageAmount || 0,
          cumulativeBonus: policyVerification.policyDetails?.cumulativeBonus || application.policy?.cumulativeBonus || 0,
          topupSI: policyVerification.policyDetails?.topUpSumInsured || application.policy?.topupSI || 0,
          effectiveCoverage: policyVerification.policyDetails?.effectiveTotalSumInsured ||
            (application.policy?.coverageAmount || 0) + (application.policy?.cumulativeBonus || 0) + (application.policy?.topupSI || 0),
          balanceSumInsured: policyVerification.policyDetails?.balanceSumInsured || application.policy?.balanceSumInsured || application.policy?.coverageAmount || 0,
          intimationId: application.intimationId || `CIR/${new Date().getFullYear()}/${Math.random().toString(36).substr(2, 9)}`,
          // Policy verification specific data
          policyType: policyVerification.policyDetails?.policyType || application.policy?.type,
          policyPeriod: `${policyVerification.policyDetails?.inceptionDate || application.policy?.startDate} to ${policyVerification.policyDetails?.expiryDate || application.policy?.endDate}`,
          roomEligibility: policyVerification.roomEligibility?.roomType || "Single Standard AC Room",
          roomAmountPerDay: policyVerification.roomEligibility?.roomAmountPerDay || 0,
          icuAmountPerDay: policyVerification.roomEligibility?.icuAmountPerDay || 0,
        },
        // Enhanced policy coverage details
        policyDetails: {
          ...policyVerification.policyDetails,
          coPayStructure: policyVerification.coPayStructure,
          deductiblesAndSublimits: policyVerification.deductiblesAndSublimits,
          claimHistory: policyVerification.claimHistory,
          totalClaimedCurrentYear: policyVerification.claimHistory?.totalClaimedCurrentYear || 0,
        },
      };

      const result = await generateKYPLetter(
        applicationId,
        user.uid,
        enhancedKypData
      );

      if (result.success && result.previewReady) {
        // Show preview modal instead of downloading immediately
        setKYPPreviewData({
          application: result.application,
          kypData: result.kypData,
          leadGeneratorData: leadGenData
        });
        setShowKYPPreview(true);

        setApplication((prev) => ({
          ...prev,
          workflow: {
            ...prev.workflow,
            medicalVerification: {
              ...prev.workflow.medicalVerification,
              kypGenerated: true,
              kypData: result.kypData, // Store the KYP data
            },
          },
        }));

      } else {
        alert("Error generating KYP: " + result.error);
      }
    } catch (error) {
      console.error("Error generating KYP:", error);
      alert("Failed to generate KYP letter");
    } finally {
      setActionLoading((prev) => ({ ...prev, kyp: false }));
    }
  };

  // Handle viewing existing KYP - Show in modal instead of opening URL
  const handleViewKYP = async () => {
    // If KYP is already approved, allow viewing regardless of verification status
    const isKypAlreadyApproved = application?.workflow?.medicalVerification?.kypApproval?.status === 'approved';

    // Allow bypass if this is a challenge revision cycle that has completed departmental review
    const kypReassignStatus = application?.workflow?.medicalVerification?.kypReassignment?.status; // 'policy_completed' | 'finance_completed' | 'completed' etc.
    const isRevisionCycleReady = ['policy_completed','finance_completed','completed'].includes(kypReassignStatus);

    // For non-approved KYP and not in revision-ready cycle, enforce original gating
    if (!isKypAlreadyApproved && !isRevisionCycleReady) {
      if (application?.workflow?.policyVerification?.status !== "approved") {
        alert('Cannot view KYP letter. Policy verification must be approved first.');
        return;
      }
      if (application?.workflow?.financialVerification?.status !== "approved") {
        alert('Cannot view KYP letter. Financial verification must be approved first.');
        return;
      }
    }

    try {
      // If previously approved/rejected and not yet approved, clear to re-open approval for revised KYP
      if (application?.workflow?.medicalVerification?.kypApproval && !isKypAlreadyApproved) {
        try {
          await resetKYPApproval(applicationId, { by: user?.uid, byName: user?.profile?.name || user?.email });
        } catch (_) { /* non-blocking */ }
      }

      // Fetch lead generator data if not already fetched
      let leadGenData = leadGeneratorData;
      if (!leadGenData && application?.leadGenerator?.leadGeneratorId) {
        try {
          const leadGenResult = await getLeadGeneratorById(application.leadGenerator.leadGeneratorId);
          if (leadGenResult.success) {
            leadGenData = leadGenResult.leadGenerator;
            setLeadGeneratorData(leadGenData);
          }
        } catch (error) {
          console.warn('Failed to fetch lead generator data:', error);
        }
      }

      // Use current unsaved edits until approved; after approval, show stored snapshot
      const existingKypData = application.workflow?.medicalVerification?.kypData;
      const kypForPreview = isKypAlreadyApproved ? existingKypData : (kypData || existingKypData);

      if (kypForPreview && Object.keys(kypForPreview || {}).length > 0) {
        setKYPPreviewData({
          application: application,
          kypData: kypForPreview,
          leadGeneratorData: leadGenData
        });
        setShowKYPPreview(true);
      } else {
        // If no data at all, generate anew
        handleGenerateKYP();
      }
    } catch (error) {
      console.error("Error viewing KYP:", error);
      alert("Failed to view KYP letter");
    }
  };

  // Force regenerate a revised KYP after challenge cycle completed
  const handleReviseKYP = async () => {
    const kypReassignStatus = application?.workflow?.medicalVerification?.kypReassignment?.status;
    const isRevisionCycleReady = ['policy_completed','finance_completed','completed'].includes(kypReassignStatus);
    if (!isRevisionCycleReady) {
      alert('Revision not allowed until departmental review is completed.');
      return;
    }
    try {
      // Clear prior approval (if any) so new KYP goes through approval again
      if (application?.workflow?.medicalVerification?.kypApproval) {
        try { await resetKYPApproval(applicationId, { by: user?.uid, byName: user?.profile?.name || user?.email, reason: 'revision' }); } catch (_) {}
      }
      // Drop existing cached kypData locally so regeneration occurs
      setApplication(prev => ({
        ...prev,
        workflow: {
          ...prev.workflow,
          medicalVerification: {
            ...prev.workflow.medicalVerification,
            kypData: null,
            kypGenerated: false
          }
        }
      }));
      await handleGenerateKYP();
    } catch (e) {
      console.error('Error revising KYP:', e);
      alert(e.message || 'Failed to revise KYP');
    }
  };

  // Handle discharge file upload
  const handleDischargeFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    setActionLoading((prev) => ({ ...prev, dischargeUpload: true }));

    try {
      const uploadPromises = files.map(async (file) => {
        const result = await uploadReviewDocument(
          applicationId,
          file,
          "discharge_review",
          user.uid
        );
        if (result.success) {
          return {
            ...result.document,
            description: "", // Will be filled by user
          };
        }
        return null;
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      const validFiles = uploadedFiles.filter((file) => file !== null);

      setDischargeFiles((prev) => [...prev, ...validFiles]);
    } catch (error) {
      console.error("Error uploading discharge files:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, dischargeUpload: false }));
    }
  };

  // Update file description (guarded when FAL is published)
  const updateFileDescription = (index, description) => {
    if (isFALPublished) return;
    setDischargeFiles((prev) =>
      prev.map((file, i) => (i === index ? { ...file, description } : file))
    );
  };

  // Remove discharge file
  const removeDischargeFile = (index) => {
    setDischargeFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Centralized confirmation for leaving the review page via in-app navigation
  const confirmLeave = async (targetRoute = '/doctor/dashboard') => {
    const confirmed = window.confirm('Are you sure you want to leave this page? Unsaved changes may be lost.');
    if (!confirmed) return;

    const isCurrentlyReviewing = application?.workflow?.medicalReview?.status === 'in_progress' &&
      application?.workflow?.medicalReview?.reviewerId === user?.uid;

    try {
      if (isCurrentlyReviewing) {
        await pauseApplicationReview(applicationId, user.uid);
        if (typeof stopTimer === 'function') {
          stopTimer();
        }
      }
    } catch (_) { /* ignore pause failures on leave */ }

    navigate(targetRoute);
  };

  // Handle quit review with confirmation and pause option
  const handleQuitReview = async () => {
    // Check if currently reviewing
    const isCurrentlyReviewing = application?.workflow?.medicalReview?.status === "in_progress" &&
      application?.workflow?.medicalReview?.reviewerId === user.uid;

    if (isCurrentlyReviewing) {
      const choice = window.confirm(
        "Would you like to:\n" +
        "• Click 'OK' to PAUSE the review (you can resume within 15 minutes)\n" +
        "• Click 'Cancel' to stay on this page\n\n" +
        "Note: If you want to completely abandon the review, you can do so from the dashboard."
      );

      if (choice) {
        // User chose to pause
        setActionLoading((prev) => ({ ...prev, pause: true }));
        try {
          const result = await pauseApplicationReview(applicationId, user.uid);
          if (result.success) {
            stopTimer();
            alert(`Review paused successfully. You have 15 minutes to resume before it's released to other doctors.`);
            await confirmLeave('/doctor/dashboard');
          } else {
            alert("Error pausing review: " + result.error);
          }
        } catch (error) {
          console.error("Error pausing review:", error);
          alert("Failed to pause review");
        } finally {
          setActionLoading((prev) => ({ ...prev, pause: false }));
        }
      }
      // If user clicks Cancel, do nothing (stay on page)
    } else {
      // Just navigate back if not actively reviewing
      await confirmLeave('/doctor/dashboard');
    }
  };

  const handleAddDischargeItem = async () => {
  if (isFALPublished) return;
  // Validate mandatory fields first
  const errs = validateItem(newDischargeItem);
  setItemErrors(errs);
  if (Object.keys(errs).length > 0) return;
  const qty = parseFloat(newDischargeItem.quantity) || 0;
    const unit = parseFloat(newDischargeItem.unitRate) || 0;
    const eligUnit = parseFloat(newDischargeItem.eligibleUnitRate) || 0;
    // If totals provided, prefer them; else compute from unit * qty
    const computedBilled = newDischargeItem.billedAmount !== "" ? (parseFloat(newDischargeItem.billedAmount) || 0) : (unit * qty || 0);
    const computedEligible = newDischargeItem.eligibleAmount !== "" ? (parseFloat(newDischargeItem.eligibleAmount) || 0) : ((eligUnit || unit) * qty || 0);
    const parsedItem = {
      id: Date.now(),
      itemName: newDischargeItem.itemName?.trim() || "",
      category: newDischargeItem.category || "medical",
      payableType: newDischargeItem.payableType || 'payable',
      unitRate: unit || undefined,
      eligibleUnitRate: eligUnit || undefined,
      quantity: qty || undefined,
      billedAmount: computedBilled,
      eligibleAmount: computedEligible,
      remarks: newDischargeItem.remarks?.trim() || "",
      approved: !!newDischargeItem.approved,
    };
    setDischargeItems((prev) => [...prev, parsedItem]);
    setShowAddItemModal(false);
  setNewDischargeItem({ itemName: "", category: "medical", payableType: 'payable', unitRate: "", eligibleUnitRate: "", quantity: "", billedAmount: "", eligibleAmount: "", remarks: "", approved: true, _billedManual: false, _eligibleManual: false });
  setItemErrors({});
  
  // CRITICAL: Immediately save after adding item to prevent data loss
  setTimeout(() => saveDischargeImmediately(), 100);
  };

  

  const handleUpdateDischargeItem = (id, field, value) => {
  if (isFALPublished) return;
    setDischargeItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
    
    // CRITICAL: Immediately save after field update to prevent data loss
    setTimeout(() => saveDischargeImmediately(), 100);
  };

  const handleRemoveDischargeItem = (id) => {
  if (isFALPublished) return;
    setDischargeItems((prev) => prev.filter((item) => item.id !== id));
    
    // CRITICAL: Immediately save after deleting item to prevent data loss
    setTimeout(() => saveDischargeImmediately(), 100);
  };

  // Edit item modal state and handlers
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [editDischargeItem, setEditDischargeItem] = useState(null); // includes id

  const handleOpenEditItem = (item) => {
    if (isFALPublished) return;
    const qty = item.quantity || (item.billedAmount && item.billedAmount !== 0 ? 1 : 0);
    const unit = item.unitRate || (qty > 0 ? (parseFloat(item.billedAmount) || 0) / qty : undefined);
    const eligUnit = item.eligibleUnitRate || (qty > 0 ? (parseFloat(item.eligibleAmount) || 0) / qty : undefined);
    setEditDischargeItem({
      id: item.id,
      itemName: item.itemName || "",
      category: item.category || "medical",
  payableType: item.payableType || 'payable',
      unitRate: unit ?? "",
      eligibleUnitRate: eligUnit ?? "",
      quantity: qty ?? "",
      billedAmount: item.billedAmount ?? "",
      eligibleAmount: item.eligibleAmount ?? "",
      remarks: item.remarks || "",
      approved: !!item.approved,
    });
    setShowEditItemModal(true);
  };

  const handleSaveEditDischargeItem = () => {
  if (isFALPublished || !editDischargeItem) return;
  const errs = validateItem(editDischargeItem);
  setItemErrors(errs);
  if (Object.keys(errs).length > 0) return;
  const qty = parseFloat(editDischargeItem.quantity) || 0;
    const unit = parseFloat(editDischargeItem.unitRate) || 0;
    const eligUnit = parseFloat(editDischargeItem.eligibleUnitRate) || 0;
    const billed = editDischargeItem.billedAmount !== "" ? (parseFloat(editDischargeItem.billedAmount) || 0) : (unit * qty || 0);
    const eligible = editDischargeItem.eligibleAmount !== "" ? (parseFloat(editDischargeItem.eligibleAmount) || 0) : ((eligUnit || unit) * qty || 0);

    setDischargeItems((prev) => prev.map((it) =>
      it.id === editDischargeItem.id
        ? {
          ...it,
          itemName: editDischargeItem.itemName?.trim() || "",
          category: editDischargeItem.category || "medical",
          payableType: editDischargeItem.payableType || 'payable',
          unitRate: unit || undefined,
          eligibleUnitRate: eligUnit || undefined,
          quantity: qty || undefined,
          billedAmount: billed,
          eligibleAmount: eligible,
          remarks: editDischargeItem.remarks?.trim() || "",
          approved: !!editDischargeItem.approved,
        }
        : it
    ));
    setShowEditItemModal(false);
    setEditDischargeItem(null);
  setItemErrors({});
  
  // CRITICAL: Immediately save after editing item to prevent data loss
  setTimeout(() => saveDischargeImmediately(), 100);
  };

  const calculateDischargeTotals = () => {
    const totals = dischargeItems.reduce(
      (acc, item) => {
        if (item.approved) {
          acc.totalBilled += parseFloat(item.billedAmount) || 0;
          acc.totalEligible += parseFloat(item.eligibleAmount) || 0;
        }
        return acc;
      },
      { totalBilled: 0, totalEligible: 0 }
    );

    totals.variance = totals.totalBilled - totals.totalEligible;
    totals.eligibilityPercentage =
      totals.totalBilled > 0
        ? ((totals.totalEligible / totals.totalBilled) * 100).toFixed(2)
        : 0;

    return totals;
  };

  const handleSubmitDischargeReview = async () => {
    if (isFALPublished) {
      alert('Discharge review is locked as Final Approval Letter is already published.');
      return;
    }
    // Require at least one item with mandatory fields valid
    if (dischargeItems.length === 0) {
      alert("Please add at least one discharge item before generating FAL.");
      return;
    }
    const invalid = dischargeItems.find((it) => Object.keys(validateItem(it)).length > 0);
    if (invalid) {
      alert("Some discharge items are missing required fields. Please fix and try again.");
      return;
    }

    setActionLoading((prev) => ({ ...prev, discharge: true }));
    try {
      const dischargeData = {
        items: dischargeItems,
        comments: dischargeComments,
        recommendation,
        uploadedFiles: dischargeFiles,
        modificationReason:
          recommendation === "modify" ? dischargeComments : "",
      };

      const result = await processDischargeReview(
        applicationId,
        user.uid,
        dischargeData
      );
      if (result.success) {
        alert("Discharge review completed successfully");
        setActiveTab("review");
      } else {
        alert("Error processing discharge review: " + result.error);
      }
    } catch (error) {
      console.error("Error processing discharge review:", error);
      alert("Failed to process discharge review");
    } finally {
      setActionLoading((prev) => ({ ...prev, discharge: false }));
    }
  };

  // Immediate autosave reference (for forced saves before reload)
  const saveDischargeImmediately = useCallback(async () => {
    if (!application?.applicationId) return { success: false };
    if (isFALPublished) return { success: false };
    try {
      const res = await saveDischargeDraft(
        application.applicationId,
        user?.uid,
        { 
          items: dischargeItems, 
          comments: dischargeComments, 
          uploadedFiles: dischargeFiles, 
          falOverrides,
          falManualAdjustments,
          falComputedPreview,
          autoSaved: false 
        }
      );
      if (res?.success) setLastAutoSaveAt(new Date());
      return res;
    } catch (e) {
      console.warn('Immediate save failed', e);
      return { success: false, error: e.message };
    }
  }, [application?.applicationId, dischargeItems, dischargeComments, dischargeFiles, falOverrides, falManualAdjustments, falComputedPreview, user?.uid, isFALPublished]);

  // Debounced autosave of discharge items/comments as draft (reduced to 500ms for faster saves)
  useEffect(() => {
    if (!application?.applicationId) return;
    if (isFALPublished) return; // stop autosave once FAL is published
    const timer = setTimeout(async () => {
      try {
        const res = await saveDischargeDraft(
          application.applicationId,
          user?.uid,
          { 
            items: dischargeItems, 
            comments: dischargeComments, 
            uploadedFiles: dischargeFiles, 
            falOverrides,
            falManualAdjustments,
            falComputedPreview,
            autoSaved: true 
          }
        );
        if (res?.success) setLastAutoSaveAt(new Date());
      } catch (e) {
        console.warn('Autosave failed', e);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [application?.applicationId, dischargeItems, dischargeComments, dischargeFiles, falOverrides, falManualAdjustments, falComputedPreview, user?.uid, isFALPublished]);

  // Load document queries for all documents
  useEffect(() => {
    if (!application?.applicationId) return;
    const loadQueries = async () => {
      try {
        const result = await getDocumentQueries(application.applicationId);
        if (result.success) {
          // Group queries by documentKey
          const byKey = {};
          result.queries.forEach(q => {
            if (!byKey[q.documentKey]) byKey[q.documentKey] = [];
            byKey[q.documentKey].push(q);
          });
          setDocumentQueries(byKey);
        }
      } catch (err) {
        console.error('Failed to load document queries:', err);
      }
    };
    loadQueries();
  }, [application?.applicationId]);

  const handleRaiseQuery = async () => {
    if (!queryComment.trim()) {
      alert('Please enter a comment');
      return;
    }
    try {
      setActionLoading(prev => ({ ...prev, 'raise_query': true }));
      const result = await raiseDocumentQuery(
        application.applicationId,
        queryDocumentKey,
        queryComment.trim(),
        {
          uid: user.uid,
          name: user.profile?.name || user.email,
          email: user.email,
          team: 'doctor',
        }
      );
      if (result.success) {
        alert('Query raised successfully');
        setShowQueryModal(false);
        setQueryComment('');
        setQueryDocumentKey(null);
        // Reload queries
        const updatedQueries = await getDocumentQueries(application.applicationId);
        if (updatedQueries.success) {
          const byKey = {};
          updatedQueries.queries.forEach(q => {
            if (!byKey[q.documentKey]) byKey[q.documentKey] = [];
            byKey[q.documentKey].push(q);
          });
          setDocumentQueries(byKey);
        }
      } else {
        alert('Failed to raise query: ' + result.error);
      }
    } catch (err) {
      console.error('Error raising query:', err);
      alert('Failed to raise query');
    } finally {
      setActionLoading(prev => ({ ...prev, 'raise_query': false }));
    }
  };

  const openQueryModal = (docKey) => {
    setQueryDocumentKey(docKey);
    setQueryComment('');
    setShowQueryModal(true);
  };

  const openDocument = (document) => {
    try { setLastScrollY(window.scrollY || 0); } catch (e) {}
    try {
      if (rightPanelRef?.current) {
        setLastRightScrollTop(rightPanelRef.current.scrollTop || 0);
      }
    } catch (e) {}
    setSelectedDocument(document);
    setShowDocumentModal(true);
    // Close discussion panel if open to ensure document viewer gets full space
    setShowDiscussionPanel(false);
    // Reset zoom when opening a new document
    setPdfZoom(100);
    setImageZoom(100);
  };

  const closeDocumentPanel = () => {
    setShowDocumentModal(false);
    setSelectedDocument(null);
    // Reset Excel state
    setExcelData(null);
    setExcelSheets([]);
    setActiveSheet(0);
    setExcelLoading(false);
    setExcelError(null);
    // Restore scroll positions where user was before opening
    const y = lastScrollY || 0;
    const rightY = lastRightScrollTop || 0;
    try {
      requestAnimationFrame(() => {
        window.scrollTo(0, y);
        if (rightPanelRef?.current) {
          rightPanelRef.current.scrollTop = rightY;
        }
        setTimeout(() => {
          window.scrollTo(0, y);
          if (rightPanelRef?.current) {
            rightPanelRef.current.scrollTop = rightY;
          }
        }, 0);
      });
    } catch (e) {}
  };

  // Load Excel file from Firebase Storage and parse with SheetJS
  useEffect(() => {
    const loadExcelFile = async () => {
      if (!selectedDocument) return;

      const isExcel = /\.(xlsx?|xls|csv)$/i.test(selectedDocument.fileName || selectedDocument.name || "");
      if (!isExcel) {
        setExcelData(null);
        setExcelSheets([]);
        setActiveSheet(0);
        setExcelError(null);
        return;
      }

      setExcelLoading(true);
      setExcelError(null);
      setExcelData(null);

      try {
        let arrayBuffer;

        try {
          // First, try to extract the storage path from the Firebase download URL
          const url = selectedDocument.url;
          let storagePath = null;

          // Parse Firebase Storage download URL to get the file path
          if (url.includes('firebase')) {
            const match = url.match(/\/o\/(.+?)\?/);
            if (match) {
              storagePath = decodeURIComponent(match[1]);
              console.log('Extracted storage path:', storagePath);
            }
          }

          if (storagePath) {
            // Use the extracted path to create a proper storage reference
            const fileRef = storageRef(storage, storagePath);
            console.log('Attempting to fetch with Firebase SDK...');
            const bytes = await getBytes(fileRef);
            arrayBuffer = bytes.buffer;
            console.log('Successfully fetched via Firebase SDK, size:', arrayBuffer.byteLength);
          } else {
            throw new Error('Could not extract storage path from URL');
          }
        } catch (storageError) {
          console.log("Firebase Storage SDK failed, trying direct fetch:", storageError);

          // Fallback to direct fetch
          const response = await fetch(selectedDocument.url, {
            mode: 'cors',
            credentials: 'omit'
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          arrayBuffer = await response.arrayBuffer();
          console.log('Successfully fetched via direct fetch, size:', arrayBuffer.byteLength);
        }

        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          throw new Error('File is empty or could not be loaded');
        }

        // Parse with SheetJS - try multiple parsing strategies
        let workbook;
        const uint8Array = new Uint8Array(arrayBuffer);

        // Detect file type
        const isCsv = /\.csv$/i.test(selectedDocument.fileName || "");

        if (isCsv) {
          // For CSV files, convert to text first
          const text = new TextDecoder('utf-8').decode(uint8Array);
          console.log('CSV text preview:', text.substring(0, 200));
          workbook = XLSX.read(text, {
            type: 'string',
            raw: false
          });
        } else {
          // For Excel files
          console.log('Parsing Excel file...');
          workbook = XLSX.read(uint8Array, {
            type: 'array',
            cellDates: true,
            cellNF: false,
            cellText: false,
            raw: false
          });
        }

        console.log('Workbook parsed:', workbook);
        console.log('Sheet names:', workbook.SheetNames);

        const sheetNames = workbook.SheetNames || [];
        if (sheetNames.length === 0) {
          throw new Error('No sheets found in the file');
        }

        const sheets = sheetNames.map(name => {
          const worksheet = workbook.Sheets[name];
          console.log(`Processing sheet "${name}":`, worksheet);

          // Try different conversion methods
          let data;
          try {
            // Method 1: Convert to array of arrays (most reliable)
            data = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              defval: "",
              blankrows: true,
              raw: false
            });
            console.log(`Sheet "${name}" data (method 1):`, data.slice(0, 3));
          } catch (e1) {
            console.log('Method 1 failed, trying method 2:', e1);
            try {
              // Method 2: Convert to objects then back to arrays
              const objects = XLSX.utils.sheet_to_json(worksheet);
              if (objects.length > 0) {
                const headers = Object.keys(objects[0]);
                data = [headers, ...objects.map(obj => headers.map(h => obj[h] || ""))];
              } else {
                data = [];
              }
              console.log(`Sheet "${name}" data (method 2):`, data.slice(0, 3));
            } catch (e2) {
              console.log('Method 2 failed, trying method 3:', e2);
              // Method 3: Manual range processing
              const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');
              data = [];
              for (let R = range.s.r; R <= range.e.r; ++R) {
                const row = [];
                for (let C = range.s.c; C <= range.e.c; ++C) {
                  const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                  const cell = worksheet[cellAddress];
                  row.push(cell ? (cell.v || "") : "");
                }
                data.push(row);
              }
              console.log(`Sheet "${name}" data (method 3):`, data.slice(0, 3));
            }
          }

          return {
            name,
            data: data || []
          };
        });

        console.log('Final processed sheets:', sheets);
        setExcelSheets(sheets);
        setExcelData(workbook);
        setActiveSheet(0);

      } catch (error) {
        console.error("Excel loading error:", error);
        setExcelError(`Failed to load Excel file: ${error.message}`);
      } finally {
        setExcelLoading(false);
      }
    };

    loadExcelFile();
  }, [selectedDocument]);

  // Render Excel data as HTML table
  const renderExcelTable = (sheetData) => {
    console.log('Rendering sheet data:', sheetData);

    if (!sheetData || sheetData.length === 0) {
      return (
        <div className="p-8 text-center text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-2 text-gray-400" />
          <p>No data found in this sheet</p>
          <p className="text-xs mt-2">Sheet data: {JSON.stringify(sheetData)}</p>
        </div>
      );
    }

    // Filter out completely empty rows
    const filteredData = sheetData.filter(row => {
      if (!Array.isArray(row)) return true;
      return row.some(cell => cell !== null && cell !== undefined && cell !== "");
    });

    if (filteredData.length === 0) {
      return (
        <div className="p-8 text-center text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-2 text-gray-400" />
          <p>All rows in this sheet are empty</p>
          <p className="text-xs mt-2">Original rows: {sheetData.length}</p>
        </div>
      );
    }

    // Find the maximum number of columns
    const maxCols = Math.max(...filteredData.map(row => Array.isArray(row) ? row.length : 1));

    return (
      <div className="overflow-auto h-full">
        <table className="min-w-full border-collapse border border-gray-300 text-sm">
          <tbody>
            {filteredData.map((row, rowIndex) => (
              <tr key={rowIndex} className={rowIndex === 0 ? "bg-gray-100 font-semibold" : "hover:bg-gray-50"}>
                {Array.isArray(row) ? (
                  // Ensure all rows have the same number of columns
                  Array.from({ length: maxCols }, (_, cellIndex) => {
                    const cell = row[cellIndex];
                    return (
                      <td
                        key={cellIndex}
                        className="border border-gray-300 px-2 py-1 text-left align-top"
                        style={{ minWidth: '100px', maxWidth: '300px' }}
                      >
                        {(() => {
                          const ClampedText = require("../../components/common/ClampedText.jsx").default;
                          return (
                            <ClampedText value={cell !== null && cell !== undefined ? String(cell) : ""} />
                          );
                        })()}
                      </td>
                    );
                  })
                ) : (
                  <td
                    className="border border-gray-300 px-2 py-1 align-top"
                    colSpan={maxCols}
                    title={row !== null && row !== undefined ? String(row) : ""}
                  >
                    {(() => {
                      const ClampedText = require("../../components/common/ClampedText.jsx").default;
                      return (
                        <ClampedText value={row !== null && row !== undefined ? String(row) : ""} />
                      );
                    })()}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Debug info */}
        <div className="p-2 bg-gray-50 text-xs text-gray-600 border-t">
          Showing {filteredData.length} rows × {maxCols} columns
        </div>
      </div>
    );
  };

  const filterDiscussions = (discussions) => {
    return discussions.filter((discussion) => {
      const matchesSearch =
        discussion.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        discussion.messages?.some((msg) =>
          msg.message?.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesUser =
        filterUser === "all" ||
        discussion.messages?.some((msg) => msg.senderRole === filterUser);

      return matchesSearch && matchesUser;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-teal-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading application for review...</p>
        </div>
      </div>
    );
  }


  // No inline Excel parsing; iframe viewers handle rendering

  if (!application) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Application Not Found
          </h3>
          <button
            onClick={() => confirmLeave('/doctor/dashboard')}
            className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2 inline" />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const totals = calculateDischargeTotals();

  const renderApplicationOverview = () => (
    <div className="space-y-6">
  {/* Approval for Process Section - Moved to Overview */}


      {/* Approval Status Display */}
  {approvedForProcessEffective && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-green-900">
              DiagnosisApproved
            </h3>
          </div>

          <p className="text-green-700 mb-3">
            This application is now visible to Policy Underwriting and Financial teams.
          </p>

          {/* {application.workflow?.medicalVerification?.approvalForProcessComments && (
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <p className="text-sm text-gray-600 mb-1">Approval Comments:</p>
              <p className="text-gray-900">{application.workflow.medicalVerification.approvalForProcessComments}</p>
            </div>
          )} */}

          <p className="text-sm text-green-600 mt-3">
    Approved on: {formatDate(application.workflow?.medicalVerification?.approvedForProcessAt || application?.updatedAt || application?.submittedAt)}
          </p>
        </div>
      )}

      {/* Patient Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-teal-100 p-2 rounded-lg">
            <User className="h-5 w-5 text-teal-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            Patient Information
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-500">
              Patient Name
            </label>
            <p className="text-lg font-semibold text-gray-900">
              {application.patient?.name}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">
              Age & Gender
            </label>
            <p className="text-gray-900">
              {application.patient?.age} years, {application.patient?.gender}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Contact</label>
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Phone className="h-3 w-3 text-gray-400" />
                <p className="text-sm text-gray-900">
                  {application.patient?.phone}
                </p>
              </div>
              {application.patient?.email && (
                <div className="flex items-center space-x-2">
                  <Mail className="h-3 w-3 text-gray-400" />
                  <p className="text-sm text-gray-900">
                    {application.patient?.email}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">
              Aadhar Number
            </label>
            <p className="text-gray-900 font-mono">
              ****-****-{application.patient?.kyc?.aadharNumber?.slice(-4)}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Address</label>
            <div className="flex items-start space-x-2">
              <MapPin className="h-3 w-3 text-gray-400 mt-1" />
              <p className="text-sm text-gray-900">
                {application.patient?.address}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Medical Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Stethoscope className="h-5 w-5 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            Medical Information
          </h2>
        </div>

        {/* First Row: Doctor Details */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Doctor Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-500">
                Consulting Doctor
              </label>
              <p className="text-gray-900 font-medium">
                {application.medical?.doctor?.name}
              </p>
              <p className="text-sm text-gray-600">
                {application.medical?.doctor?.designation}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">
                Admission Date
              </label>
              <div className="flex items-center space-x-2">
                <Calendar className="h-3 w-3 text-gray-400" />
                <p className="text-sm text-gray-900">
                  {application.medical?.admissionDate}
                </p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">
                Discharge Date
              </label>
              <div className="flex items-center space-x-2">
                <Calendar className="h-3 w-3 text-gray-400" />
                <p className="text-sm text-gray-900">
                  {application.medical?.dischargeDate || "TBD"}
                </p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">
                Estimated Cost
              </label>
              <p className="text-lg font-semibold text-teal-600">
                ₹{application.medical?.estimatedCost?.toLocaleString("en-IN")}
              </p>
            </div>
          </div>
        </div>

        {/* Second Row: Diagnosis Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Diagnosis & Treatment</h3>
              <button
                onClick={startAddDiagnosis}
                disabled={approvedForProcessEffective}
                className={`flex items-center gap-1 text-sm px-3 py-1.5 rounded-md ${approvedForProcessEffective ? 'opacity-50 cursor-not-allowed bg-gray-200 text-gray-600' : 'bg-teal-600 text-white hover:bg-teal-700'}`}
                title={approvedForProcessEffective ? 'Disabled after approval for process' : 'Add diagnosis'}
            >
              <Plus className="h-4 w-4" />
              <span>Add Diagnosis</span>
            </button>
          </div>

          {/* Add Diagnosis Form */}
          {isAddingDiagnosis && (
            <div className="bg-teal-50 rounded-lg p-4 border border-teal-200 mb-6">
              <div className="space-y-4">
                {/* Risky Application Flag */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <button
                    onClick={() => toggleSection('risk')}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${riskInfo.flagged ? 'bg-red-100' : 'bg-gray-100'}`}>
                        <AlertTriangle className={`h-4 w-4 ${riskInfo.flagged ? 'text-red-600' : 'text-gray-600'}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Risky Application</h3>
                        <p className="text-sm text-gray-600">
                          {riskInfo.flagged ? `Flagged${riskInfo.flaggedByName ? ` by ${riskInfo.flaggedByName}` : ''}` : 'Not flagged'}
                        </p>
                      </div>
                    </div>
                    {collapsedSections.risk ? (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </button>

                  {!collapsedSections.risk && (
                    <div className="px-4 pb-4 border-t border-gray-100">
                      <div className="mt-4 space-y-3">
                        {riskInfo.flagged && (
                          <div className="p-3 rounded border border-red-200 bg-red-50">
                            <p className="text-sm text-red-800 font-medium">Flagged as risky</p>
                            {riskInfo.comment && (
                              <p className="text-sm text-red-700 mt-1 whitespace-pre-wrap">{riskInfo.comment}</p>
                            )}
                            {riskInfo.flaggedByName && (
                              <p className="text-xs text-red-600 mt-1">By {riskInfo.flaggedByName}</p>
                            )}
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Add/Update Risk Comment</label>
                          <textarea
                            value={riskComment}
                            onChange={(e) => setRiskComment(e.target.value)}
                            rows="3"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                            placeholder="Why is this application risky? Add context for other doctors."
                          />
                        </div>

                        <div className="flex items-center justify-end space-x-2">
                          {riskInfo.flagged && (
                            <button
                              onClick={() => handleSetRiskFlag(false)}
                              disabled={riskSaving}
                              className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                              {riskSaving ? 'Updating...' : 'Unflag'}
                            </button>
                          )}
                          <button
                            onClick={() => handleSetRiskFlag(true)}
                            disabled={riskSaving || !riskComment.trim()}
                            className="px-4 py-2 text-sm text-white bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {riskSaving ? 'Saving...' : (riskInfo.flagged ? 'Update Flag' : 'Flag as Risky')}
                          </button>
                        </div>

                        {Array.isArray(riskInfo.notes) && riskInfo.notes.length > 0 && (
                          <div className="pt-2 border-t border-gray-100">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Risk Notes History</h4>
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                              {riskInfo.notes.slice().reverse().map((n) => (
                                <div key={n.id} className="p-2 bg-gray-50 border border-gray-200 rounded">
                                  <div className="flex justify-between items-start">
                                    <span className="text-sm font-medium text-gray-800">{n.action === 'flag' ? 'Flagged' : 'Unflagged'}</span>
                                    <span className="text-xs text-gray-500">{n.at?.toDate?.()?.toLocaleString?.() || (n.at ? new Date(n.at).toLocaleString() : '')}</span>
                                  </div>
                                  {n.comment && <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{n.comment}</p>}
                                  {n.byName && <p className="text-xs text-gray-500 mt-1">By {n.byName}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Diagnosis Name *</label>
                  <textarea
                    value={editedDiagnosis}
                    onChange={(e) => setEditedDiagnosis(e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Enter diagnosis name..."
                  />
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={fetchAiSuggestionsForAdd}
                      disabled={aiDiagLoadingAdd}
                      className={`text-sm px-3 py-1.5 rounded-md ${aiDiagLoadingAdd ? 'bg-gray-200 text-gray-600' : 'bg-purple-600 text-white hover:bg-purple-700'}`}
                      title="Get AI diagnosis suggestions with ICD codes"
                    >
                      {aiDiagLoadingAdd ? 'Getting suggestions…' : 'AI Suggest (ICD)'}
                    </button>
                    {showAiPanelAdd && (
                      <button type="button" onClick={() => setShowAiPanelAdd(false)} className="text-sm text-gray-600 hover:text-gray-800">Hide</button>
                    )}
                  </div>
                  {showAiPanelAdd && (
                    <div className="mt-2 border border-purple-200 bg-purple-50 rounded-md p-3">
                      <p className="text-sm font-medium text-purple-800 mb-2">AI Suggestions</p>
                      {aiDiagErrorAdd && <p className="text-sm text-red-700 mb-2">{aiDiagErrorAdd}</p>}
                      {!aiDiagLoadingAdd && !aiDiagErrorAdd && aiDiagSuggestionsAdd?.length === 0 && (
                        <p className="text-sm text-gray-600">No suggestions yet.</p>
                      )}
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {aiDiagSuggestionsAdd?.map((sug, idx) => (
                          <div key={idx} className="bg-white border border-gray-200 rounded p-2">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="text-sm font-semibold text-gray-900">{sug.diagnosis}</div>
                                <div className="text-xs text-gray-700">{sug.icdCode} {sug.icdTitle ? `- ${sug.icdTitle}` : ''}</div>
                                {sug.reasoning && <div className="text-xs text-gray-500 mt-1">Reason: {sug.reasoning}</div>}
                              </div>
                              <div className="flex items-center gap-2">
                                {typeof sug.confidence === 'number' && (
                                  <span className="text-xs text-gray-600">{Math.round(sug.confidence * 100)}%</span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => acceptAiSuggestionAdd(sug)}
                                  className="text-xs px-2 py-1 rounded bg-teal-600 text-white hover:bg-teal-700"
                                >
                                  Accept
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Treatment Type</label>
                    <select
                      value={editedTreatmentType}
                      onChange={(e) => setEditedTreatmentType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      <option value="">Select Treatment Type</option>
                      {treatmentTypes.map((type) => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Procedure Name</label>
                    <input
                      type="text"
                      value={editedProcedureName}
                      onChange={(e) => setEditedProcedureName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Enter procedure name..."
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Enter description..."
                  />
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">ICD Code (Optional)</label>
                  <input
                    type="text"
                    value={icdSearchTerm}
                    onChange={(e) => {
                      setIcdSearchTerm(e.target.value);
                      setShowIcdSuggestions(e.target.value.length > 0);
                      setSelectedIcdIndex(-1);
                    }}
                    onKeyDown={handleIcdKeyDown}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Type to search ICD codes..."
                  />
                  {showIcdSuggestions && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {getFilteredIcdCodes().map((icd, index) => (
                        <div
                          key={icd.code}
                          onClick={() => handleIcdSelection(icd)}
                          className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${index === selectedIcdIndex ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                        >
                          <div className="font-medium text-gray-900">{icd.code}</div>
                          <div className="text-sm text-gray-600">{icd.description}</div>
                        </div>
                      ))}
                      {getFilteredIcdCodes().length === 0 && icdSearchTerm && (
                        <div className="px-3 py-2 text-gray-500 text-sm">No matching ICD codes found</div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={saveNewDiagnosis}
                    disabled={!editedDiagnosis.trim() || actionLoading.saveNewDiagnosis}
                    className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {actionLoading.saveNewDiagnosis ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        <span>Adding...</span>
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        <span>Add Diagnosis</span>
                      </>
                    )}
                  </button>
                  <button onClick={cancelAddDiagnosis} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300">Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Diagnosis Grid Display */}
          {application?.medical?.treatments?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {application.medical.treatments.map((treatment) => (
                <div
                  key={treatment.id}
                  className="bg-white rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow"
                >
                  {/* Check if this specific diagnosis is being edited */}
                  {isEditingDiagnosis && editingDiagnosisId === treatment.id ? (
                    <div className="space-y-4">
                      {/* Diagnosis Input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Diagnosis Name *
                        </label>
                        <textarea
                          value={editedDiagnosis}
                          onChange={(e) => setEditedDiagnosis(e.target.value)}
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter diagnosis name..."
                        />
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={fetchAiSuggestionsForEdit}
                            disabled={aiDiagLoadingEdit}
                            className={`text-sm px-3 py-1.5 rounded-md ${aiDiagLoadingEdit ? 'bg-gray-200 text-gray-600' : 'bg-purple-600 text-white hover:bg-purple-700'}`}
                            title="Get AI diagnosis suggestions with ICD codes"
                          >
                            {aiDiagLoadingEdit ? 'Getting suggestions…' : 'AI Suggest (ICD)'}
                          </button>
                          {showAiPanelEdit && (
                            <button type="button" onClick={() => setShowAiPanelEdit(false)} className="text-sm text-gray-600 hover:text-gray-800">Hide</button>
                          )}
                        </div>
                        {showAiPanelEdit && (
                          <div className="mt-2 border border-purple-200 bg-purple-50 rounded-md p-3">
                            <p className="text-sm font-medium text-purple-800 mb-2">AI Suggestions</p>
                            {aiDiagErrorEdit && <p className="text-sm text-red-700 mb-2">{aiDiagErrorEdit}</p>}
                            {!aiDiagLoadingEdit && !aiDiagErrorEdit && aiDiagSuggestionsEdit?.length === 0 && (
                              <p className="text-sm text-gray-600">No suggestions yet.</p>
                            )}
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                              {aiDiagSuggestionsEdit?.map((sug, idx) => (
                                <div key={idx} className="bg-white border border-gray-200 rounded p-2">
                                  <div className="flex items-start justify-between gap-2">
                                    <div>
                                      <div className="text-sm font-semibold text-gray-900">{sug.diagnosis}</div>
                                      <div className="text-xs text-gray-700">{sug.icdCode} {sug.icdTitle ? `- ${sug.icdTitle}` : ''}</div>
                                      {sug.reasoning && <div className="text-xs text-gray-500 mt-1">Reason: {sug.reasoning}</div>}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {typeof sug.confidence === 'number' && (
                                        <span className="text-xs text-gray-600">{Math.round(sug.confidence * 100)}%</span>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => acceptAiSuggestionEdit(sug)}
                                        className="text-xs px-2 py-1 rounded bg-teal-600 text-white hover:bg-teal-700"
                                      >
                                        Accept
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Treatment Type and Procedure Name in a row */}
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Treatment Type
                          </label>
                          <select
                            value={editedTreatmentType}
                            onChange={(e) => setEditedTreatmentType(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select Treatment Type</option>
                            {treatmentTypes.map((type) => (
                              <option key={type} value={type}>
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Procedure Name
                          </label>
                          <input
                            type="text"
                            value={editedProcedureName}
                            onChange={(e) => setEditedProcedureName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter procedure name..."
                          />
                        </div>
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={editedDescription}
                          onChange={(e) => setEditedDescription(e.target.value)}
                          rows="2"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter description..."
                        />
                      </div>

                      {/* ICD Code Search */}
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ICD Code (Optional)
                        </label>
                        <input
                          type="text"
                          value={icdSearchTerm}
                          onChange={(e) => {
                            setIcdSearchTerm(e.target.value);
                            setShowIcdSuggestions(e.target.value.length > 0);
                            setSelectedIcdIndex(-1);
                          }}
                          onKeyDown={handleIcdKeyDown}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Type to search ICD codes..."
                        />

                        {/* ICD Code Suggestions Dropdown */}
                        {showIcdSuggestions && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {getFilteredIcdCodes().map((icd, index) => (
                              <div
                                key={icd.code}
                                onClick={() => handleIcdSelection(icd)}
                                className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${index === selectedIcdIndex ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                                  }`}
                              >
                                <div className="font-medium text-gray-900">{icd.code}</div>
                                <div className="text-sm text-gray-600">{icd.description}</div>
                              </div>
                            ))}
                            {getFilteredIcdCodes().length === 0 && icdSearchTerm && (
                              <div className="px-3 py-2 text-gray-500 text-sm">
                                No matching ICD codes found
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={saveDiagnosisChanges}
                          disabled={!editedDiagnosis.trim() || actionLoading.saveDiagnosis}
                          className="bg-blue-600 text-white px-3 py-1.5 text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                        >
                          {actionLoading.saveDiagnosis ? (
                            <>
                              <Loader className="h-3 w-3 animate-spin" />
                              <span>Saving...</span>
                            </>
                          ) : (
                            <>
                              <Save className="h-3 w-3" />
                              <span>Save</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={cancelDiagnosisEdit}
                          className="bg-gray-200 text-gray-700 px-3 py-1.5 text-sm rounded-lg hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h6 className="font-medium text-gray-800 line-clamp-2">
                              {treatment.diagnosisName}
                            </h6>
                            <button
                              onClick={() => startDiagnosisEdit(treatment.id)}
                              disabled={approvedForProcessEffective}
                              className={`flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm ${approvedForProcessEffective ? 'opacity-50 cursor-not-allowed hover:text-blue-600' : ''}`}
                              title={approvedForProcessEffective ? 'Disabled after approval for process' : 'Edit diagnosis'}
                            >
                              <Edit className="h-3 w-3" />
                              <span>Edit</span>
                            </button>
                          </div>

                          {/* Show ICD Code if available */}
                          {treatment.icdCode && (
                            <div className="bg-blue-50 rounded p-2 mb-2">
                              <p className="text-xs text-blue-800 font-medium">
                                ICD: {treatment.icdCode}
                              </p>
                              {treatment.icdDescription && (
                                <p className="text-xs text-blue-600 line-clamp-2">
                                  {treatment.icdDescription}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Show review information if available */}
                          {treatment.reviewedBy && (
                            <p className="text-xs text-gray-500 mb-2">
                              Reviewed by {treatment.reviewerName} on{" "}
                              {new Date(treatment.reviewedAt?.seconds * 1000 || treatment.reviewedAt).toLocaleDateString()}
                            </p>
                          )}

                          <div className="space-y-1">
                            {treatment.treatmentType && (
                              <div className="flex items-center space-x-2">
                                <span className={`text-xs px-2 py-1 rounded-full ${treatment.treatmentType === 'medical' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                                  {treatment.treatmentType}
                                </span>
                              </div>
                            )}
                            {treatment.procedureName && (
                              <p className="text-sm text-gray-600">
                                <strong>Procedure:</strong> {treatment.procedureName}
                              </p>
                            )}
                            {treatment.description && (
                              <p className="text-sm text-gray-600 line-clamp-3">
                                {treatment.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Treatment Document Upload - Only show when not editing */}
                      <div className="border-t border-gray-200 pt-3">
                        <label className="text-xs font-medium text-gray-700 mb-2 block">
                          Supporting Document
                        </label>
                        {!application?.documents?.treatmentDocs?.[treatment.id] ? (
                          <div className="text-xs text-gray-500">No document uploaded</div>
                        ) : (
                          <div className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                            <div className="flex items-center space-x-2">
                              <FileText className="h-3 w-3 text-green-600" />
                              <span className="text-xs text-green-800 truncate">
                                {application?.documents?.treatmentDocs[treatment.id]?.name}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8">
              <div className="text-center">
                <Stethoscope className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-3">No diagnoses added yet.</p>
                <button
                  onClick={startAddDiagnosis}
                  disabled={approvedForProcessEffective}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg ${approvedForProcessEffective ? 'opacity-50 cursor-not-allowed bg-gray-200 text-gray-600' : 'bg-teal-600 text-white hover:bg-teal-700'}`}
                >
                  <Plus className="h-4 w-4" />
                  Add First Diagnosis
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Approval Section */}
  {!approvedForProcessEffective && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-green-100 p-2 rounded-lg">
                <FastForward className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-green-900">
                Approve Application for Process
              </h3>
            </div>

            <p className="text-green-700 mb-4">
              Quick approve this application to make it visible to Policy Underwriting and Financial teams
              while you continue your detailed review.
            </p>

            {/* AI Approval Assistant */}
            <div className="bg-white border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Stethoscope className="h-4 w-4 text-green-600" />
                  <span className="font-medium text-green-900">AI Approval Assistant</span>
                </div>
                <button
                  onClick={runAiApprovalReview}
                  disabled={aiApproveLoading}
                  className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {aiApproveLoading ? 'Analyzing…' : 'Review Diagnoses'}
                </button>
              </div>
              {showAiApprovePanel && (
                <div className="space-y-2">
                  {aiApproveError && (
                    <div className="text-red-600 text-sm">{aiApproveError}</div>
                  )}
                  {!aiApproveError && aiApproveLoading && (
                    <div className="text-sm text-gray-600">Running AI review…</div>
                  )}
                  {aiApproveResult && (
                    <div className="space-y-2">
                      <div className={`text-sm ${aiApproveResult.approveRecommended ? 'text-green-700' : 'text-amber-700'}`}>
                        {aiApproveResult.approveRecommended ? 'Ready to approve based on current data.' : 'Not ready yet. See reasons below.'}
                      </div>
                      <ul className="list-disc pl-5 text-sm text-gray-700">
                        {(aiApproveResult.reasons || []).map((r, i) => (
                          <li key={i}>{r}</li>
                        ))}
                      </ul>
                      {!!aiApproveResult.suggestionsById && Object.keys(aiApproveResult.suggestionsById).length > 0 && (
                        <div className="bg-green-50 border border-green-200 rounded p-3">
                          <div className="text-sm font-medium text-green-900 mb-2">ICD suggestions for missing entries</div>
                          <div className="max-h-40 overflow-auto pr-1 text-sm">
                            {Object.entries(aiApproveResult.suggestionsById).map(([id, s]) => (
                              <div key={id} className="flex items-start justify-between py-1 border-b border-green-100 last:border-b-0">
                                <div>
                                  <div className="font-mono">{s.icdCode} — {s.icdTitle}</div>
                                  <div className="text-xs text-gray-600">{s.reasoning}</div>
                                </div>
                                <div className="text-xs text-gray-500">{Math.round((s.confidence||0)*100)}%</div>
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-end mt-2">
                            <button
                              onClick={applyAiApprovalSuggestions}
                              disabled={actionLoading.applyAiApproval}
                              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              {actionLoading.applyAiApproval ? 'Applying…' : 'Apply Suggestions'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleApproveForProcess}
                disabled={actionLoading.approveForProcess}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {actionLoading.approveForProcess ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span>Approving...</span>
                  </>
                ) : (
                  <>
                    <ThumbsUp className="h-4 w-4" />
                    <span>Approve for Process</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Policy Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-green-100 p-2 rounded-lg">
            <Shield className="h-5 w-5 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            Insurance Policy
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-500">
              Policy Number
            </label>
            <p className="text-gray-900 font-mono">
              {application.policy?.number}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">
              Insurance Company
            </label>
            <p className="text-gray-900">
              {application.policy?.insuranceCompany}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">
              Coverage Amount
            </label>
            <p className="text-lg font-semibold text-gray-900">
              ₹{application.policy?.coverageAmount?.toLocaleString("en-IN")}
            </p>
          </div>
        </div>
      </div>

      {/* Priority and Status */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Application Status
        </h3>
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <span className="text-gray-600">Priority:</span>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${application.priority === "urgent"
                ? "bg-red-100 text-red-800"
                : application.priority === "high"
                  ? "bg-orange-100 text-orange-800"
                  : "bg-gray-100 text-gray-800"
                }`}
            >
              {application.priority?.toUpperCase()}
            </span>
          </div>

          {application.emergency && (
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <span className="text-red-600 font-medium">Emergency Case</span>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <span className="text-gray-600">Submitted:</span>
            <span className="font-medium">
              {formatDate(application.submittedAt)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // New: Scrutiny Document Review section (shows discharge + dispatch docs)
  const renderDispatchScrutiny = () => {
    // Include both Scrutiny uploads (dischargeDocuments) and Dispatch uploads
    const dischargeDocs = Array.isArray(application?.dischargeDocuments) ? application.dischargeDocuments : [];
    const dispatchDocs = Array.isArray(application?.dispatchDocuments) ? application.dispatchDocuments : [];
    // Merge lists; ids are unique per upload, so a simple concat works. If duplicates ever appear, de-dupe by id.
    const docs = [...dischargeDocs, ...dispatchDocs];
    const dispatchClearance = application?.workflow?.dischargeSettlement?.dispatchClearance;
    const isDispatchApproved = dispatchClearance?.status === 'approved';
    const approvedDocs = docs.filter((d) => d.dispatchStatus === 'approved');
    
    const onApprove = async (doc, approve) => {
      try {
        setActionLoading((p) => ({ ...p, [`scrutiny_${doc.id}`]: true }));
        
        let remarks = '';
        
        // If rejecting, show confirmation dialog and ask for comment
        if (!approve) {
          // Show confirmation dialog
          const confirmed = window.confirm(
            `Are you sure you want to reject "${doc.title || doc.fileName}"?\n\nThis action will mark the document as rejected and notify the hospital.`
          );
          
          if (!confirmed) {
            setActionLoading((p) => ({ ...p, [`scrutiny_${doc.id}`]: false }));
            return;
          }
          
          // Ask for rejection comment
          const comment = window.prompt(
            'Please provide a reason for rejecting this document:\n\nThis comment will be visible to the hospital and help them understand what needs to be corrected.',
            ''
          );
          
          if (comment === null) {
            // User cancelled
            setActionLoading((p) => ({ ...p, [`scrutiny_${doc.id}`]: false }));
            return;
          }
          
          remarks = comment || 'Document rejected by doctor';
        }
        
        const res = await setDispatchApprovalForDocument(applicationId, doc.id, {
          approved: approve,
          remarks: remarks,
          doctorId: user.uid,
          doctorName: user.profile?.name || user.email
        });
        if (!res.success) throw new Error(res.error || 'Failed');
        // Patch local state instead of reloading whole application
        const status = approve ? 'approved' : 'rejected';
        setApplication((prev) => {
          if (!prev) return prev;
          const patchFields = (item) => ({
            ...item,
            dispatchStatus: status,
            dispatchReviewerId: user.uid,
            dispatchReviewerName: user.profile?.name || user.email,
            dispatchReviewedAt: new Date(),
            dispatchRemarks: remarks
          });
          
          const updatedDischarge = Array.isArray(prev.dischargeDocuments)
            ? prev.dischargeDocuments.map((d) => (d.id === doc.id ? patchFields(d) : d))
            : prev.dischargeDocuments;
          const updatedDispatch = Array.isArray(prev.dispatchDocuments)
            ? prev.dispatchDocuments.map((d) => (d.id === doc.id ? patchFields(d) : d))
            : prev.dispatchDocuments;
          return {
            ...prev,
            dischargeDocuments: updatedDischarge,
            dispatchDocuments: updatedDispatch,
          };
        });
      } catch (e) {
        alert(e.message || 'Failed to update');
      } finally {
        setActionLoading((p) => ({ ...p, [`scrutiny_${doc.id}`]: false }));
      }
    };

    const makePreview = async () => {
      if (approvedDocs.length === 0) return alert('No approved documents yet');
      try {
        const blob = await mergeDocumentsToPdf(approvedDocs, { title: `Approved Dispatch • ${applicationId}` });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      } catch (e) {
        console.error('Preview merge failed', e);
        alert('Failed to preview bundle');
      }
    };

    const downloadApprovedBundle = async () => {
      if (approvedDocs.length === 0) return alert('No approved documents yet');
      try {
        setActionLoading((p) => ({ ...p, 'download_approved_bundle': true }));
        const blob = await mergeDocumentsToPdf(approvedDocs, { title: `Approved Dispatch • ${applicationId}` });
        downloadBlob(blob, `${applicationId}_Approved_Dispatch.pdf`);
      } catch (e) {
        console.error('Download merge failed', e);
        alert('Failed to download bundle');
      } finally {
        setActionLoading((p) => ({ ...p, 'download_approved_bundle': false }));
      }
    };

    const handleFinalDispatchApproval = async (approved) => {
      try {
        setActionLoading((p) => ({ ...p, 'dispatch_final_approval': true }));
        const res = await setDispatchStartApproval(applicationId, {
          approved,
          remarks: approved ? 'Dispatch approved - hospital can proceed' : 'Dispatch not approved - additional review required',
          doctorId: user.uid,
          doctorName: user.profile?.name || user.email
        });
        if (!res.success) throw new Error(res.error || 'Failed to update dispatch approval');
        // Patch local workflow state locally
        setApplication((prev) => {
          if (!prev) return prev;
          const now = new Date();
          const clearance = {
            status: approved ? 'approved' : 'rejected',
            at: now,
            by: user.uid,
            byName: user.profile?.name || user.email,
            remarks: approved ? 'Dispatch approved - hospital can proceed' : 'Dispatch not approved - additional review required',
          };
          return {
            ...prev,
            workflow: {
              ...prev.workflow,
              dischargeSettlement: {
                ...(prev.workflow?.dischargeSettlement || {}),
                dispatchClearance: clearance,
              },
            },
          };
        });
      } catch (e) {
        alert(e.message || 'Failed to update dispatch approval');
      } finally {
        setActionLoading((p) => ({ ...p, 'dispatch_final_approval': false }));
      }
    };

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Scrutiny Document Review</h3>
          {approvedDocs.length > 0 && (
            <div className="flex items-center gap-3">
              <button type="button" onClick={makePreview} className="text-sm text-teal-700 hover:text-teal-900">Preview Approved Bundle</button>
              <button
                type="button"
                onClick={downloadApprovedBundle}
                disabled={actionLoading['download_approved_bundle']}
                className="text-sm text-teal-700 hover:text-teal-900 disabled:opacity-50"
                title={actionLoading['download_approved_bundle'] ? 'Merging…' : 'Download Approved Bundle'}
              >
                {actionLoading['download_approved_bundle'] ? 'Downloading…' : 'Download Approved Bundle'}
              </button>
            </div>
          )}
        </div>
        
        {docs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No scrutiny documents uploaded by hospital yet.</p>
            <p className="text-xs mt-1">Hospital uploads may appear under Scrutiny (discharge) or Dispatch after FAL acceptance.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-gray-600 mb-4">
              Review scrutiny documents (claim forms, KYC, ID proofs, etc.) uploaded under Discharge/Scrutiny or Dispatch, then approve for dispatch.
            </div>
            
            <div className="space-y-2">
              {docs.map((doc) => (
                <div key={doc.id || doc.url} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <FileText className="h-4 w-4 text-blue-500" />
                      <p className="font-medium text-gray-900 truncate">{doc.title || doc.fileName}</p>
                    </div>
                    <p className="text-xs text-gray-600">Uploaded {formatDate(doc.uploadedAt)} by {doc.uploaderName || 'Hospital'}</p>
                    {doc.description && (
                      <p className="text-xs text-gray-600 mt-1 italic">"{doc.description}"</p>
                    )}
                    <p className="text-xs mt-1">
                      Status: <span className={
                        doc.dispatchStatus === 'approved' ? 'text-green-700 font-medium' : 
                        doc.dispatchStatus === 'rejected' ? 'text-red-700 font-medium' : 'text-yellow-700 font-medium'
                      }>{doc.dispatchStatus || 'pending review'}</span>
                      {doc.dispatchReviewerName && (
                        <> • reviewed by {doc.dispatchReviewerName}</>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => openDocument(doc)}
                      className="px-2 py-1 text-sm text-blue-600 hover:text-blue-800 border border-blue-200 rounded"
                      type="button"
                    >View</button>
                    {!isDispatchApproved && !['approved','rejected'].includes(doc.dispatchStatus) && (
                      <>
                        <button
                          onClick={() => onApprove(doc, true)}
                          disabled={actionLoading[`scrutiny_${doc.id}`]}
                          className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          type="button"
                        >Approve</button>
                        <button
                          onClick={() => onApprove(doc, false)}
                          disabled={actionLoading[`scrutiny_${doc.id}`]}
                          className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                          type="button"
                        >Reject</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {approvedDocs.length > 0 && (
              <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-teal-900">Approved Documents: {approvedDocs.length} of {docs.length}</p>
                    <p className="text-xs text-teal-700">Ready for dispatch approval</p>
                  </div>
                  
                  {isDispatchApproved ? (
                    <div className="flex items-center space-x-2 text-green-700">
                      <CheckCircle className="h-5 w-5" />
                      <span className="text-sm font-medium">Dispatch Process Approved</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleFinalDispatchApproval(true)}
                        disabled={actionLoading['dispatch_final_approval'] || approvedDocs.length === 0}
                        className="px-3 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
                        type="button"
                        title={approvedDocs.length === 0 ? 'Approve at least one document to enable' : ''}
                      >
                        Send to Doctor
                      </button>
                      <button
                        onClick={() => handleFinalDispatchApproval(false)}
                        disabled={actionLoading['dispatch_final_approval']}
                        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        type="button"
                      >
                        Hold Dispatch
                      </button>
                      <button
                        onClick={() => handleFinalDispatchApproval(true)}
                        disabled={actionLoading['dispatch_final_approval'] || approvedDocs.length === 0}
                        className="px-4 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center space-x-1"
                        title={approvedDocs.length === 0 ? 'Approve at least one document first' : ''}
                        type="button"
                      >
                        <span>Approve Dispatch Process</span>
                        {actionLoading['dispatch_final_approval'] && (
                          <Loader className="h-3 w-3 animate-spin" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
                
                {dispatchClearance && (
                  <div className="mt-2 text-xs text-gray-600">
                    {isDispatchApproved ? 'Approved' : 'Rejected'} by {dispatchClearance.byName} on {formatDate(dispatchClearance.at)}
                    {dispatchClearance.remarks && (
                      <div className="mt-1 text-xs text-gray-700 italic">"{dispatchClearance.remarks}"</div>
                    )}
                  </div>
                )}
                {/* Convenience actions */}
                <div className="mt-3 flex items-center gap-3">
                  <button type="button" onClick={makePreview} className="text-xs text-teal-700 hover:text-teal-900">Preview Approved Bundle</button>
                  <button
                    type="button"
                    onClick={downloadApprovedBundle}
                    disabled={actionLoading['download_approved_bundle']}
                    className="text-xs text-teal-700 hover:text-teal-900 disabled:opacity-50"
                  >
                    {actionLoading['download_approved_bundle'] ? 'Downloading…' : 'Download Approved Bundle'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDocuments = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">
          Application Documents
        </h2>
        <div className="text-sm text-gray-600">
          {Object.keys(application.documents || {}).length} document(s) uploaded
        </div>
      </div>

      {application.documents &&
        Object.keys(application.documents).length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(application.documents).map(([key, doc]) => (
            <div
              key={key}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-blue-500" />
                  <h4 className="font-medium text-gray-900 text-sm">
                    {key
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())}
                  </h4>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => openDocument(doc)}
                    className="text-blue-600 hover:text-blue-800 p-1"
                    title="View Document"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      downloadFile(doc.url, doc.fileName || key);
                    }}
                    className="text-green-600 hover:text-green-800 p-1"
                    title="Download Document"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="text-sm text-gray-600 space-y-1">
                <p className="truncate">{doc.fileName}</p>
                <p>Size: {(doc.size / 1024).toFixed(1)} KB</p>
                <p>Uploaded: {formatDate(doc.uploadedAt)}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Documents
          </h3>
          <p className="text-gray-600">
            No documents have been uploaded for this application.
          </p>
        </div>
      )}
    </div>
  );

  // New consolidated application review function with progressive sections
  const renderApplicationReview = () => {
    // Check workflow stages to determine what sections to show
    const workflow = application?.workflow;
    const showMedicalReview = false; // Medical review section removed

    // Debug log to check workflow statuses
    console.log('DEBUG - Policy status:', workflow?.policyVerification?.status);
    console.log('DEBUG - Financial status:', workflow?.financialVerification?.status);

    const showKYPGeneration = workflow?.policyVerification?.status === "approved" &&
      (application?.status === 'financial_approved' || workflow?.financialVerification?.status === "approved");

    console.log('DEBUG - showKYPGeneration:', showKYPGeneration);

  // Discharge review visibility is handled inside renderDischargeReview
  // Show Scrutiny Document Review only after FAL is approved/published
  const falApproved = Boolean(workflow?.dischargeSettlement?.finalApprovalLetterUrl);
    // Reassignment state for smoother KYP revision flow
    const kypReassign = workflow?.medicalVerification?.kypReassignment || null;
    const hasReassignment = !!(kypReassign && (kypReassign.target || (Array.isArray(kypReassign.targets) && kypReassign.targets.length > 0)));

    return (
      <div className="space-y-6">
        {/* Stage 1: Application Overview (Always visible) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-blue-100 p-2 rounded-lg">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Application Overview
            </h2>
            <div className="ml-auto">
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                Stage 1 - Active
              </span>
            </div>
          </div>

          {/* Approval Status Display */}
          {isApprovedForProcess && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
              <div className="flex items-center space-x-3 mb-3">
                <div className="bg-green-100 p-2 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-green-900">
                  Diagnosis Approved
                </h3>
              </div>

              <p className="text-green-700 mb-3">
                This application is now visible to Policy Underwriting and Financial teams.
              </p>

              {/* {application.workflow?.medicalVerification?.approvalForProcessComments && (
                <div className="bg-white rounded-lg p-3 border border-green-200">
                  <p className="text-sm text-gray-600 mb-1">Approval Comments:</p>
                  <p className="text-gray-900">{application.workflow.medicalVerification.approvalForProcessComments}</p>
                </div>
              )} */}

              <p className="text-sm text-green-600 mt-3">
                Approved on: {formatDate(application.workflow?.medicalVerification?.approvedForProcessAt)}
              </p>
            </div>
          )}

          {/* KYP Hospital Challenge Banner for Doctor */}
          {application?.workflow?.medicalVerification?.kypHospitalAcknowledgement?.status === 'challenged' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-yellow-900">Hospital challenged the KYP</span>
                    {application.workflow.medicalVerification.kypHospitalAcknowledgement?.byName && (
                      <span className="text-xs text-yellow-700">by {application.workflow.medicalVerification.kypHospitalAcknowledgement.byName}</span>
                    )}
                    {application.workflow.medicalVerification.kypHospitalAcknowledgement?.at && (
                      <span className="text-xs text-yellow-700">on {formatDate(application.workflow.medicalVerification.kypHospitalAcknowledgement.at)}</span>
                    )}
                  </div>
                  {application.workflow.medicalVerification.kypHospitalAcknowledgement?.comment && (
                    <p className="text-sm text-yellow-800 mb-3">Comment: {application.workflow.medicalVerification.kypHospitalAcknowledgement.comment}</p>
                  )}

                  {/* Existing doctor response (if any) */}
                  {application.workflow.medicalVerification.kypHospitalAcknowledgement?.doctorResponse ? (
                    <div className="flex items-center gap-2 p-2 bg-white border border-yellow-200 rounded">
                      <CheckCircle className="h-4 w-4 text-teal-600" />
                      <span className="text-sm text-gray-800">
                        You responded: {application.workflow.medicalVerification.kypHospitalAcknowledgement.doctorResponse.decision === 'accept_revision' ? 'Accepted and will revise KYP' : 'Did not accept'}
                      </span>
                      {application.workflow.medicalVerification.kypHospitalAcknowledgement.doctorResponse?.comment && (
                        <span className="text-xs text-gray-600 italic">— {application.workflow.medicalVerification.kypHospitalAcknowledgement.doctorResponse.comment}</span>
                      )}
                    </div>
                  ) : hasReassignment ? (
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-yellow-900">Reassigned to:</span>
                        {Array.isArray(kypReassign?.targets) && kypReassign.targets.length > 0 ? (
                          kypReassign.targets.map((tgt) => (
                            <span key={tgt} className="px-2 py-0.5 text-xs rounded-full border bg-white text-gray-800">
                              {tgt === 'policy' ? 'Policy' : tgt === 'finance' ? 'Finance' : 'Doctor'}
                            </span>
                          ))
                        ) : (
                          kypReassign?.target ? (
                            <span className="px-2 py-0.5 text-xs rounded-full border bg-white text-gray-800">
                              {kypReassign.target === 'policy' ? 'Policy' : kypReassign.target === 'finance' ? 'Finance' : kypReassign.target === 'doctor' ? 'Doctor' : 'Policy & Finance'}
                            </span>
                          ) : null
                        )}
                        {kypReassign?.at && (
                          <span className="text-xs text-yellow-700">on {formatDate(kypReassign.at)}</span>
                        )}
                        {kypReassign?.comment && (
                          <span className="text-xs italic text-yellow-700">— {kypReassign.comment}</span>
                        )}
                      </div>

                      {/* Progress chips */}
                      <div className="flex flex-wrap items-center gap-2">
                        {(kypReassign?.targets?.includes('policy') || kypReassign?.target === 'policy' || kypReassign?.target === 'both') && (
                          <span className={`px-2 py-0.5 text-xs rounded-full border ${kypReassign?.policyCompletedAt ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                            Policy {kypReassign?.policyCompletedAt ? `reviewed ${formatDate(kypReassign.policyCompletedAt)}` : 'pending'}
                          </span>
                        )}
                        {(kypReassign?.targets?.includes('finance') || kypReassign?.target === 'finance' || kypReassign?.target === 'both') && (
                          <span className={`px-2 py-0.5 text-xs rounded-full border ${kypReassign?.financeCompletedAt ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                            Finance {kypReassign?.financeCompletedAt ? `reviewed ${formatDate(kypReassign.financeCompletedAt)}` : 'pending'}
                          </span>
                        )}
                        {kypReassign?.status && (
                          <span className={`ml-1 px-2 py-0.5 text-xs rounded-full border ${kypReassign.status === 'completed' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-blue-100 text-blue-800 border-blue-200'}`}>
                            {kypReassign.status.replace('_', ' ')}
                          </span>
                        )}
                      </div>

                      {/* Next step CTA for doctor */}
                      <div className="flex flex-wrap items-center gap-2">
                        {['policy_completed','finance_completed','completed'].includes(kypReassign?.status) ? (
                          <>
                            <span className="text-sm text-yellow-800">Department review completed. Please revise the KYP.</span>
                            <button
                              onClick={handleReviseKYP}
                              className="px-3 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 text-sm"
                            >
                              Revise KYP
                            </button>
                            {/* Inline approve/reject when revised KYP is generated but not yet approved */}
                            {application.workflow?.medicalVerification?.kypGenerated && !application.workflow?.medicalVerification?.kypApproval && (
                              <>
                                <button
                                  onClick={async () => {
                                    setKypApprovalLoading(true);
                                    try {
                                      const res = await setKYPApproval(applicationId, user.uid, {
                                        status: 'approved',
                                        doctorName: user.profile?.name || user.email,
                                        approvedAt: new Date(),
                                      });
                                      if (!res.success) throw new Error(res.error || 'Failed');
                                      await loadApplicationData();
                                    } catch (e) {
                                      alert(e.message || 'Failed to set KYP approval');
                                    } finally {
                                      setKypApprovalLoading(false);
                                    }
                                  }}
                                  disabled={kypApprovalLoading}
                                  className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm disabled:opacity-50"
                                >
                                  Approve Revised KYP
                                </button>
                                <button
                                  onClick={async () => {
                                    const reason = prompt('Please provide reason for rejection:', '') || '';
                                    setKypApprovalLoading(true);
                                    try {
                                      const res = await setKYPApproval(applicationId, user.uid, {
                                        status: 'rejected',
                                        reason,
                                        doctorName: user.profile?.name || user.email,
                                        rejectedAt: new Date(),
                                      });
                                      if (!res.success) throw new Error(res.error || 'Failed');
                                      await loadApplicationData();
                                    } catch (e) {
                                      alert(e.message || 'Failed to set KYP rejection');
                                    } finally {
                                      setKypApprovalLoading(false);
                                    }
                                  }}
                                  disabled={kypApprovalLoading}
                                  className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm disabled:opacity-50"
                                >
                                  Reject Revised KYP
                                </button>
                              </>
                            )}
                          </>
                        ) : (
                          <span className="text-sm text-yellow-800">Awaiting department review…</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        onClick={async () => {
                          setKypChallengeActionLoading(true);
                          try {
                            const res = await respondToKYPChallenge(
                              applicationId,
                              user.uid,
                              {
                                decision: 'accept_revision',
                                comment: '',
                                doctorName: user.profile?.name || user.email,
                              }
                            );
                            if (!res.success) throw new Error(res.error || 'Failed');
                            await loadApplicationData();
                            await handleViewKYP();
                          } catch (e) {
                            alert(e.message || 'Failed to record response');
                          } finally {
                            setKypChallengeActionLoading(false);
                          }
                        }}
                        disabled={
                          disableKypActions ||
                          kypChallengeActionLoading ||
                          ['policy_completed','finance_completed','completed'].includes(
                            application?.workflow?.medicalVerification?.kypReassignment?.status
                          )
                        }
                        className="px-3 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 text-sm disabled:opacity-50"
                      >
                        Accept & Revise KYP
                      </button>
                      <button
                        onClick={async () => {
                          const comment = prompt('Optional comment for not accepting the challenge:', '') || '';
                          setKypChallengeActionLoading(true);
                          try {
                            const res = await respondToKYPChallenge(
                              applicationId,
                              user.uid,
                              {
                                decision: 'not_accept',
                                comment,
                                doctorName: user.profile?.name || user.email,
                              }
                            );
                            if (!res.success) throw new Error(res.error || 'Failed');
                            await loadApplicationData();
                          } catch (e) {
                            alert(e.message || 'Failed to record response');
                          } finally {
                            setKypChallengeActionLoading(false);
                          }
                        }}
                        disabled={
                          disableKypActions ||
                          kypChallengeActionLoading ||
                          ['policy_completed','finance_completed','completed'].includes(
                            application?.workflow?.medicalVerification?.kypReassignment?.status
                          )
                        }
                        className="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-800 text-sm disabled:opacity-50"
                      >
                        Not Accept
                      </button>

                      {['policy_completed','finance_completed','completed'].includes(application?.workflow?.medicalVerification?.kypReassignment?.status)
                        ? null
                        : <>
                      <span className="text-xs text-gray-500">or</span>
                      <button
                        onClick={async () => {
                          const comment = prompt('Add a note for Policy team (optional):', '') || '';
                          setKypReassignLoading(true);
                          try {
                            const res = await reassignKYPForReview(
                              applicationId,
                              user.uid,
                              { target: 'policy', comment, doctorName: user.profile?.name || user.email }
                            );
                            if (!res.success) throw new Error(res.error || 'Failed');
                            await loadApplicationData();
                            alert('Reassigned to Policy team');
                          } catch (e) {
                            alert(e.message || 'Failed to reassign');
                          } finally {
                            setKypReassignLoading(false);
                          }
                        }}
                        disabled={
                          disableKypActions ||
                          kypReassignLoading ||
                          kypChallengeActionLoading
                        }
                        className="px-3 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm disabled:opacity-50"
                      >
                        Reassign to Policy
                      </button>

                      <button
                        onClick={async () => {
                          const comment = prompt('Add a note for Finance team (optional):', '') || '';
                          setKypReassignLoading(true);
                          try {
                            const res = await reassignKYPForReview(
                              applicationId,
                              user.uid,
                              { target: 'finance', comment, doctorName: user.profile?.name || user.email }
                            );
                            if (!res.success) throw new Error(res.error || 'Failed');
                            await loadApplicationData();
                            alert('Reassigned to Finance team');
                          } catch (e) {
                            alert(e.message || 'Failed to reassign');
                          } finally {
                            setKypReassignLoading(false);
                          }
                        }}
                        disabled={
                          disableKypActions ||
                          kypReassignLoading ||
                          kypChallengeActionLoading
                        }
                        className="px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm disabled:opacity-50"
                      >
                        Reassign to Finance
                      </button>
                      <button
                        onClick={async () => {
                          const comment = prompt('Add a note for Doctor (optional):', '') || '';
                          setKypReassignLoading(true);
                          try {
                            const res = await reassignKYPForReview(
                              applicationId,
                              user.uid,
                              { target: 'doctor', comment, doctorName: user.profile?.name || user.email }
                            );
                            if (!res.success) throw new Error(res.error || 'Failed');
                            await loadApplicationData();
                            alert('Reassigned to Doctor');
                          } catch (e) {
                            alert(e.message || 'Failed to reassign to doctor');
                          } finally {
                            setKypReassignLoading(false);
                          }
                        }}
                        disabled={
                          disableKypActions ||
                          kypReassignLoading ||
                          kypChallengeActionLoading
                        }
                        className="px-3 py-2 bg-teal-700 text-white rounded hover:bg-teal-800 text-sm disabled:opacity-50"
                      >
                        Reassign to Doctor
                      </button>
                      <button
                        onClick={async () => {
                          const comment = prompt('Add a note (optional) for Policy & Finance:', '') || '';
                          setKypReassignLoading(true);
                          try {
                            const res = await reassignKYPForReview(
                              applicationId,
                              user.uid,
                              { target: 'both', comment, doctorName: user.profile?.name || user.email }
                            );
                            if (!res.success) throw new Error(res.error || 'Failed');
                            await loadApplicationData();
                            alert('Reassigned to Policy and Finance');
                          } catch (e) {
                            alert(e.message || 'Failed to reassign to both');
                          } finally {
                            setKypReassignLoading(false);
                          }
                        }}
                        disabled={
                          disableKypActions ||
                          kypReassignLoading ||
                          kypChallengeActionLoading
                        }
                        className="px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm disabled:opacity-50"
                      >
                        Reassign to Both
                      </button>
                      </>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Revision status banners */}
          {application?.workflow?.medicalVerification?.kypReassignment?.status && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  {['policy_completed','finance_completed'].includes(application.workflow.medicalVerification.kypReassignment.status) && (
                    <div className="text-sm text-blue-800">
                      Revision requested: {application.workflow.medicalVerification.kypReassignment.status === 'policy_completed' ? 'Policy team has completed their part.' : 'Finance team has completed their part.'} Please review and revise the KYP.
                    </div>
                  )}
                  {application.workflow.medicalVerification.kypReassignment.status === 'completed' && (
                    <div className="text-sm text-blue-800">
                      Department revisions completed. Please review and finalize the KYP.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* FAL Hospital Challenge Banner for Doctor */}
          {isFALChallenged && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-orange-900">Hospital challenged the FAL</span>
                    {falAck?.byName && (
                      <span className="text-xs text-orange-700">by {falAck.byName}</span>
                    )}
                    {falAck?.at && (
                      <span className="text-xs text-orange-700">on {formatDate(falAck.at)}</span>
                    )}
                  </div>
                  {falAck?.comment && (
                    <p className="text-sm text-orange-800 mb-3">Comment: {falAck.comment}</p>
                  )}

                  {/* Existing doctor response (if any) */}
                  {falAck?.doctorResponse ? (
                    <div className="flex items-center gap-2 p-2 bg-white border border-orange-200 rounded">
                      <CheckCircle className="h-4 w-4 text-teal-600" />
                      <span className="text-sm text-gray-800">
                        You responded: {falAck.doctorResponse.decision === 'accept_revision' ? 'Accepted and will revise FAL' : 'Did not accept'}
                        {doctorAcceptedFALRevision && (
                          <span className="ml-2 text-teal-700 font-medium">— FAL section unlocked for editing</span>
                        )}
                      </span>
                      {falAck.doctorResponse?.comment && (
                        <span className="text-xs text-gray-600 italic">— {falAck.doctorResponse.comment}</span>
                      )}
                    </div>
                  ) : (
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-sm font-medium text-orange-900">Do you want to revise the FAL?</span>
                      <button
                        onClick={async () => {
                          setFalActionLoading((p) => ({ ...p, respond: true }));
                          try {
                            const res = await respondToSanctionChallenge(applicationId, falId, { 
                              decision: 'accept_revision', 
                              comment: '', 
                              doctorId: user?.uid, 
                              doctorName: user?.profile?.name 
                            });
                            if (!res.success) throw new Error(res.error || 'Failed');
                            alert('Accepted to revise. FAL section is now unlocked for editing.');
                            await loadApplicationData();
                          } catch (e) {
                            alert(e.message || 'Failed to respond');
                          } finally {
                            setFalActionLoading((p) => ({ ...p, respond: false }));
                          }
                        }}
                        disabled={falActionLoading?.respond}
                        className="text-xs bg-teal-600 text-white px-3 py-1 rounded hover:bg-teal-700 disabled:opacity-50"
                      >
                        {falActionLoading?.respond ? 'Processing...' : 'Yes, Revise FAL'}
                      </button>
                      <button
                        onClick={async () => {
                          const comment = prompt('Add a comment (optional)') || '';
                          setFalActionLoading((p) => ({ ...p, respond2: true }));
                          try {
                            const res = await respondToSanctionChallenge(applicationId, falId, { 
                              decision: 'not_accept', 
                              comment, 
                              doctorId: user?.uid, 
                              doctorName: user?.profile?.name 
                            });
                            if (!res.success) throw new Error(res.error || 'Failed');
                            alert('Hospital challenge not accepted');
                            await loadApplicationData();
                          } catch (e) {
                            alert(e.message || 'Failed to respond');
                          } finally {
                            setFalActionLoading((p) => ({ ...p, respond2: false }));
                          }
                        }}
                        disabled={falActionLoading?.respond2}
                        className="text-xs bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700 disabled:opacity-50"
                      >
                        {falActionLoading?.respond2 ? 'Processing...' : 'No, Don\'t Accept'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Patient Information */}
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-teal-100 p-2 rounded-lg">
                <User className="h-5 w-5 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Patient Information
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Patient Name
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {application.patient?.name}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">
                  Age & Gender
                </label>
                <p className="text-gray-900">
                  {application.patient?.age} years, {application.patient?.gender}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Contact</label>
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-3 w-3 text-gray-400" />
                    <p className="text-sm text-gray-900">
                      {application.patient?.phone}
                    </p>
                  </div>
                  {application.patient?.email && (
                    <div className="flex items-center space-x-2">
                      <Mail className="h-3 w-3 text-gray-400" />
                      <p className="text-sm text-gray-900">
                        {application.patient?.email}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">
                  Aadhar Number
                </label>
                <p className="text-gray-900 font-mono">
                  ****-****-{application.patient?.kyc?.aadharNumber?.slice(-4)}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Address</label>
                <div className="flex items-start space-x-2">
                  <MapPin className="h-3 w-3 text-gray-400 mt-1" />
                  <p className="text-sm text-gray-900">
                    {application.patient?.address}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Medical Information */}
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Stethoscope className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Medical Information
              </h3>
            </div>

            {/* Doctor Information Section */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Doctor Information</h4>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-600 mb-1">
                      Consulting Doctor
                    </label>
                    <p className="text-gray-900 font-semibold text-lg">
                      {application.medical?.doctor?.name}
                    </p>
                    <p className="text-sm text-blue-600 font-medium">
                      {application.medical?.doctor?.designation}
                    </p>
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-600 mb-1">
                      Admission Date
                    </label>
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-blue-500" />
                      <p className="text-gray-900 font-medium">
                        {application.medical?.admissionDate}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-600 mb-1">
                      Discharge Date
                    </label>
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-blue-500" />
                      <p className="text-gray-900 font-medium">
                        {application.medical?.dischargeDate || "TBD"}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-600 mb-1">
                      Estimated Cost
                    </label>
                    <p className="text-xl font-bold text-teal-600">
                      ₹{application.medical?.estimatedCost?.toLocaleString("en-IN")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Diagnosis & Treatment Section */}
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-800">Diagnosis & Treatment</h4>
                {(() => {
                  const reassignedToDoctor = Boolean(
                    application?.workflow?.medicalVerification?.kypReassignment && (
                      application.workflow.medicalVerification.kypReassignment.target === 'doctor' ||
                      (Array.isArray(application.workflow.medicalVerification.kypReassignment.targets) && application.workflow.medicalVerification.kypReassignment.targets.includes('doctor'))
                    )
                  );
                  const disabled = isApprovedForProcess && !reassignedToDoctor;
                  const title = disabled ? 'Disabled after approval for process' : 'Add diagnosis';
                  return (
                    <button
                      onClick={startAddDiagnosis}
                      disabled={disabled}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-200 text-gray-600' : 'bg-teal-600 text-white hover:bg-teal-700 shadow-md hover:shadow-lg'}`}
                      title={title}
                    >
                  <Plus className="h-4 w-4" />
                  <span>Add Diagnosis</span>
                    </button>
                  );
                })()}
              </div>

              {/* Add Diagnosis Form */}
              {isAddingDiagnosis && (
                <div className="bg-teal-50 rounded-lg p-6 border border-teal-200 shadow-sm">
                  <h5 className="text-md font-semibold text-gray-800 mb-4">Add New Diagnosis</h5>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Diagnosis Name *</label>
                      <textarea
                        value={editedDiagnosis}
                        onChange={(e) => setEditedDiagnosis(e.target.value)}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="Enter diagnosis name..."
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Treatment Type</label>
                        <select
                          value={editedTreatmentType}
                          onChange={(e) => setEditedTreatmentType(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        >
                          <option value="">Select Treatment Type</option>
                          {treatmentTypes.map((type) => (
                            <option key={type} value={type}>
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Procedure Name</label>
                        <input
                          type="text"
                          value={editedProcedureName}
                          onChange={(e) => setEditedProcedureName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          placeholder="Enter procedure name..."
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="Enter description..."
                      />
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">ICD Code (Optional)</label>
                      <input
                        type="text"
                        value={icdSearchTerm}
                        onChange={(e) => {
                          setIcdSearchTerm(e.target.value);
                          setShowIcdSuggestions(e.target.value.length > 0);
                          setSelectedIcdIndex(-1);
                        }}
                        onKeyDown={handleIcdKeyDown}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="Type to search ICD codes..."
                      />
                      {showIcdSuggestions && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {getFilteredIcdCodes().map((icd, index) => (
                            <div
                              key={icd.code}
                              onClick={() => handleIcdSelection(icd)}
                              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${index === selectedIcdIndex ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                            >
                              <div className="font-medium text-gray-900">{icd.code}</div>
                              <div className="text-sm text-gray-600">{icd.description}</div>
                            </div>
                          ))}
                          {getFilteredIcdCodes().length === 0 && icdSearchTerm && (
                            <div className="px-3 py-2 text-gray-500 text-sm">No matching ICD codes found</div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-3 pt-2">
                      <button
                        onClick={saveNewDiagnosis}
                        disabled={!editedDiagnosis.trim() || actionLoading.saveNewDiagnosis}
                        className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 font-medium"
                      >
                        {actionLoading.saveNewDiagnosis ? (
                          <>
                            <Loader className="h-4 w-4 animate-spin" />
                            <span>Adding...</span>
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4" />
                            <span>Add Diagnosis</span>
                          </>
                        )}
                      </button>
                      <button 
                        onClick={cancelAddDiagnosis} 
                        className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Diagnosis Grid Display */}
              {application?.medical?.treatments?.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {application.medical.treatments.map((treatment) => (
                    <div
                      key={treatment.id}
                      className="bg-white rounded-lg p-5 border border-gray-200 hover:shadow-lg transition-shadow duration-200"
                    >
                        {isEditingDiagnosis && editingDiagnosisId === treatment.id ? (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Diagnosis Name *
                              </label>
                              <textarea
                                value={editedDiagnosis}
                                onChange={(e) => setEditedDiagnosis(e.target.value)}
                                rows="3"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter diagnosis name..."
                              />
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Treatment Type
                                </label>
                                <select
                                  value={editedTreatmentType}
                                  onChange={(e) => setEditedTreatmentType(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                  <option value="">Select Treatment Type</option>
                                  {treatmentTypes.map((type) => (
                                    <option key={type} value={type}>
                                      {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Procedure Name
                                </label>
                                <input
                                  type="text"
                                  value={editedProcedureName}
                                  onChange={(e) => setEditedProcedureName(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Enter procedure name..."
                                />
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Description
                              </label>
                              <textarea
                                value={editedDescription}
                                onChange={(e) => setEditedDescription(e.target.value)}
                                rows="3"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter description..."
                              />
                            </div>

                            <div className="relative">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                ICD Code (Optional)
                              </label>
                              <input
                                type="text"
                                value={icdSearchTerm}
                                onChange={(e) => {
                                  setIcdSearchTerm(e.target.value);
                                  setShowIcdSuggestions(e.target.value.length > 0);
                                  setSelectedIcdIndex(-1);
                                }}
                                onKeyDown={handleIcdKeyDown}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Type to search ICD codes..."
                              />

                              {showIcdSuggestions && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                  {getFilteredIcdCodes().map((icd, index) => (
                                    <div
                                      key={icd.code}
                                      onClick={() => handleIcdSelection(icd)}
                                      className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${index === selectedIcdIndex ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                                        }`}
                                    >
                                      <div className="font-medium text-gray-900">{icd.code}</div>
                                      <div className="text-sm text-gray-600">{icd.description}</div>
                                    </div>
                                  ))}
                                  {getFilteredIcdCodes().length === 0 && icdSearchTerm && (
                                    <div className="px-3 py-2 text-gray-500 text-sm">
                                      No matching ICD codes found
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="flex items-center space-x-3">
                              <button
                                onClick={saveDiagnosisChanges}
                                disabled={!editedDiagnosis.trim() || actionLoading.saveDiagnosis}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                              >
                                {actionLoading.saveDiagnosis ? (
                                  <>
                                    <Loader className="h-4 w-4 animate-spin" />
                                    <span>Saving...</span>
                                  </>
                                ) : (
                                  <>
                                    <Save className="h-4 w-4" />
                                    <span>Save Changes</span>
                                  </>
                                )}
                              </button>
                              <button
                                onClick={cancelDiagnosisEdit}
                                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                  <h6 className="font-medium text-gray-800">
                                    {treatment.diagnosisName}
                                  </h6>
                                  {(() => {
                                    const reassignedToDoctor = Boolean(
                                      application?.workflow?.medicalVerification?.kypReassignment && (
                                        application.workflow.medicalVerification.kypReassignment.target === 'doctor' ||
                                        (Array.isArray(application.workflow.medicalVerification.kypReassignment.targets) && application.workflow.medicalVerification.kypReassignment.targets.includes('doctor'))
                                      )
                                    );
                                    const disabled = isApprovedForProcess && !reassignedToDoctor;
                                    const title = disabled ? 'Disabled after approval for process' : 'Edit diagnosis';
                                    return (
                                      <button
                                        onClick={() => startDiagnosisEdit(treatment.id)}
                                        disabled={disabled}
                                        className={`flex items-center space-x-1 text-sm ${disabled ? 'opacity-50 cursor-not-allowed text-gray-400' : 'text-blue-600 hover:text-blue-700'}`}
                                        title={title}
                                      >
                                    <Edit className="h-4 w-4" />
                                    <span>Edit</span>
                                      </button>
                                    );
                                  })()}
                                </div>

                                {treatment.icdCode && (
                                  <p className="text-sm text-gray-600 mb-1">
                                    ICD Code: {treatment.icdCode}
                                    {treatment.icdDescription && (
                                      <span> - {treatment.icdDescription}</span>
                                    )}
                                  </p>
                                )}

                                {treatment.reviewedBy && (
                                  <p className="text-xs text-gray-500 mb-2">
                                    Last reviewed by {treatment.reviewerName} on{" "}
                                    {new Date(treatment.reviewedAt?.seconds * 1000 || treatment.reviewedAt).toLocaleDateString()}
                                  </p>
                                )}

                                {treatment.treatmentType && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    Type: {treatment.treatmentType}
                                  </p>
                                )}
                                {treatment.procedureName && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    Procedure: {treatment.procedureName}
                                  </p>
                                )}
                                {treatment.description && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    {treatment.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </>
                        )}

                        {!(isEditingDiagnosis && editingDiagnosisId === treatment.id) && (
                          <div className="border-t border-gray-200 pt-3">
                            <label className="text-sm font-medium text-gray-700 mb-2 block">
                              Supporting Document
                            </label>
                            {!application?.documents?.treatmentDocs?.[treatment.id] ? (
                              <div />
                            ) : (
                              <div className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                                <div className="flex items-center space-x-2">
                                  <FileText className="h-4 w-4 text-green-600" />
                                  <span className="text-sm text-green-800">
                                    {application?.documents?.treatmentDocs[treatment.id]?.name}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <div className="flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 border border-dashed border-gray-300 rounded-lg p-12">
                  <div className="text-center">
                    <Stethoscope className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h5 className="text-lg font-medium text-gray-700 mb-2">No diagnoses added yet</h5>
                    <p className="text-sm text-gray-600 mb-4">Start by adding the first diagnosis for this application.</p>
                    {(() => {
                      const reassignedToDoctor = Boolean(
                        application?.workflow?.medicalVerification?.kypReassignment && (
                          application.workflow.medicalVerification.kypReassignment.target === 'doctor' ||
                          (Array.isArray(application.workflow.medicalVerification.kypReassignment.targets) && application.workflow.medicalVerification.kypReassignment.targets.includes('doctor'))
                        )
                      );
                      const disabled = isApprovedForProcess && !reassignedToDoctor;
                      return (
                        <button
                          onClick={startAddDiagnosis}
                          disabled={disabled}
                          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-200 text-gray-600' : 'bg-teal-600 text-white hover:bg-teal-700 shadow-md hover:shadow-lg'}`}
                        >
                      <Plus className="h-4 w-4" />
                      Add First Diagnosis
                        </button>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Insurance Policy */}
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-green-100 p-2 rounded-lg">
                <Shield className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Insurance Policy
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Policy Number
                </label>
                <p className="text-gray-900 font-mono">
                  {application.policy?.number}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">
                  Insurance Company
                </label>
                <p className="text-gray-900">
                  {application.policy?.insuranceCompany}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">
                  Coverage Amount
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  ₹{application.policy?.coverageAmount?.toLocaleString("en-IN")}
                </p>
              </div>
            </div>
          </div>

          {/* Early Approval for Process Section */}
          {!isApprovedForProcess && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-green-100 p-2 rounded-lg">
                  <FastForward className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-green-900">
                  Approve Application for Process
                </h3>
              </div>

              <p className="text-green-700 mb-4">
                Quick approve this application to make it visible to Policy Underwriting and Financial teams
                while you continue your detailed review.
              </p>

              {/* AI Approval Assistant (duplicate here to match this layout) */}
              <div className="bg-white border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Stethoscope className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-900">AI Approval Assistant</span>
                  </div>
                  <button
                    onClick={runAiApprovalReview}
                    disabled={aiApproveLoading}
                    className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {aiApproveLoading ? 'Analyzing…' : 'Review Diagnoses'}
                  </button>
                </div>
                {showAiApprovePanel && (
                  <div className="space-y-2">
                    {aiApproveError && (
                      <div className="text-red-600 text-sm">{aiApproveError}</div>
                    )}
                    {!aiApproveError && aiApproveLoading && (
                      <div className="text-sm text-gray-600">Running AI review…</div>
                    )}
                    {aiApproveResult && (
                      <div className="space-y-2">
                        <div className={`text-sm ${aiApproveResult.approveRecommended ? 'text-green-700' : 'text-amber-700'}`}>
                          {aiApproveResult.approveRecommended ? 'Ready to approve based on current data.' : 'Not ready yet. See reasons below.'}
                        </div>
                        <ul className="list-disc pl-5 text-sm text-gray-700">
                          {(aiApproveResult.reasons || []).map((r, i) => (
                            <li key={i}>{r}</li>
                          ))}
                        </ul>
                        {!!aiApproveResult.suggestionsById && Object.keys(aiApproveResult.suggestionsById).length > 0 && (
                          <div className="bg-green-50 border border-green-200 rounded p-3">
                            <div className="text-sm font-medium text-green-900 mb-2">ICD suggestions for missing entries</div>
                            <div className="max-h-40 overflow-auto pr-1 text-sm">
                              {Object.entries(aiApproveResult.suggestionsById).map(([id, s]) => (
                                <div key={id} className="flex items-start justify-between py-1 border-b border-green-100 last:border-b-0">
                                  <div>
                                    <div className="font-mono">{s.icdCode} — {s.icdTitle}</div>
                                    <div className="text-xs text-gray-600">{s.reasoning}</div>
                                  </div>
                                  <div className="text-xs text-gray-500">{Math.round((s.confidence||0)*100)}%</div>
                                </div>
                              ))}
                            </div>
                            <div className="flex justify-end mt-2">
                              <button
                                onClick={applyAiApprovalSuggestions}
                                disabled={actionLoading.applyAiApproval}
                                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                              >
                                {actionLoading.applyAiApproval ? 'Applying…' : 'Apply Suggestions'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleApproveForProcess}
                  disabled={actionLoading.approveForProcess}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {actionLoading.approveForProcess ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Approving...</span>
                    </>
                  ) : (
                    <>
                      <ThumbsUp className="h-4 w-4" />
                      <span>Approve for Process</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Stage 2: Medical Review (Always visible for doctors) */}
        {showMedicalReview && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Stethoscope className="h-5 w-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Medical Review
              </h2>
              <div className="ml-auto">
                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                  Stage 2 - Active
                </span>
              </div>
            </div>

            {/* Review Decision */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Review Decision *
                </label>
                <select
                  value={reviewDecision}
                  onChange={(e) => setReviewDecision(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">Select Decision</option>
                  <option value="approved">Approve</option>
                  <option value="rejected">Reject</option>
                  <option value="clarification_needed">Clarification Needed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Review Comments *
                </label>
                <textarea
                  value={reviewComments}
                  onChange={(e) => setReviewComments(e.target.value)}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="Provide detailed comments about your review decision..."
                />
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => alert("Medical review functionality has been removed")}
                  disabled={true}
                  className="bg-gray-400 text-white px-6 py-2 rounded-lg cursor-not-allowed flex items-center space-x-2"
                >
                  {actionLoading.complete ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Complete Review</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stage 3: KYP Generation (Conditional) */}
        {!showKYPGeneration && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-gray-100 p-2 rounded-lg">
                <FileText className="h-5 w-5 text-gray-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                KYP (Know Your Policy) Letter & Disbursement Approval
              </h2>
              <div className="ml-auto">
                <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
                  Stage 3 - Locked
                </span>
              </div>
            </div>

            <div className="text-center py-8">
              <div className="bg-yellow-100 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Shield className="h-8 w-8 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                Policy and Financial Verification Required
              </h3>
              <p className="text-yellow-700 mb-4">
                KYP generation is only available after both Policy Underwriting and Financial teams have completed their verification and approved the application.
              </p>

              <div className="space-y-3 max-w-md mx-auto">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <span className="text-sm font-medium">Policy Verification</span>
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${application?.workflow?.policyVerification?.status === "approved"
                    ? "bg-green-100 text-green-800"
                    : application?.workflow?.policyVerification?.status === "in_progress"
                      ? "bg-yellow-100 text-yellow-800"
                      : application?.workflow?.policyVerification?.status === "rejected"
                        ? "bg-red-100 text-red-800"
                        : "bg-gray-100 text-gray-800"
                    }`}>
                    {application?.workflow?.policyVerification?.status === "approved" ? "✓ Approved" :
                      application?.workflow?.policyVerification?.status === "in_progress" ? "In Progress" :
                        application?.workflow?.policyVerification?.status === "rejected" ? "Rejected" :
                          "Pending"}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <span className="text-sm font-medium">Financial Verification</span>
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${(application?.status === 'financial_approved' || application?.workflow?.financialVerification?.status === "approved")
                    ? "bg-green-100 text-green-800"
                    : application?.workflow?.financialVerification?.status === "in_progress"
                      ? "bg-yellow-100 text-yellow-800"
                      : application?.workflow?.financialVerification?.status === "rejected"
                        ? "bg-red-100 text-red-800"
                        : "bg-gray-100 text-gray-800"
                    }`}>
                    {(application?.status === 'financial_approved' || application?.workflow?.financialVerification?.status === "approved") ? "✓ Approved" :
                      application?.workflow?.financialVerification?.status === "in_progress" ? "In Progress" :
                        application?.workflow?.financialVerification?.status === "rejected" ? "Rejected" :
                          "Pending"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {showKYPGeneration && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-indigo-100 p-2 rounded-lg">
                <FileText className="h-5 w-5 text-indigo-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                KYP (Know Your Policy) Letter & Disbursement Approval
              </h2>
              <div className="ml-auto">
                <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded-full">
                  Stage 3 - Available
                </span>
              </div>
            </div>

            {/* Complete KYP Generation Section with Policy Verification fields only (mirroring PolicyVerificationForm) */}
            <div className="space-y-6">
              {(() => {
                const pv = application?.workflow?.policyVerification || {};
                const pd = pv.policyDetails || {};
                const room = pv.roomEligibility || {};
                const copay = pv.coPayStructure || {};
                const deds = pv.deductiblesAndSublimits || {};
                const intim = pv.intimationDetails || {};
                const ch = pv.claimHistory || {};
                const pa = pv.policyAnalysis || {};
                const cb = pv.coverageBreakdown || {};
                const clar = pv.clarificationDetails || {};
                const addr = pv.dispatchAddress || {};
                const yesNo = (v) => (v ? 'Yes' : 'No');
                const showMoney = (v) => (v || v === 0 ? formatAmount(Number(v) || 0) : 'N/A');

                return (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h4 className="font-medium text-green-900 mb-4 flex items-center space-x-2">
                      <Shield className="h-5 w-5" />
                      <span>Policy Verification Details</span>
                    </h4>

                    {/* Policy Details */}
                    <div className="space-y-4">
                      <h5 className="text-sm font-semibold text-green-800">Policy Details</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div><label className="text-xs text-green-700">Policy Type</label><p className="text-gray-900 font-medium">{pd.policyType || 'N/A'}</p></div>
                        <div><label className="text-xs text-green-700">Policy Number</label><p className="text-gray-900 font-mono">{pd.policyNumber || 'N/A'}</p></div>
                        <div><label className="text-xs text-green-700">Inception Date</label><p className="text-gray-900">{pd.inceptionDate || 'N/A'}</p></div>
                        <div><label className="text-xs text-green-700">Expiry Date</label><p className="text-gray-900">{pd.expiryDate || 'N/A'}</p></div>
                        <div><label className="text-xs text-green-700">Employee/Insured Active Status</label><p className="text-gray-900">{pd.employeeActiveStatus === undefined ? 'N/A' : yesNo(pd.employeeActiveStatus)}</p></div>
                        <div><label className="text-xs text-green-700">Base Sum Insured</label><p className="text-gray-900 font-semibold">{showMoney(pd.baseSumInsured)}</p></div>
                        <div><label className="text-xs text-green-700">Top-up Sum Insured</label><p className="text-gray-900">{showMoney(pd.topUpSumInsured)}</p></div>
                        <div><label className="text-xs text-green-700">Cumulative Bonus</label><p className="text-gray-900">{showMoney(pd.cumulativeBonus)}</p></div>
                        <div><label className="text-xs text-green-700">Refill/Recharge Amount</label><p className="text-gray-900">{showMoney(pd.refillRechargeAmount)}</p></div>
                        <div><label className="text-xs text-green-700">Sum Insured Utilized (Current Year)</label><p className="text-gray-900">{showMoney(pd.sumInsuredUtilizedCurrentYear)}</p></div>
                        <div><label className="text-xs text-green-700">Effective Total Sum Insured</label><p className="text-gray-900 font-semibold">{showMoney(pd.effectiveTotalSumInsured)}</p></div>
                        <div><label className="text-xs text-green-700">Balance Sum Insured</label><p className="text-gray-900 font-semibold text-teal-700">{showMoney(pd.balanceSumInsured)}</p></div>
                      </div>
                    </div>

                    {/* Room Eligibility */}
                    <div className="mt-6 space-y-3">
                      <h5 className="text-sm font-semibold text-green-800">Room Eligibility</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div><label className="text-xs text-green-700">Room Type</label><p className="text-gray-900">{room.roomType || 'N/A'}</p></div>
                        <div><label className="text-xs text-green-700">Room Amount/Day</label><p className="text-gray-900">{showMoney(room.roomAmountPerDay)}</p></div>
                        <div><label className="text-xs text-green-700">ICU Amount/Day</label><p className="text-gray-900">{showMoney(room.icuAmountPerDay)}</p></div>
                        <div><label className="text-xs text-green-700">Room Capping Applicable</label><p className="text-gray-900">{room.roomCappingApplicable === undefined ? 'N/A' : yesNo(room.roomCappingApplicable)}</p></div>
                        <div><label className="text-xs text-green-700">Room Capping Type</label><p className="text-gray-900">{room.roomCappingType || 'N/A'}</p></div>
                        <div><label className="text-xs text-green-700">Room Capping Limit</label><p className="text-gray-900">{showMoney(room.roomCappingLimit)}</p></div>
                        <div><label className="text-xs text-green-700">Room Capping %</label><p className="text-gray-900">{room.roomCappingPercentage || 'N/A'}</p></div>
                        <div><label className="text-xs text-green-700">ICU Capping Applicable</label><p className="text-gray-900">{room.icuCappingApplicable === undefined ? 'N/A' : yesNo(room.icuCappingApplicable)}</p></div>
                        <div><label className="text-xs text-green-700">ICU Capping Type</label><p className="text-gray-900">{room.icuCappingType || 'N/A'}</p></div>
                        <div><label className="text-xs text-green-700">ICU Capping Limit</label><p className="text-gray-900">{showMoney(room.icuCappingLimit)}</p></div>
                        <div><label className="text-xs text-green-700">ICU Capping %</label><p className="text-gray-900">{room.icuCappingPercentage || 'N/A'}</p></div>
                        <div><label className="text-xs text-green-700">ICU Type</label><p className="text-gray-900">{room.icuType || 'N/A'}</p></div>
                      </div>
                    </div>

                    {/* Co-Pay Structure */}
                    <div className="mt-6 space-y-3">
                      <h5 className="text-sm font-semibold text-green-800">Co-Pay Structure</h5>
                      {copay ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div><label className="text-xs text-green-700">Co-pay Mandatory</label><p className="text-gray-900">{copay.coPayMandatory === undefined ? 'N/A' : yesNo(copay.coPayMandatory)}</p></div>
                          <div><label className="text-xs text-green-700">General Co-pay Applicable</label><p className="text-gray-900">{copay.generalCoPayApplicable === undefined ? 'N/A' : yesNo(copay.generalCoPayApplicable)}</p></div>
                          <div><label className="text-xs text-green-700">General Co-pay Type</label><p className="text-gray-900">{copay.generalCoPayType || 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">General Co-pay Value</label><p className="text-gray-900">{copay.generalCoPayValue ?? copay.generalCoPay ?? 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">Parental Co-pay Applicable</label><p className="text-gray-900">{copay.parentalCoPayApplicable === undefined ? 'N/A' : yesNo(copay.parentalCoPayApplicable)}</p></div>
                          <div><label className="text-xs text-green-700">Parental Co-pay Type</label><p className="text-gray-900">{copay.parentalCoPayType || 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">Parental Co-pay Value</label><p className="text-gray-900">{copay.parentalCoPayValue ?? 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">Parental Age Threshold</label><p className="text-gray-900">{copay.parentalCoPayAgeThreshold || 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">Location-based Co-pay Applicable</label><p className="text-gray-900">{copay.locationBasedCoPayApplicable === undefined ? 'N/A' : yesNo(copay.locationBasedCoPayApplicable)}</p></div>
                          <div><label className="text-xs text-green-700">Metro Co-pay Type</label><p className="text-gray-900">{copay.metroCoPayType || 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">Metro Co-pay Value</label><p className="text-gray-900">{copay.metroCoPayValue ?? 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">Non-Metro Co-pay Type</label><p className="text-gray-900">{copay.nonMetroCoPayType || 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">Non-Metro Co-pay Value</label><p className="text-gray-900">{copay.nonMetroCoPayValue ?? 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">Non-Network Co-pay Applicable</label><p className="text-gray-900">{copay.nonNetworkCoPayApplicable === undefined ? 'N/A' : yesNo(copay.nonNetworkCoPayApplicable)}</p></div>
                          <div><label className="text-xs text-green-700">Non-Network Co-pay Type</label><p className="text-gray-900">{copay.nonNetworkCoPayType || 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">Non-Network Co-pay Value</label><p className="text-gray-900">{copay.nonNetworkCoPayValue ?? 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">Voluntary Co-pay Applicable</label><p className="text-gray-900">{copay.voluntaryCoPayApplicable === undefined ? 'N/A' : yesNo(copay.voluntaryCoPayApplicable)}</p></div>
                          <div><label className="text-xs text-green-700">Voluntary Co-pay Type</label><p className="text-gray-900">{copay.voluntaryCoPayType || 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">Voluntary Co-pay Value</label><p className="text-gray-900">{copay.voluntaryCoPayValue ?? 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">Mandatory Co-pay Applicable</label><p className="text-gray-900">{copay.mandatoryCoPayApplicable === undefined ? 'N/A' : yesNo(copay.mandatoryCoPayApplicable)}</p></div>
                          <div><label className="text-xs text-green-700">Mandatory Co-pay Type</label><p className="text-gray-900">{copay.mandatoryCoPayType || 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">Mandatory Co-pay Value</label><p className="text-gray-900">{copay.mandatoryCoPayValue ?? 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">Non-intimation Co-pay Applicable</label><p className="text-gray-900">{copay.nonIntimationCoPayApplicable === undefined ? 'N/A' : yesNo(copay.nonIntimationCoPayApplicable)}</p></div>
                          <div><label className="text-xs text-green-700">Non-intimation Co-pay Type</label><p className="text-gray-900">{copay.nonIntimationCoPayType || 'N/A'}</p></div>
                          <div><label className="text-xs text-green-700">Non-intimation Co-pay Value</label><p className="text-gray-900">{copay.nonIntimationCoPayValue ?? 'N/A'}</p></div>
                        </div>
                      ) : (
                        <div className="text-gray-500 text-sm">No co-pay information</div>
                      )}
                    </div>

                    {/* Deductibles & Sublimits */}
                    <div className="mt-6 space-y-3">
                      <h5 className="text-sm font-semibold text-green-800">Deductibles & Sublimits</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div><label className="text-xs text-green-700">Deductibles Mandatory</label><p className="text-gray-900">{deds.deductiblesMandatory === undefined ? 'N/A' : yesNo(deds.deductiblesMandatory)}</p></div>
                        <div><label className="text-xs text-green-700">Compulsory Deductible</label><p className="text-gray-900">{showMoney(deds.compulsoryDeductible)}</p></div>
                        <div><label className="text-xs text-green-700">Pre/Post Natal Limit</label><p className="text-gray-900">{showMoney(deds.prePostNatalLimit)}</p></div>
                      </div>
                      <div>
                        <label className="text-xs text-green-700 block mb-1">Ailment/Procedure Sublimits</label>
                        {Array.isArray(deds.ailmentProcedureSublimits) && deds.ailmentProcedureSublimits.length > 0 ? (
                          <div className="space-y-1">
                            {deds.ailmentProcedureSublimits.map((s, i) => (
                              <div key={i} className="flex justify-between text-sm bg-white/50 rounded px-2 py-1 border">
                                <span className="text-gray-700">{s.ailmentProcedure || s.name || '—'}</span>
                                <span className="text-gray-900 font-medium">{showMoney(s.sublimitAmount)}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No sublimits</p>
                        )}
                      </div>
                    </div>

                    {/* Intimation Details */}
                    <div className="mt-6 space-y-3">
                      <h5 className="text-sm font-semibold text-green-800">Intimation Details</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div><label className="text-xs text-green-700">Intimation Done</label><p className="text-gray-900">{intim.intimationDone === undefined ? 'N/A' : yesNo(intim.intimationDone)}</p></div>
                        <div><label className="text-xs text-green-700">Intimation ID</label><p className="text-gray-900">{intim.intimationId || 'N/A'}</p></div>
                        <div><label className="text-xs text-green-700">Intimation Source</label><p className="text-gray-900">{intim.intimationSource || 'N/A'}</p></div>
                        <div><label className="text-xs text-green-700">Intimation Mandatory</label><p className="text-gray-900">{intim.intimationMandatory === undefined ? 'N/A' : yesNo(intim.intimationMandatory)}</p></div>
                      </div>
                    </div>

                    {/* Claim History */}
                    <div className="mt-6 space-y-3">
                      <h5 className="text-sm font-semibold text-green-800">Claim History</h5>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div><label className="text-xs text-green-700">Total Claimed (Current Year)</label><p className="text-gray-900 font-semibold">{showMoney(ch.totalClaimedCurrentYear)}</p></div>
                      </div>
                      {Array.isArray(ch.previousClaimsCurrentYear) && ch.previousClaimsCurrentYear.length > 0 && (
                        <div>
                          <label className="text-xs text-green-700 block mb-1">Previous Claims (Current Year)</label>
                          <div className="space-y-1">
                            {ch.previousClaimsCurrentYear.map((c, i) => (
                              <div key={i} className="flex justify-between text-sm bg-white/50 rounded px-2 py-1 border">
                                <span className="text-gray-700">{c.diagnosis || c.description || '—'}{c.claimDate ? ` (${c.claimDate})` : ''}</span>
                                <span className="text-gray-900 font-medium">{showMoney(c.claimAmount)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Policy Analysis (booleans) */}
                    <div className="mt-6 space-y-3">
                      <h5 className="text-sm font-semibold text-green-800">Policy Analysis</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[
                          { label: 'Policy Valid', value: pa.policyValid },
                          { label: 'Coverage Active', value: pa.coverageActive },
                          { label: 'Beneficiary Eligible', value: pa.beneficiaryEligible },
                          { label: 'Treatment Covered', value: pa.treatmentCovered },
                          { label: 'Pre-Auth Required', value: pa.preAuthRequired },
                          { label: 'Pre-Auth Obtained', value: pa.preAuthObtained },
                          { label: 'Claim Limit Available', value: pa.claimLimitAvailable },
                          { label: 'Waiting Period Completed', value: pa.waitingPeriodCompleted },
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <label className="text-xs text-green-700 flex-1 leading-5">{item.label}</label>
                            <p className="text-gray-900 font-medium">{item.value === undefined ? 'N/A' : yesNo(item.value)}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Eligibility & Coverage Breakdown */}
                    <div className="mt-6 space-y-3">
                      <h5 className="text-sm font-semibold text-green-800">Eligibility & Coverage</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div><label className="text-xs text-green-700">Eligibility Confirmed</label><p className="text-gray-900">{pv.eligibilityConfirmed === undefined ? 'N/A' : yesNo(pv.eligibilityConfirmed)}</p></div>
                        <div><label className="text-xs text-green-700">Approved Coverage Amount</label><p className="text-gray-900 font-semibold">{showMoney(pv.approvedCoverageAmount)}</p></div>
                      </div>
                      {cb && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div><label className="text-xs text-green-700">Base Coverage</label><p className="text-gray-900">{showMoney(cb.baseCoverage)}</p></div>
                          <div><label className="text-xs text-green-700">Cumulative Bonus</label><p className="text-gray-900">{showMoney(cb.cumulativeBonus)}</p></div>
                          <div><label className="text-xs text-green-700">Top-up Coverage</label><p className="text-gray-900">{showMoney(cb.topUpCoverage)}</p></div>
                          <div><label className="text-xs text-green-700">Total Available</label><p className="text-gray-900 font-medium">{showMoney(cb.totalAvailable)}</p></div>
                          <div><label className="text-xs text-green-700">Utilized</label><p className="text-gray-900">{showMoney(cb.utilized)}</p></div>
                          <div><label className="text-xs text-green-700">Balance</label><p className="text-gray-900 font-medium">{showMoney(cb.balance)}</p></div>
                        </div>
                      )}
                      {Array.isArray(pv.exclusionsApplied) && pv.exclusionsApplied.length > 0 && (
                        <div>
                          <label className="text-xs text-green-700 block mb-1">Exclusions Applied</label>
                          <div className="space-y-1">
                            {pv.exclusionsApplied.map((e, i) => (
                              <div key={i} className="flex justify-between text-sm bg-white/50 rounded px-2 py-1 border">
                                <span className="text-gray-700">{e.reason || e.name || '—'}</span>
                                <span className="text-red-700 font-medium">{showMoney(e.amount)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Clarification */}
                    <div className="mt-6 space-y-3">
                      <h5 className="text-sm font-semibold text-green-800">Clarification</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div><label className="text-xs text-green-700">Clarification Required</label><p className="text-gray-900">{pv.clarificationRequired === undefined ? 'N/A' : yesNo(pv.clarificationRequired)}</p></div>
                      </div>
                      {pv.clarificationRequired && (
                        <div className="space-y-2 text-sm">
                          <div><span className="text-green-700 font-medium">Subject: </span><span className="text-gray-900">{clar.subject || 'N/A'}</span></div>
                          <div><span className="text-green-700 font-medium">Message: </span><span className="text-gray-900">{clar.message || 'N/A'}</span></div>
                          {Array.isArray(clar.requestedDocuments) && clar.requestedDocuments.length > 0 && (
                            <div>
                              <span className="text-green-700 font-medium">Requested Documents:</span>
                              <ul className="list-disc ml-5 text-gray-900">
                                {clar.requestedDocuments.map((d, i) => (<li key={i}>{d}</li>))}
                              </ul>
                            </div>
                          )}
                          <div><span className="text-green-700 font-medium">Priority: </span><span className="text-gray-900">{clar.priority || 'N/A'}</span></div>
                          {Array.isArray(clar.qnaQuestions) && clar.qnaQuestions.length > 0 && (
                            <div>
                              <span className="text-green-700 font-medium">Q&A Questions:</span>
                              <ul className="list-disc ml-5 text-gray-900">
                                {clar.qnaQuestions.map((q, i) => (<li key={i}>{q.question || JSON.stringify(q)}</li>))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Dispatch Address */}
                    <div className="mt-6 space-y-2">
                      <h5 className="text-sm font-semibold text-green-800">Dispatch Address</h5>
                      {addr && (addr.recipientName || addr.contactNumber || addr.addressLine1 || addr.city || addr.state || addr.pincode) ? (
                        <div className="text-sm p-3 bg-green-25 rounded border">
                          <p className="font-medium text-gray-900">{addr.recipientName || 'N/A'}</p>
                          <p className="text-gray-700">{addr.contactNumber || 'N/A'}</p>
                          <p className="text-gray-700">{addr.addressLine1 || 'N/A'}</p>
                          {addr.addressLine2 && <p className="text-gray-700">{addr.addressLine2}</p>}
                          <p className="text-gray-700">{addr.city || 'N/A'}, {addr.state || 'N/A'} - {addr.pincode || 'N/A'}</p>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No dispatch address</p>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Medical Information Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h4 className="font-medium text-blue-900 mb-4 flex items-center space-x-2">
                  <Stethoscope className="h-5 w-5" />
                  <span>Medical Treatment Details</span>
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-blue-700">Primary Diagnosis</label>
                      <p className="text-gray-900 font-semibold">
                        {application.medical?.treatments?.[0]?.diagnosisName || application.medical?.diagnosis || 'N/A'}
                      </p>
                      {application.medical?.treatments?.[0]?.icdCode && (
                        <p className="text-sm text-gray-600">
                          ICD Code: {application.medical.treatments[0].icdCode}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-medium text-blue-700">Treatment Type</label>
                      <p className="text-gray-900">
                        {application.medical?.treatments?.[0]?.treatmentType || application.medical?.treatmentType || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-blue-700">Procedure</label>
                      <p className="text-gray-900">
                        {application.medical?.treatments?.[0]?.procedureName || application.medical?.procedureName || 'As per diagnosis'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-blue-700">Consulting Doctor</label>
                      <p className="text-gray-900">{application.medical?.doctor?.name}</p>
                      <p className="text-sm text-gray-600">{application.medical?.doctor?.designation}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-blue-700">Admission Date</label>
                      <p className="text-gray-900">{application.medical?.admissionDate}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-blue-700">Expected Discharge</label>
                      <p className="text-gray-900">{application.medical?.dischargeDate || 'TBD'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-blue-700">Estimated Cost</label>
                      <p className="text-lg font-semibold text-blue-600">
                        {formatAmount(application.medical?.estimatedCost)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-blue-700">Hospital</label>
                      <p className="text-gray-900">{application.hospitalName}</p>
                      <p className="text-sm text-gray-600">{application.hospital?.city}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Verification Checklist */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">
                  Medical Verification Checklist
                </h4>
                <div className="space-y-3">
                  {[
                    {
                      key: "medicalHistoryVerified",
                      label: "Medical History Verified",
                    },
                    { key: "documentsAuthentic", label: "Documents Authentic" },
                    {
                      key: "treatmentAppropriate",
                      label: "Treatment Plan Appropriate",
                    },
                  ].map((item) => (
                    <label key={item.key} className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={kypData[item.key]}
                        onChange={(e) =>
                          setKypData((prev) => ({
                            ...prev,
                            [item.key]: e.target.checked,
                          }))
                        }
                        disabled={isKypReadOnly}
                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <span className="text-sm text-gray-700">{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pre-existing Conditions
                  </label>
                  <textarea
                    value={kypData.preExistingConditions}
                    onChange={(e) =>
                      setKypData((prev) => ({
                        ...prev,
                        preExistingConditions: e.target.value,
                      }))
                    }
                    rows="3"
                    disabled={isKypReadOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="List any pre-existing conditions..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Medical Recommendation
                  </label>
                  <textarea
                    value={kypData.recommendation}
                    onChange={(e) =>
                      setKypData((prev) => ({
                        ...prev,
                        recommendation: e.target.value,
                      }))
                    }
                    rows="3"
                    disabled={isKypReadOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Medical recommendation for treatment..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Clinical Notes
                </label>
                <textarea
                  value={kypData.clinicalNotes}
                  onChange={(e) =>
                    setKypData((prev) => ({
                      ...prev,
                      clinicalNotes: e.target.value,
                    }))
                  }
                  rows="4"
                  disabled={isKypReadOnly}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="Additional clinical notes and observations..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Doctor Registration No.
                  </label>
                  <input
                    type="text"
                    value={kypData.registrationNo}
                    onChange={(e) =>
                      setKypData((prev) => ({
                        ...prev,
                        registrationNo: e.target.value,
                      }))
                    }
                    disabled={isKypReadOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Medical registration number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Validity Period
                  </label>
                  <select
                    value={kypData.validityPeriod}
                    onChange={(e) =>
                      setKypData((prev) => ({
                        ...prev,
                        validityPeriod: e.target.value,
                      }))
                    }
                    disabled={isKypReadOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="15 days">15 days</option>
                    <option value="30 days">30 days</option>
                    <option value="60 days">60 days</option>
                    <option value="90 days">90 days</option>
                    <option value="180 days">180 days</option>
                  </select>
                </div>
              </div>

              {/* Remarks Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Internal Remarks
                    <span className="text-xs text-gray-500 ml-1">(For internal reference only)</span>
                  </label>
                  <textarea
                    value={kypData.internalRemarks}
                    onChange={(e) =>
                      setKypData((prev) => ({
                        ...prev,
                        internalRemarks: e.target.value,
                      }))
                    }
                    rows="4"
                    disabled={isKypReadOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Internal notes and remarks for reference (not shown in KYP letter)..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Letter Remarks
                    <span className="text-xs text-gray-500 ml-1">(Will appear in KYP letter)</span>
                  </label>
                  <textarea
                    value={kypData.letterRemarks}
                    onChange={(e) =>
                      setKypData((prev) => ({
                        ...prev,
                        letterRemarks: e.target.value,
                      }))
                    }
                    rows="4"
                    disabled={isKypReadOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Remarks to be included in the KYP letter for communication purposes..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                {application.workflow?.medicalVerification?.kypGenerated ? (
                  <button
                    onClick={handleViewKYP}
                    className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg flex items-center space-x-2"
                  >
                    <Eye className="h-4 w-4" />
                    <span>View Generated KYP</span>
                  </button>
                ) : (
                  <button
                    onClick={handleGenerateKYP}
                    disabled={disableKypActions || actionLoading.kyp || allDisputesStatus.hasActiveDisputes || !isMedicalChecklistComplete}
                    className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${disableKypActions ? 'bg-gray-200 text-gray-600 cursor-not-allowed' : 'bg-teal-600 text-white hover:bg-teal-700'} disabled:opacity-50 disabled:cursor-not-allowed`}
                    title={
                      allDisputesStatus.hasActiveDisputes
                        ? `Cannot generate KYP - Active queries from: ${[...new Set(allDisputesStatus.activeDisputes.map(d => d.raisedBy?.department || 'unknown'))].join(', ')}`
                        : disableKypActions
                          ? 'Disabled: KYP approved after reassignment'
                          : !isMedicalChecklistComplete
                          ? "Complete the Medical Verification Checklist to enable KYP generation"
                          : ""
                    }
                  >
                    {actionLoading.kyp ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        <span>Generating PDF...</span>
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4" />
                        <span>Generate KYP Letter (PDF)</span>
                      </>
                    )}
                  </button>
                )}
                {/* Doctor KYP Approve/Reject */}
                {/* KYP Approval Status Display */}
                {application.workflow?.medicalVerification?.kypApproval && (
                  <div className="flex items-center space-x-2 mb-3">
                    {application.workflow.medicalVerification.kypApproval.status === 'approved' ? (
                      <div className="flex items-center space-x-2 px-3 py-2 bg-green-100 text-green-800 rounded-lg border border-green-200">
                        <CheckCircle className="h-4 w-4" />
                        <span className="text-sm font-medium">KYP Approved</span>
                        <span className="text-xs">
                          by {application.workflow.medicalVerification.kypApproval.doctorName}
                          {application.workflow.medicalVerification.kypApproval.approvedAt
                            ? ` on ${formatDate(application.workflow.medicalVerification.kypApproval.approvedAt)}`
                            : application.workflow.medicalVerification.kypApproval.at
                              ? ` on ${formatDate(application.workflow.medicalVerification.kypApproval.at)}`
                              : ''}
                        </span>
                      </div>
                    ) : application.workflow.medicalVerification.kypApproval.status === 'rejected' ? (
                      <div className="flex items-center space-x-2 px-3 py-2 bg-red-100 text-red-800 rounded-lg border border-red-200">
                        <XCircle className="h-4 w-4" />
                        <span className="text-sm font-medium">KYP Rejected</span>
                        <span className="text-xs">
                          by {application.workflow.medicalVerification.kypApproval.doctorName}
                          {application.workflow.medicalVerification.kypApproval.rejectedAt
                            ? ` on ${formatDate(application.workflow.medicalVerification.kypApproval.rejectedAt)}`
                            : application.workflow.medicalVerification.kypApproval.at
                              ? ` on ${formatDate(application.workflow.medicalVerification.kypApproval.at)}`
                              : ''}
                        </span>
                        {application.workflow.medicalVerification.kypApproval.reason && (
                          <span className="text-xs italic">
                            - {application.workflow.medicalVerification.kypApproval.reason}
                          </span>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
                
                {/* Doctor KYP Approve/Reject - Only show if KYP is generated and not yet approved/rejected */}
                {application.workflow?.medicalVerification?.kypGenerated && 
                 !application.workflow?.medicalVerification?.kypApproval && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={async () => {
                        setKypApprovalLoading(true);
                        try {
                          const res = await setKYPApproval(applicationId, user.uid, { 
                            status: 'approved', 
                            doctorName: user.profile?.name || user.email,
                            approvedAt: new Date()
                          });
                          if (!res.success) throw new Error(res.error || 'Failed');
                          await loadApplicationData();
                        } catch (e) {
                          alert(e.message || 'Failed to set KYP approval');
                        } finally {
                          setKypApprovalLoading(false);
                        }
                      }}
                      disabled={disableKypActions || kypApprovalLoading}
                      className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm disabled:opacity-50"
                    >
                      Approve KYP
                    </button>
                    <button
                      onClick={async () => {
                        const reason = prompt('Please provide reason for rejection:', '') || '';
                        setKypApprovalLoading(true);
                        try {
                          const res = await setKYPApproval(applicationId, user.uid, { 
                            status: 'rejected', 
                            reason, 
                            doctorName: user.profile?.name || user.email,
                            rejectedAt: new Date()
                          });
                          if (!res.success) throw new Error(res.error || 'Failed');
                          await loadApplicationData();
                        } catch (e) {
                          alert(e.message || 'Failed to set KYP rejection');
                        } finally {
                          setKypApprovalLoading(false);
                        }
                      }}
                      disabled={disableKypActions || kypApprovalLoading}
                      className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm disabled:opacity-50"
                    >
                      Reject KYP
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stage 4: Discharge Review (split view with document panel) */}
  {renderDischargeReview()}
  {falApproved && renderDispatchScrutiny()}

        {/* Risky Application (always visible) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <button
            onClick={() => toggleSection('risk')}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
         >
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${riskInfo.flagged ? 'bg-red-100' : 'bg-gray-100'}`}>
                <AlertTriangle className={`h-5 w-5 ${riskInfo.flagged ? 'text-red-600' : 'text-gray-600'}`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Risky Application</h3>
                <p className="text-sm text-gray-600">
                  {riskInfo.flagged ? `Flagged${riskInfo.flaggedByName ? ` by ${riskInfo.flaggedByName}` : ''}` : 'Not flagged'}
                </p>
              </div>
            </div>
            {collapsedSections.risk ? (
              <ChevronRight className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {!collapsedSections.risk && (
            <div className="px-6 pb-6 border-t border-gray-100">
              <div className="mt-4 space-y-3">
                {riskInfo.flagged && (
                  <div className="p-3 rounded border border-red-200 bg-red-50">
                    <p className="text-sm text-red-800 font-medium">Flagged as risky</p>
                    {riskInfo.comment && (
                      <p className="text-sm text-red-700 mt-1 whitespace-pre-wrap">{riskInfo.comment}</p>
                    )}
                    {riskInfo.flaggedByName && (
                      <p className="text-xs text-red-600 mt-1">By {riskInfo.flaggedByName}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Add/Update Risk Comment</label>
                  <textarea
                    value={riskComment}
                    onChange={(e) => setRiskComment(e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    placeholder="Why is this application risky? Add context for other doctors."
                  />
                </div>

                <div className="flex items-center justify-end space-x-2">
                  {riskInfo.flagged && (
                    <button
                      onClick={() => handleSetRiskFlag(false)}
                      disabled={riskSaving}
                      className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      {riskSaving ? 'Updating...' : 'Unflag'}
                    </button>
                  )}
                  <button
                    onClick={() => handleSetRiskFlag(true)}
                    disabled={riskSaving || !riskComment.trim()}
                    className="px-4 py-2 text-sm text-white bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {riskSaving ? 'Saving...' : (riskInfo.flagged ? 'Update Flag' : 'Flag as Risky')}
                  </button>
                </div>

                {Array.isArray(riskInfo.notes) && riskInfo.notes.length > 0 && (
                  <div className="pt-2 border-t border-gray-100">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Risk Notes History</h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {riskInfo.notes.slice().reverse().map((n) => (
                        <div key={n.id} className="p-2 bg-gray-50 border border-gray-200 rounded">
                          <div className="flex justify-between items-start">
                            <span className="text-sm font-medium text-gray-800">{n.action === 'flag' ? 'Flagged' : 'Unflagged'}</span>
                            <span className="text-xs text-gray-500">{n.at?.toDate?.()?.toLocaleString?.() || (n.at ? new Date(n.at).toLocaleString() : '')}</span>
                          </div>
                          {n.comment && <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{n.comment}</p>}
                          {n.byName && <p className="text-xs text-gray-500 mt-1">By {n.byName}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Application Status */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Application Status
          </h3>
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <span className="text-gray-600">Priority:</span>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${application.priority === "urgent"
                  ? "bg-red-100 text-red-800"
                  : application.priority === "high"
                    ? "bg-orange-100 text-orange-800"
                    : "bg-gray-100 text-gray-800"
                  }`}
              >
                {application.priority?.toUpperCase()}
              </span>
            </div>

            {application.emergency && (
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <span className="text-red-600 font-medium">Emergency Case</span>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <span className="text-gray-600">Submitted:</span>
              <span className="font-medium">
                {formatDate(application.submittedAt)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // const renderMedicalReview = () => (
  <div className="space-y-6">
    {/* Review Decision */}
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Medical Review Decision
      </h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Review Decision *
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                value: "approved",
                label: "Approve",
                color: "border-green-500 bg-green-50",
                icon: CheckCircle,
              },
              {
                value: "rejected",
                label: "Reject",
                color: "border-red-500 bg-red-50",
                icon: XCircle,
              },
              {
                value: "pending_info",
                label: "Request Info",
                color: "border-yellow-500 bg-yellow-50",
                icon: AlertTriangle,
              },
            ].map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => setReviewDecision(option.value)}
                  className={`p-4 border-2 rounded-lg transition-all ${reviewDecision === option.value
                    ? option.color
                    : "border-gray-200 hover:border-gray-300"
                    }`}
                >
                  <div className="flex items-center space-x-2">
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{option.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Review Comments *
          </label>
          <textarea
            value={reviewComments}
            onChange={(e) => setReviewComments(e.target.value)}
            rows="4"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Provide detailed medical review comments..."
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={() => confirmLeave('/doctor/dashboard')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel Review
          </button>
          <button
            onClick={() => alert("Medical review functionality has been removed")}
            disabled={true}
            className="bg-gray-400 text-white px-4 py-2 rounded-lg cursor-not-allowed flex items-center space-x-2"
            title="Medical review functionality has been removed"
          >
            {actionLoading.complete ? (
              <>
                <Loader className="h-4 w-4 animate-spin" />
                <span>Completing...</span>
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                <span>Complete Review</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  </div>
  // );

  const renderKYPGeneration = () => {
    // Check if both policy and financial verification are completed
    if (application?.workflow?.policyVerification?.status !== "approved" ||
      application?.workflow?.financialVerification?.status !== "approved") {
      return (
        <div className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8 text-center">
            <div className="bg-yellow-100 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <Shield className="h-8 w-8 text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-yellow-900 mb-2">
              Policy and Financial Verification Required
            </h3>
            <p className="text-yellow-700 mb-4">
              KYP generation is only available after both Policy Underwriting and Financial teams have completed their verification and approved the application.
            </p>

            <div className="space-y-3 max-w-md mx-auto">
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                <span className="text-sm font-medium">Policy Verification</span>
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${application?.workflow?.policyVerification?.status === "approved"
                  ? "bg-green-100 text-green-800"
                  : application?.workflow?.policyVerification?.status === "in_progress"
                    ? "bg-yellow-100 text-yellow-800"
                    : application?.workflow?.policyVerification?.status === "rejected"
                      ? "bg-red-100 text-red-800"
                      : "bg-gray-100 text-gray-800"
                  }`}>
                  {application?.workflow?.policyVerification?.status === "approved" ? "✓ Approved" :
                    application?.workflow?.policyVerification?.status === "in_progress" ? "In Progress" :
                      application?.workflow?.policyVerification?.status === "rejected" ? "Rejected" :
                        "Pending"}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                <span className="text-sm font-medium">Financial Verification</span>
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${(application?.status === 'financial_approved' || application?.workflow?.financialVerification?.status === "approved")
                  ? "bg-green-100 text-green-800"
                  : application?.workflow?.financialVerification?.status === "in_progress"
                    ? "bg-yellow-100 text-yellow-800"
                    : application?.workflow?.financialVerification?.status === "rejected"
                      ? "bg-red-100 text-red-800"
                      : "bg-gray-100 text-gray-800"
                  }`}>
                  {(application?.status === 'financial_approved' || application?.workflow?.financialVerification?.status === "approved") ? "✓ Approved" :
                    application?.workflow?.financialVerification?.status === "in_progress" ? "In Progress" :
                      application?.workflow?.financialVerification?.status === "rejected" ? "Rejected" :
                        "Pending"}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const policyVerification = application?.workflow?.policyVerification || {};

    return (
      <div className="space-y-6">
        {/* KYP Generation with Enhanced Policy Details */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              KYP (Know Your Policy) Letter & Disbursement Approval
            </h3>
            {application.workflow?.medicalVerification?.kypGenerated && (
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="text-green-600 font-medium">KYP Generated</span>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {/* Policy Verification Summary */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h4 className="font-medium text-green-900 mb-4 flex items-center space-x-2">
                <Shield className="h-5 w-5" />
                <span>Verified Policy Coverage Details</span>
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="text-sm font-medium text-green-700">Policy Type</label>
                  <p className="text-gray-900 font-semibold">
                    {policyVerification.policyDetails?.policyType || application.policy?.type || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-green-700">Policy Number</label>
                  <p className="text-gray-900 font-mono">
                    {policyVerification.policyDetails?.policyNumber || application.policy?.number}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-green-700">Base Sum Insured</label>
                  <p className="text-lg font-semibold text-green-600">
                    {formatAmount(policyVerification.policyDetails?.baseSumInsured || application.policy?.coverageAmount)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-green-700">Cumulative Bonus</label>
                  <p className="text-gray-900">
                    {formatAmount(policyVerification.policyDetails?.cumulativeBonus || 0)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-green-700">Top-up Sum Insured</label>
                  <p className="text-gray-900">
                    {formatAmount(policyVerification.policyDetails?.topUpSumInsured || 0)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-green-700">Effective Total Coverage</label>
                  <p className="text-lg font-semibold text-green-600">
                    {formatAmount(policyVerification.policyDetails?.effectiveTotalSumInsured || application.policy?.coverageAmount)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-green-700">Balance Available</label>
                  <p className="text-lg font-semibold text-blue-600">
                    {formatAmount(policyVerification.policyDetails?.balanceSumInsured || application.policy?.coverageAmount)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-green-700">Policy Period</label>
                  <p className="text-gray-900">
                    {policyVerification.policyDetails?.inceptionDate || application.policy?.startDate} to {policyVerification.policyDetails?.expiryDate || application.policy?.endDate}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-green-700">Room Eligibility</label>
                  <p className="text-gray-900">
                    {policyVerification.roomEligibility?.roomType || 'Single Standard AC Room'}
                    {policyVerification.roomEligibility?.roomAmountPerDay ?
                      ` (₹${policyVerification.roomEligibility.roomAmountPerDay}/day)` : ''}
                  </p>
                </div>
              </div>

              {/* Co-pay Information */}
              <div className="mt-4 pt-4 border-t border-green-200">
                <h5 className="font-medium text-green-900 mb-3">Co-pay Structure</h5>
                {policyVerification.coPayStructure ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {policyVerification.coPayStructure.generalCoPayApplicable ? (
                      <div>
                        <label className="text-sm font-medium text-green-700">General Co-pay</label>
                        <p className="text-gray-900">
                          {policyVerification.coPayStructure.generalCoPayValue || 0}
                          {policyVerification.coPayStructure.generalCoPayType?.includes('percentage') ? '%' : ' ₹'}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label className="text-sm font-medium text-green-700">General Co-pay</label>
                        <p className="text-gray-900">Not Applicable</p>
                      </div>
                    )}
                    {policyVerification.coPayStructure.parentalCoPayApplicable ? (
                      <div>
                        <label className="text-sm font-medium text-green-700">Parental Co-pay</label>
                        <p className="text-gray-900">
                          {policyVerification.coPayStructure.parentalCoPayValue || 0}
                          {policyVerification.coPayStructure.parentalCoPayType?.includes('percentage') ? '%' : ' ₹'}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label className="text-sm font-medium text-green-700">Parental Co-pay</label>
                        <p className="text-gray-900">Not Applicable</p>
                      </div>
                    )}
                    {policyVerification.coPayStructure.locationBasedCoPayApplicable && (
                      <div>
                        <label className="text-sm font-medium text-green-700">Location-based Co-pay</label>
                        <div className="text-sm text-gray-900">
                          <div>Metro: {policyVerification.coPayStructure.metroCoPayValue || 0}{policyVerification.coPayStructure.metroCoPayType?.includes('percentage') ? '%' : ' ₹'}</div>
                          <div>Non-Metro: {policyVerification.coPayStructure.nonMetroCoPayValue || 0}{policyVerification.coPayStructure.nonMetroCoPayType?.includes('percentage') ? '%' : ' ₹'}</div>
                        </div>
                      </div>
                    )}
                    {policyVerification.coPayStructure.voluntaryCoPayApplicable && (
                      <div>
                        <label className="text-sm font-medium text-green-700">Voluntary Co-pay</label>
                        <p className="text-gray-900">
                          {policyVerification.coPayStructure.voluntaryCoPayValue || 0}
                          {policyVerification.coPayStructure.voluntaryCoPayType?.includes('percentage') ? '%' : ' ₹'}
                        </p>
                      </div>
                    )}
                    {policyVerification.coPayStructure.mandatoryCoPayApplicable && (
                      <div>
                        <label className="text-sm font-medium text-green-700">Mandatory Co-pay</label>
                        <p className="text-gray-900">
                          {policyVerification.coPayStructure.mandatoryCoPayValue || 0}
                          {policyVerification.coPayStructure.mandatoryCoPayType?.includes('percentage') ? '%' : ' ₹'}
                        </p>
                      </div>
                    )}
                    {policyVerification.coPayStructure.nonIntimationCoPayApplicable && (
                      <div>
                        <label className="text-sm font-medium text-green-700">Non-intimation Co-pay</label>
                        <p className="text-gray-900">
                          {policyVerification.coPayStructure.nonIntimationCoPayValue || 0}
                          {policyVerification.coPayStructure.nonIntimationCoPayType?.includes('percentage') ? '%' : ' ₹'}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">
                    No co-pay information available - please complete policy verification first.
                  </div>
                )}
              </div>

              {/* Deductibles & Sublimits */}
              <div className="mt-4 pt-4 border-t border-green-200">
                <h5 className="font-medium text-green-900 mb-3">Deductibles & Sublimits</h5>
                {policyVerification.deductiblesAndSublimits ? (
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-green-700">Compulsory Deductible</label>
                        <p className="text-gray-900">
                          {(policyVerification.deductiblesAndSublimits.compulsoryDeductible &&
                            parseFloat(policyVerification.deductiblesAndSublimits.compulsoryDeductible) > 0) ?
                            formatAmount(policyVerification.deductiblesAndSublimits.compulsoryDeductible) :
                            'Not Applicable'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-green-700">Pre/Post Natal Limit</label>
                        <p className="text-gray-900">
                          {(policyVerification.deductiblesAndSublimits.prePostNatalLimit &&
                            parseFloat(policyVerification.deductiblesAndSublimits.prePostNatalLimit) > 0) ?
                            formatAmount(policyVerification.deductiblesAndSublimits.prePostNatalLimit) :
                            'Not Applicable'}
                        </p>
                      </div>
                    </div>

                    {policyVerification.deductiblesAndSublimits.ailmentProcedureSublimits?.length > 0 ? (
                      <div className="mt-3">
                        <label className="text-sm font-medium text-green-700 block mb-2">Procedure Sublimits</label>
                        <div className="space-y-2">
                          {policyVerification.deductiblesAndSublimits.ailmentProcedureSublimits.map((sublimit, index) => (
                            <div key={index} className="flex justify-between text-sm">
                              <span className="text-gray-700">{sublimit.ailmentProcedure}</span>
                              <span className="text-gray-900 font-medium">{formatAmount(sublimit.sublimitAmount)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3">
                        <label className="text-sm font-medium text-green-700 block mb-2">Procedure Sublimits</label>
                        <p className="text-gray-500 text-sm">No procedure sublimits defined</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">
                    No deductible information available - please complete policy verification first.
                  </div>
                )}
              </div>
            </div>

            {/* Medical Information Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h4 className="font-medium text-blue-900 mb-4 flex items-center space-x-2">
                <Stethoscope className="h-5 w-5" />
                <span>Medical Treatment Details</span>
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-blue-700">Primary Diagnosis</label>
                    <p className="text-gray-900 font-semibold">
                      {application.medical?.treatments?.[0]?.diagnosisName || application.medical?.diagnosis || 'N/A'}
                    </p>
                    {application.medical?.treatments?.[0]?.icdCode && (
                      <p className="text-sm text-gray-600">
                        ICD Code: {application.medical.treatments[0].icdCode}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-blue-700">Treatment Type</label>
                    <p className="text-gray-900">
                      {application.medical?.treatments?.[0]?.treatmentType || application.medical?.treatmentType || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-blue-700">Procedure</label>
                    <p className="text-gray-900">
                      {application.medical?.treatments?.[0]?.procedureName || application.medical?.procedureName || 'As per diagnosis'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-blue-700">Consulting Doctor</label>
                    <p className="text-gray-900">{application.medical?.doctor?.name}</p>
                    <p className="text-sm text-gray-600">{application.medical?.doctor?.designation}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-blue-700">Admission Date</label>
                    <p className="text-gray-900">{application.medical?.admissionDate}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-blue-700">Expected Discharge</label>
                    <p className="text-gray-900">{application.medical?.dischargeDate || 'TBD'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-blue-700">Estimated Cost</label>
                    <p className="text-lg font-semibold text-blue-600">
                      {formatAmount(application.medical?.estimatedCost)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-blue-700">Hospital</label>
                    <p className="text-gray-900">{application.hospitalName}</p>
                    <p className="text-sm text-gray-600">{application.hospital?.city}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Verification Checklist */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">
                Medical Verification Checklist
              </h4>
              <div className="space-y-3">
                {[
                  {
                    key: "medicalHistoryVerified",
                    label: "Medical History Verified",
                  },
                  { key: "documentsAuthentic", label: "Documents Authentic" },
                  {
                    key: "treatmentAppropriate",
                    label: "Treatment Plan Appropriate",
                  },
                ].map((item) => (
                  <label key={item.key} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={kypData[item.key]}
                      onChange={(e) =>
                        setKypData((prev) => ({
                          ...prev,
                          [item.key]: e.target.checked,
                        }))
                      }
                      disabled={application?.workflow?.medicalVerification?.kypApproval?.status === 'approved'}
                      className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span className="text-sm text-gray-700">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pre-existing Conditions
                </label>
                <textarea
                  value={kypData.preExistingConditions}
                  onChange={(e) =>
                    setKypData((prev) => ({
                      ...prev,
                      preExistingConditions: e.target.value,
                    }))
                  }
                  rows="3"
                  disabled={application?.workflow?.medicalVerification?.kypApproval?.status === 'approved'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="List any pre-existing conditions..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Medical Recommendation
                </label>
                <textarea
                  value={kypData.recommendation}
                  onChange={(e) =>
                    setKypData((prev) => ({
                      ...prev,
                      recommendation: e.target.value,
                    }))
                  }
                  rows="3"
                  disabled={application?.workflow?.medicalVerification?.kypApproval?.status === 'approved'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Medical recommendation for treatment..."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clinical Notes
              </label>
              <textarea
                value={kypData.clinicalNotes}
                onChange={(e) =>
                  setKypData((prev) => ({
                    ...prev,
                    clinicalNotes: e.target.value,
                  }))
                }
                rows="4"
                disabled={application?.workflow?.medicalVerification?.kypApproval?.status === 'approved'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Additional clinical notes and observations..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Doctor Registration No.
                </label>
                <input
                  type="text"
                  value={kypData.registrationNo}
                  onChange={(e) =>
                    setKypData((prev) => ({
                      ...prev,
                      registrationNo: e.target.value,
                    }))
                  }
                  disabled={application?.workflow?.medicalVerification?.kypApproval?.status === 'approved'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Medical registration number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Validity Period
                </label>
                <select
                  value={kypData.validityPeriod}
                  onChange={(e) =>
                    setKypData((prev) => ({
                      ...prev,
                      validityPeriod: e.target.value,
                    }))
                  }
                  disabled={application?.workflow?.medicalVerification?.kypApproval?.status === 'approved'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="15 days">15 days</option>
                  <option value="30 days">30 days</option>
                  <option value="60 days">60 days</option>
                  <option value="90 days">90 days</option>
                  <option value="180 days">180 days</option>
                </select>
              </div>
            </div>

            {/* Remarks Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Internal Remarks
                  <span className="text-xs text-gray-500 ml-1">(For internal reference only)</span>
                </label>
                <textarea
                  value={kypData.internalRemarks}
                  onChange={(e) =>
                    setKypData((prev) => ({
                      ...prev,
                      internalRemarks: e.target.value,
                    }))
                  }
                  rows="4"
                  disabled={application?.workflow?.medicalVerification?.kypApproval?.status === 'approved'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Internal notes and remarks for reference (not shown in KYP letter)..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Letter Remarks
                  <span className="text-xs text-gray-500 ml-1">(Will appear in KYP letter)</span>
                </label>
                <textarea
                  value={kypData.letterRemarks}
                  onChange={(e) =>
                    setKypData((prev) => ({
                      ...prev,
                      letterRemarks: e.target.value,
                    }))
                  }
                  rows="4"
                  disabled={application?.workflow?.medicalVerification?.kypApproval?.status === 'approved'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Remarks to be included in the KYP letter for communication purposes..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              {application.workflow?.medicalVerification?.kypGenerated ? (
                <button
                  onClick={handleViewKYP}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
                >
                  <Eye className="h-4 w-4" />
                  <span>View Generated KYP</span>
                </button>
              ) : (
                <button
                  onClick={handleGenerateKYP}
                  disabled={actionLoading.kyp || !isMedicalChecklistComplete}
                  className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  title={!isMedicalChecklistComplete ? "Complete the Medical Verification Checklist to enable KYP generation" : ""}
                >
                  {actionLoading.kyp ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Generating PDF...</span>
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4" />
                      <span>Generate KYP Letter (PDF)</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDischargeReview = () => {
    // Check if KYP is generated before allowing discharge review
    if (!application?.workflow?.medicalVerification?.kypGenerated) {
      return (
        <div className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8 text-center">
            <div className="bg-yellow-100 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-yellow-900 mb-2">
              KYP Letter Required
            </h3>
            <p className="text-yellow-700 mb-4">
              Please generate the KYP (Know Your Policy) letter before proceeding with discharge review.
            </p>
            <button
              onClick={() => setActiveTab("application")}
              className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
            >
              Go to KYP Generation
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Discharge Review - Split view */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Discharge Summary Review</h3>
            <div className="flex space-x-3">
              <input ref={dischargeFileInputRef} type="file" className="hidden" onChange={async (e) => {
                const file = e.target.files && e.target.files[0];
                if (!file) return;
                setActionLoading((p) => ({ ...p, extractBill: true }));
                try {
                  // Upload to review-documents to get a storage path
                  const result = await uploadReviewDocument(applicationId, file, 'discharge_bill', user.uid);
                  if (!result.success) throw new Error(result.error || 'Upload failed');
                  // Derive storagePath from the known pattern
                  // Our uploadReviewDocument stores under `review-documents/${applicationId}/${fileName}`; we don't have direct storage path here,
                  // but we can reconstruct from the URL for default bucket, or we pass URL to the function.
                  const fileUrl = result.document.url;
                  const { extractDischargeItemsFromBill } = await import('../../services/billExtractionService');
                  const extraction = await extractDischargeItemsFromBill({ applicationId, fileUrl, autoSave: false });
                  if (extraction.success && Array.isArray(extraction.items) && extraction.items.length) {
                    // Map extracted items to UI shape for review modal (selected by default)
                    const mapped = extraction.items.map((it) => ({
                      id: it.id,
                      itemName: it.description,
                      category: it.category || 'other',
                      quantity: it.quantity || 1,
                      unitRate: it.unitRate || 0,
                      eligibleUnitRate: it.unitRate || 0,
                      billedAmount: it.billedAmount || ((it.unitRate || 0) * (it.quantity || 1)),
                      eligibleAmount: it.eligibleAmount || it.billedAmount || 0,
                      payableType: 'payable',
                      remarks: 'Extracted from bill',
                      selected: true,
                    }));
                    setExtractedItemsReview(mapped);
                    setShowExtractModal(true);
                  } else {
                    alert('No items extracted from the bill.');
                  }
                } catch (err) {
                  console.error('Extract from bill error:', err);
                  alert(err.message || 'Failed to extract items');
                } finally {
                  setActionLoading((p) => ({ ...p, extractBill: false }));
                }
              }} />
              {/* <button onClick={() => dischargeFileInputRef.current?.click()} disabled={actionLoading.dischargeUpload} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2">
                {actionLoading.dischargeUpload ? <Loader className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <span>Upload Files</span>
              </button> */}
              <button onClick={() => dischargeFileInputRef.current?.click()} disabled={isFALPublished || actionLoading.extractBill} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2">
                {actionLoading.extractBill ? <Loader className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <span>Extract from Bill</span>
              </button>
              <button onClick={() => !isFALPublished && setShowAddItemModal(true)} disabled={isFALPublished} className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2">
                <Plus className="h-4 w-4" />
                <span>Add Item</span>
              </button>
            </div>
          </div>

          {/* Extraction Review Modal */}
          {showExtractModal && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-gray-900">Review Extracted Items</h4>
                  <button onClick={() => setShowExtractModal(false)} className="text-gray-500 hover:text-gray-700">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-6 max-h-[60vh] overflow-auto">
                  {extractedItemsReview.length === 0 ? (
                    <div className="text-sm text-gray-600">No items extracted.</div>
                  ) : (
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-gray-600 border-b">
                          <th className="px-2 py-2"><input type="checkbox" checked={extractedItemsReview.every(i => i.selected)} onChange={(e) => setExtractedItemsReview(prev => prev.map(i => ({ ...i, selected: e.target.checked })))} /></th>
                          <th className="px-2 py-2 text-left">Item/Service</th>
                          <th className="px-2 py-2 text-left">Category</th>
                          <th className="px-2 py-2 text-right">Qty</th>
                          <th className="px-2 py-2 text-right">Rate</th>
                          <th className="px-2 py-2 text-right">Billed</th>
                          <th className="px-2 py-2 text-right">Eligible</th>
                        </tr>
                      </thead>
                      <tbody>
                        {extractedItemsReview.map((it, idx) => (
                          <tr key={it.id || idx} className="border-b hover:bg-gray-50">
                            <td className="px-2 py-2 text-center">
                              <input type="checkbox" checked={!!it.selected} onChange={(e) => setExtractedItemsReview(prev => prev.map((x, i) => i === idx ? { ...x, selected: e.target.checked } : x))} />
                            </td>
                            <td className="px-2 py-2">{it.itemName}</td>
                            <td className="px-2 py-2 capitalize">{it.category}</td>
                            <td className="px-2 py-2 text-right">{it.quantity}</td>
                            <td className="px-2 py-2 text-right">₹{(parseFloat(it.unitRate) || 0).toLocaleString('en-IN')}</td>
                            <td className="px-2 py-2 text-right">₹{(parseFloat(it.billedAmount) || 0).toLocaleString('en-IN')}</td>
                            <td className="px-2 py-2 text-right">₹{(parseFloat(it.eligibleAmount) || 0).toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="px-6 py-4 border-t flex items-center justify-between">
                  <div className="text-sm text-gray-600">Selected: {extractedItemsReview.filter(i => i.selected).length} / {extractedItemsReview.length}</div>
                  <div className="space-x-3">
                    <button onClick={() => setShowExtractModal(false)} className="px-4 py-2 rounded-lg border text-gray-700">Cancel</button>
                    <button
                      onClick={async () => {
                        const toAdd = extractedItemsReview.filter(i => i.selected);
                        if (toAdd.length === 0) { setShowExtractModal(false); return; }
                        // Merge into local state
                        setDischargeItems((prev) => [...prev, ...toAdd.map(({ selected, ...rest }) => rest)]);
                        // Auto-save to dischargeSummaries and reload UI
                        try {
                          const merged = (dischargeItems || []).concat(toAdd.map(({ selected, ...rest }) => rest));
                          await saveDischargeDraft(applicationId, user?.uid, { items: merged, autoSaved: true });
                          const res = await getDischargeSummary(applicationId);
                          if (res.success && res.dischargeSummary) {
                            setDischargeSummary(res.dischargeSummary);
                            setDischargeItems(res.dischargeSummary.items || merged);
                          }
                        } catch (e) { console.error('Auto-save extracted items failed:', e); }
                        setShowExtractModal(false);
                        setExtractedItemsReview([]);
                      }}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                    >
                      Add Selected Items
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isFALPublished && (
            <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-800">
              Final Approval Letter is published. Discharge items and comments are locked. You can still view data and proceed with dispatch-related actions.
            </div>
          )}

          <div className="grid grid-cols-1 gap-6">
            {/* Left: Items and Summary */}
            <div className="space-y-6">
              {/* Discharge Items Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Item/Service
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Rate/Unit
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Elig. Rate/Unit
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Qty
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Billed Amount
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Eligible Amount
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Variance
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Remarks
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Approved
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {dischargeItems.map((item, index) => {
                      const variance =
                        (parseFloat(item.billedAmount) || 0) -
                        (parseFloat(item.eligibleAmount) || 0);
                      return (
                        <tr key={item.id} className={`hover:bg-gray-50 ${isFALPublished ? 'cursor-default' : 'cursor-pointer'}`} onClick={() => { if (!isFALPublished) handleOpenEditItem(item); }}>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-900 font-medium">
                            {item.itemName}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700 capitalize">{item.category}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700">{item.unitRate ? `₹${(parseFloat(item.unitRate) || 0).toLocaleString('en-IN')}` : '-'}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700">{item.eligibleUnitRate ? `₹${(parseFloat(item.eligibleUnitRate) || 0).toLocaleString('en-IN')}` : '-'}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700">{item.quantity ?? '-'}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-900">₹{(parseFloat(item.billedAmount) || 0).toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-900">₹{(parseFloat(item.eligibleAmount) || 0).toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span
                              className={`text-sm font-semibold ${variance > 0
                                ? "text-red-600"
                                : variance < 0
                                  ? "text-green-600"
                                  : "text-gray-600"
                                }`}
                            >
                              ₹{variance.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700 max-w-xs truncate" title={item.remarks || ''}>
                            {item.remarks ? item.remarks : '-'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${item.approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {item.approved ? 'Yes' : 'Hold'}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <button
                              onClick={(e) => { e.stopPropagation(); if (!isFALPublished) handleRemoveDischargeItem(item.id); }}
                              className="text-red-600 hover:text-red-800 disabled:opacity-50"
                              title="Remove Item"
                              disabled={isFALPublished}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {dischargeItems.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No discharge items added yet. Click "Add Item" to start reviewing discharge summary.
                  </div>
                )}
              </div>

              {/* New Stepped Workflow */}
              {dischargeItems.length > 0 && (
                <div className="mt-6 space-y-6">
                  
                  {/* Step 1: Show Summary */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-900 flex items-center">
                        <Calculator className="h-5 w-5 text-blue-600 mr-2" />
                        Financial Summary
                      </h4>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">Step 1</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">₹{totals.totalBilled.toLocaleString("en-IN")}</div>
                        <div className="text-sm text-gray-600">Total Billed</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-teal-600">₹{totals.totalEligible.toLocaleString("en-IN")}</div>
                        <div className="text-sm text-gray-600">Total Eligible</div>
                      </div>
                      <div className="text-center">
                        <div className={`text-2xl font-bold ${totals.variance > 0 ? "text-red-600" : totals.variance < 0 ? "text-green-600" : "text-gray-600"}`}>₹{Math.abs(totals.variance).toLocaleString("en-IN")}</div>
                        <div className="text-sm text-gray-600">{totals.variance > 0 ? "Over-billed" : totals.variance < 0 ? "Under-billed" : "Exact Match"}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{totals.eligibilityPercentage}%</div>
                        <div className="text-sm text-gray-600">Eligibility Rate</div>
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Show Copays and Sublimits */}
                  <div className="space-y-6">
                    {/* Co-pay Selection */}
                    {(() => {
                      const cp = application?.workflow?.policyVerification?.coPayStructure;
                      const applicableCopays = [];
                      
                      if (cp?.generalCoPayApplicable && cp.generalCoPayValue > 0) {
                        applicableCopays.push({
                          type: 'general',
                          label: 'General Co-pay',
                          percent: cp.generalCoPayValue,
                          description: 'Standard co-payment as per policy terms'
                        });
                      }
                      
                      if (cp?.nonNetworkCoPayApplicable && cp.nonNetworkCoPayValue > 0) {
                        applicableCopays.push({
                          type: 'nonNetwork',
                          label: 'Non-Network Hospital Co-pay',
                          percent: cp.nonNetworkCoPayValue,
                          description: 'Additional co-pay for non-network hospitals'
                        });
                      }
                      
                      if (cp?.locationBasedCoPayApplicable) {
                        if (cp.metroCoPayValue > 0) {
                          applicableCopays.push({
                            type: 'locationMetro',
                            label: 'Metro Location Co-pay',
                            percent: cp.metroCoPayValue,
                            description: 'Location-based co-pay for metro cities'
                          });
                        }
                        if (cp.nonMetroCoPayValue > 0) {
                          applicableCopays.push({
                            type: 'locationNonMetro',
                            label: 'Non-Metro Location Co-pay',
                            percent: cp.nonMetroCoPayValue,
                            description: 'Location-based co-pay for non-metro areas'
                          });
                        }
                      }
                      
                      if (cp?.parentalCoPayApplicable && cp.parentalCoPayValue > 0) {
                        applicableCopays.push({
                          type: 'parental',
                          label: `Parental Co-pay (Age ${cp.parentalCoPayAgeThreshold || '60'}+)`,
                          percent: cp.parentalCoPayValue,
                          description: 'Age-based co-payment for elderly patients'
                        });
                      }
                      
                      if (cp?.mandatoryCoPayApplicable && cp.mandatoryCoPayValue > 0) {
                        applicableCopays.push({
                          type: 'mandatory',
                          label: 'Mandatory Co-pay',
                          percent: cp.mandatoryCoPayValue,
                          description: 'Compulsory co-payment as per policy'
                        });
                      }
                      
                      if (cp?.voluntaryCoPayApplicable && cp.voluntaryCoPayValue > 0) {
                        applicableCopays.push({
                          type: 'voluntary',
                          label: 'Voluntary Co-pay',
                          percent: cp.voluntaryCoPayValue,
                          description: 'Optional co-payment for premium reduction'
                        });
                      }
                      
                      if (cp?.nonIntimationCoPayApplicable && cp.nonIntimationCoPayValue > 0) {
                        applicableCopays.push({
                          type: 'nonIntimation',
                          label: 'Non-Intimation Co-pay',
                          percent: cp.nonIntimationCoPayValue,
                          description: 'Penalty co-pay for non-intimation of claim'
                        });
                      }

                      return applicableCopays.length > 0 ? (
                        <div className="bg-white border border-gray-200 rounded-xl p-6">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                              <Percent className="h-5 w-5 text-purple-600 mr-2" />
                              Co-pay Selection
                            </h4>
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">Step 2a</span>
                          </div>
                          
                          <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                            <div className="flex items-start space-x-2">
                              <Info className="h-4 w-4 text-purple-600 mt-0.5" />
                              <div className="text-sm text-purple-800">
                                <strong>Co-pay Rules:</strong> Select which co-pays apply to this claim. The system will add all selected applicable co-pay percentages together.
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {applicableCopays.map((copay) => (
                              <div key={copay.type} className={`border rounded-lg p-4 transition-all ${
                                falOverrides.copaySelection?.[copay.type] 
                                  ? 'border-purple-300 bg-purple-50' 
                                  : 'border-gray-200 bg-gray-50'
                              }`}>
                                <div className="flex items-start space-x-3">
                                  <input
                                    type="checkbox"
                                    id={`copay-${copay.type}`}
                                    checked={falOverrides.copaySelection?.[copay.type] || false}
                                    onChange={(e) => setFalOverrides(prev => ({
                                      ...prev,
                                      copaySelection: {
                                        ...prev.copaySelection,
                                        [copay.type]: e.target.checked
                                      }
                                    }))}
                                    className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                  />
                                  <div className="flex-1">
                                    <label htmlFor={`copay-${copay.type}`} className="block text-sm font-medium text-gray-900 cursor-pointer">
                                      {copay.label}
                                    </label>
                                    <div className="text-lg font-bold text-purple-700 mt-1">
                                      {copay.percent}%
                                    </div>
                                    <p className="text-xs text-gray-600 mt-1">
                                      {copay.description}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
                          <Percent className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <div className="text-sm text-gray-600">No co-pays configured in policy verification</div>
                        </div>
                      );
                    })()}

                    {/* Sublimit Selection */}
                    {(() => {
                      const sl = application?.workflow?.policyVerification?.deductiblesAndSublimits?.ailmentProcedureSublimits;
                      const availableSublimits = Array.isArray(sl) ? sl.map((sublimit, idx) => ({
                        key: `sublimit_${idx}`,
                        ailmentProcedure: sublimit.ailmentProcedure,
                        sublimitAmount: parseFloat(sublimit.sublimitAmount) || 0,
                        description: `Sublimit for ${sublimit.ailmentProcedure}: ₹${(parseFloat(sublimit.sublimitAmount) || 0).toLocaleString('en-IN')}`
                      })).filter(s => s.sublimitAmount > 0) : [];

                      return availableSublimits.length > 0 ? (
                        <div className="bg-white border border-gray-200 rounded-xl p-6">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                              <Shield className="h-5 w-5 text-indigo-600 mr-2" />
                              Sublimit Selection
                            </h4>
                            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">Step 2b</span>
                          </div>
                          
                          <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                            <div className="flex items-start space-x-2">
                              <Info className="h-4 w-4 text-indigo-600 mt-0.5" />
                              <div className="text-sm text-indigo-800">
                                <strong>Sublimit Rules:</strong> Select which sublimits apply to this claim. Selected sublimits will be added together, and the approved amount will be capped at the total selected sublimit amount.
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {availableSublimits.map((sublimit) => (
                              <div key={sublimit.key} className={`border rounded-lg p-4 transition-all ${
                                falOverrides.sublimitSelection?.[sublimit.key] 
                                  ? 'border-indigo-300 bg-indigo-50' 
                                  : 'border-gray-200 bg-gray-50'
                              }`}>
                                <div className="flex items-start space-x-3">
                                  <input
                                    type="checkbox"
                                    id={`sublimit-${sublimit.key}`}
                                    checked={falOverrides.sublimitSelection?.[sublimit.key] || false}
                                    onChange={(e) => setFalOverrides(prev => ({
                                      ...prev,
                                      sublimitSelection: {
                                        ...prev.sublimitSelection,
                                        [sublimit.key]: e.target.checked
                                      }
                                    }))}
                                    className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                  />
                                  <div className="flex-1">
                                    <label htmlFor={`sublimit-${sublimit.key}`} className="block text-sm font-medium text-gray-900 cursor-pointer">
                                      {sublimit.ailmentProcedure}
                                    </label>
                                    <div className="text-lg font-bold text-indigo-700 mt-1">
                                      ₹{sublimit.sublimitAmount.toLocaleString('en-IN')}
                                    </div>
                                    <p className="text-xs text-gray-600 mt-1">
                                      Maximum coverage for this condition/procedure
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Show selected sublimits summary */}
                          {(() => {
                            const selectedKeys = Object.keys(falOverrides.sublimitSelection || {}).filter(
                              key => falOverrides.sublimitSelection[key] === true
                            );
                            const totalSelected = selectedKeys.reduce((sum, key) => {
                              const sublimit = availableSublimits.find(s => s.key === key);
                              return sum + (sublimit ? sublimit.sublimitAmount : 0);
                            }, 0);

                            return selectedKeys.length > 0 ? (
                              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="text-sm text-green-800">
                                  <strong>Selected Sublimits Total:</strong> ₹{totalSelected.toLocaleString('en-IN')}
                                  <br />
                                  <span className="text-xs">The approved amount will be capped at this total, even if eligible amount is higher.</span>
                                </div>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
                          <Shield className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <div className="text-sm text-gray-600">No sublimits configured in policy verification</div>
                        </div>
                      );
                    })()}

                    {/* Sublimit Override Adjustments */}
                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                          <Shield className="h-5 w-5 text-amber-600 mr-2" />
                          Sublimit Override Adjustments
                        </h4>
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">Step 2c</span>
                      </div>
                      
                      {Array.isArray(application?.workflow?.policyVerification?.deductiblesAndSublimits?.ailmentProcedureSublimits) && application.workflow.policyVerification.deductiblesAndSublimits.ailmentProcedureSublimits.length > 0 ? (
                        <div className="space-y-4">
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-start space-x-2">
                              <Info className="h-4 w-4 text-amber-600 mt-0.5" />
                              <div className="text-sm text-amber-800">
                                <strong>Override Rules:</strong> These overrides apply to the selected sublimits above. You can override the specific deduction/application amount if clinically justified.
                              </div>
                            </div>
                          </div>
                          
                          {application.workflow.policyVerification.deductiblesAndSublimits.ailmentProcedureSublimits.map((sl, idx) => {
                            const key = `sublimit_${idx}`;
                            const existing = (falOverrides.sublimits || []).find((s) => s.key === key) || null;
                            const hasOverride = !!existing?.amountOverride;
                            const isSelected = falOverrides.sublimitSelection?.[key] === true;
                            
                            return (
                              <div key={key} className={`border rounded-lg p-4 transition-all ${
                                hasOverride ? 'border-amber-300 bg-amber-50' : 
                                isSelected ? 'border-indigo-300 bg-indigo-50' : 
                                'border-gray-200 bg-gray-50'
                              }`}>
                                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start">
                                  <div className="lg:col-span-1">
                                    <div className="text-sm font-medium text-gray-900 mb-1">
                                      {sl.ailmentProcedure || `Sublimit #${idx + 1}`}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      Limit: ₹{(parseFloat(sl.sublimitAmount) || 0).toLocaleString('en-IN')}
                                    </div>
                                    {isSelected && (
                                      <div className="text-xs text-indigo-600 font-medium mt-1">
                                        ✓ Selected for application
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Override Amount (₹)</label>
                                    <input
                                      type="number"
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                      value={existing?.amountOverride || ''}
                                      onChange={(e) => setFalOverrides((prev) => {
                                        const list = Array.isArray(prev.sublimits) ? [...prev.sublimits] : [];
                                        const idxFound = list.findIndex((x) => x.key === key);
                                        const updated = { key, amountOverride: e.target.value, reason: existing?.reason || '' };
                                        if (idxFound >= 0) list[idxFound] = updated; else list.push(updated);
                                        return { ...prev, sublimits: list };
                                      })}
                                      placeholder="Override amount"
                                    />
                                  </div>
                                  
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Clinical Justification</label>
                                    <input
                                      type="text"
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                      value={existing?.reason || ''}
                                      onChange={(e) => setFalOverrides((prev) => {
                                        const list = Array.isArray(prev.sublimits) ? [...prev.sublimits] : [];
                                        const idxFound = list.findIndex((x) => x.key === key);
                                        const updated = { key, amountOverride: existing?.amountOverride || '', reason: e.target.value };
                                        if (idxFound >= 0) list[idxFound] = updated; else list.push(updated);
                                        return { ...prev, sublimits: list };
                                      })}
                                      placeholder="Clinical reason for override"
                                    />
                                  </div>
                                  
                                  <div className="flex justify-center">
                                    {hasOverride ? (
                                      <button
                                        type="button"
                                        className="px-3 py-2 bg-red-100 text-red-700 border border-red-300 rounded-lg text-sm hover:bg-red-200 transition-colors"
                                        onClick={() => setFalOverrides((prev) => ({
                                          ...prev,
                                          sublimits: (prev.sublimits || []).filter((s) => s.key !== key)
                                        }))}
                                      >
                                        Remove Override
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="px-3 py-2 bg-amber-100 text-amber-700 border border-amber-300 rounded-lg text-sm hover:bg-amber-200 transition-colors"
                                        onClick={() => setFalOverrides((prev) => ({
                                          ...prev,
                                          sublimits: [ ...(prev.sublimits || []), { key, amountOverride: '', reason: '' } ]
                                        }))}
                                      >
                                        Add Override
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <Shield className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <div className="text-sm text-gray-600">No sublimits configured in policy verification</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Step 3: Show Breakdown */}
                  {falComputedPreview && (
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                          <Calculator className="h-5 w-5 text-blue-600 mr-2" />
                          Settlement Breakdown
                        </h4>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">Step 3</span>
                      </div>
                      
                      {/* Enhanced FAL Summary */}
                      {falComputedPreview.falSummary && (
                        <div className="mb-6 p-4 bg-white border border-blue-200 rounded-lg">
                          <h5 className="text-md font-semibold text-gray-900 mb-3">Payment Summary</h5>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="text-center p-3 bg-green-50 border border-green-200 rounded-lg">
                              <div className="text-2xl font-bold text-green-700">₹{(falComputedPreview.falSummary.approvedAmount || 0).toLocaleString('en-IN')}</div>
                              <div className="text-xs text-green-600 font-medium">Approved Amount</div>
                              <div className="text-xs text-gray-500 mt-1">Insurer pays to hospital</div>
                            </div>
                            <div className="text-center p-3 bg-orange-50 border border-orange-200 rounded-lg">
                              <div className="text-2xl font-bold text-orange-700">₹{(falComputedPreview.falSummary.totalPatientPayment || 0).toLocaleString('en-IN')}</div>
                              <div className="text-xs text-orange-600 font-medium">Patient Contribution</div>
                              <div className="text-xs text-gray-500 mt-1">Copay + Sublimits + Deductible</div>
                            </div>
                            <div className="text-center p-3 bg-red-50 border border-red-200 rounded-lg">
                              <div className="text-2xl font-bold text-red-700">₹{(falComputedPreview.falSummary.remainingPatientPayment || 0).toLocaleString('en-IN')}</div>
                              <div className="text-xs text-red-600 font-medium">Remaining Amount</div>
                              <div className="text-xs text-gray-500 mt-1">Patient pays to hospital</div>
                            </div>
                          </div>
                          
                          {/* Detailed patient payment breakdown */}
                          <details className="mt-4">
                            <summary className="cursor-pointer text-sm font-medium text-blue-700 hover:text-blue-800">
                              View Patient Payment Breakdown
                            </summary>
                            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                              <div className="flex justify-between items-center py-1 border-b border-blue-100">
                                <span className="text-gray-700">Co-pay Amount</span>
                                <span className="font-medium text-gray-900">₹{(falComputedPreview.falSummary.patientCoPayAmount || 0).toLocaleString('en-IN')}</span>
                              </div>
                              <div className="flex justify-between items-center py-1 border-b border-blue-100">
                                <span className="text-gray-700">Sublimit Amount</span>
                                <span className="font-medium text-gray-900">₹{(falComputedPreview.falSummary.patientSublimitAmount || 0).toLocaleString('en-IN')}</span>
                              </div>
                              <div className="flex justify-between items-center py-1 border-b border-blue-100">
                                <span className="text-gray-700">Deductible Amount</span>
                                <span className="font-medium text-gray-900">₹{(falComputedPreview.falSummary.patientDeductibleAmount || 0).toLocaleString('en-IN')}</span>
                              </div>
                            </div>
                          </details>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-gray-900">₹{(falComputedPreview.totals?.billed || 0).toLocaleString('en-IN')}</div>
                          <div className="text-xs text-gray-600">Total Billed</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-700">₹{(falComputedPreview.totals?.eligible || 0).toLocaleString('en-IN')}</div>
                          <div className="text-xs text-gray-600">Eligible Amount</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-red-600">₹{((falComputedPreview.totals?.nonAdmissibles || 0) + (falComputedPreview.totals?.proportionate || 0) + (falComputedPreview.totals?.sublimits || 0) + (falComputedPreview.totals?.deductible || 0) + (falComputedPreview.totals?.copay || 0)).toLocaleString('en-IN')}</div>
                          <div className="text-xs text-gray-600">Total Deductions</div>
                        </div>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-teal-700">₹{(falComputedPreview.totals?.finalSettlement || 0).toLocaleString('en-IN')}</div>
                          <div className="text-xs text-gray-600">Final Settlement</div>
                        </div>
                      </div>

                      {/* Sublimit Information */}
                      {falComputedPreview.sublimitInfo && falComputedPreview.sublimitInfo.selectedLimits && falComputedPreview.sublimitInfo.selectedLimits.length > 0 && (
                        <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                          <div className="text-sm text-indigo-800">
                            <strong>Applied Sublimits:</strong> {falComputedPreview.sublimitInfo.selectedLimits.length} selected
                            <br />
                            <strong>Total Sublimit Cap:</strong> ₹{(falComputedPreview.sublimitInfo.totalSelectedAmount || 0).toLocaleString('en-IN')}
                          </div>
                        </div>
                      )}

                      <details className="mt-4">
                        <summary className="cursor-pointer text-sm font-medium text-blue-700 hover:text-blue-800">
                          View Detailed Breakdown
                        </summary>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          {falComputedPreview.breakdown?.map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center py-1 border-b border-blue-100">
                              <span className="text-gray-700">{item.label}</span>
                              <span className="font-medium text-gray-900">₹{item.amount.toLocaleString('en-IN')}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  )}

                  {/* Step 4: Ask to Approve */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center">
                          <CheckCircle className="h-5 w-5 text-yellow-600 mr-2" />
                          <span className="text-sm font-medium text-yellow-900">Approve the calculated settlement</span>
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full ml-2">Step 4</span>
                        </div>
                        <div className="text-xs text-yellow-800 mt-1">Review the above breakdown and approve to proceed, or add modifications below.</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          // Guardrails before approval
                          if (!falComputedPreview || !falComputedPreview.totals) {
                            alert('Please compute the settlement first (add items/overrides).');
                            return;
                          }
                          const missing = (falManualAdjustments || []).some(a => (a.amount || a.amount === 0) && !String(a.reason || '').trim());
                          if (missing) { alert('Please provide justification for all adjustments.'); return; }
                          // If copay override enabled, require reason or value
                          if (falOverrides?.copay?.enabled) {
                            const hasOverride = String(falOverrides.copay.amountOverride || falOverrides.copay.percentOverride || '').trim();
                            const hasReason = String(falOverrides.copay.reason || '').trim();
                            if (!hasOverride || !hasReason) {
                              alert('Provide copay override value and reason.');
                              return;
                            }
                          }
                          setIsSettlementApproved(true);
                          // Persist approval for stickiness
                          try {
                            setDischargeSettlementApproval(applicationId, {
                              approved: true,
                              by: user?.uid,
                              byName: user?.profile?.name || user?.email || 'Doctor'
                            });
                          } catch (_) {}
                        }}
                        className={`px-4 py-2 rounded-lg text-white ${isSettlementApproved ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'}`}
                      >
                        {isSettlementApproved ? '✓ Approved' : 'Approve Settlement'}
                      </button>
                    </div>
                  </div>

                  {/* Step 5: Modify Amount (if needed) */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                        <DollarSign className="h-5 w-5 text-green-600 mr-2" />
                        Manual Adjustments (Optional)
                      </h4>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Step 5</span>
                        <button
                          type="button"
                          className="flex items-center space-x-2 px-3 py-2 bg-green-100 text-green-700 border border-green-300 rounded-lg text-sm hover:bg-green-200 transition-colors"
                          onClick={() => setFalManualAdjustments((prev) => ([...prev, { id: Date.now().toString(), label: '', amount: 0, reason: '', mode: 'add' }]))}
                        >
                          <Plus className="h-4 w-4" />
                          <span>Add Adjustment</span>
                        </button>
                      </div>
                    </div>
                    
                    {falManualAdjustments.length === 0 ? (
                      <div className="text-center py-6">
                        <DollarSign className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <div className="text-sm text-gray-600">No manual adjustments added</div>
                        <div className="text-xs text-gray-500 mt-1">Add goodwill gestures, waivers, or other adjustments if needed</div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-start space-x-2">
                            <Info className="h-4 w-4 text-green-600 mt-0.5" />
                            <div className="text-sm text-green-800">
                              <strong>Manual Adjustments:</strong> Select whether to add to or deduct from the total settlement. All adjustments require proper justification.
                            </div>
                          </div>
                        </div>
                        
                        {falManualAdjustments.map((adj) => (
                          <div key={adj.id} className="grid grid-cols-1 lg:grid-cols-6 gap-4 items-end p-4 border border-gray-200 rounded-lg bg-gray-50">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Adjustment Label</label>
                              <input
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                value={adj.label}
                                onChange={(e) => setFalManualAdjustments((prev) => prev.map((x) => x.id === adj.id ? { ...x, label: e.target.value } : x))}
                                placeholder="e.g., Goodwill waiver"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                              <select
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                value={adj.mode || 'add'}
                                onChange={(e) => setFalManualAdjustments((prev) => prev.map((x) => x.id === adj.id ? { ...x, mode: e.target.value } : x))}
                              >
                                <option value="add">Add to total</option>
                                <option value="deduct">Deduct from total</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Amount (₹)</label>
                              <input
                                type="number"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                value={adj.amount}
                                onChange={(e) => setFalManualAdjustments((prev) => prev.map((x) => x.id === adj.id ? { ...x, amount: e.target.value } : x))}
                                placeholder="Amount"
                              />
                            </div>
                            <div className="lg:col-span-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Justification *</label>
                              <input
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                value={adj.reason || ''}
                                onChange={(e) => setFalManualAdjustments((prev) => prev.map((x) => x.id === adj.id ? { ...x, reason: e.target.value } : x))}
                                placeholder="Business justification required"
                              />
                            </div>
                            <div className="flex justify-center">
                              <button
                                type="button"
                                className="px-3 py-2 bg-red-100 text-red-700 border border-red-300 rounded-lg text-sm hover:bg-red-200 transition-colors"
                                onClick={() => setFalManualAdjustments((prev) => prev.filter((x) => x.id !== adj.id))}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Step 6: Final Actions */}
                  {isSettlementApproved && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="text-lg font-semibold text-green-900 flex items-center">
                            <FileText className="h-5 w-5 text-green-600 mr-2" />
                            Final Approval Letter Actions
                          </h4>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Step 6</span>
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Remarks (will appear in FAL)</label>
                        <textarea
                          value={falRemarks}
                          onChange={(e) => setFalRemarks(e.target.value)}
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          placeholder="Any remarks or notes for Final Approval..."
                        />
                      </div>

                      <div className="flex flex-wrap items-center gap-3">
                        {/* Generate FAL */}
                        {!isFALPublished && (
                          <button
                            onClick={async () => {
                              // Only require at least one discharge item
                              if (!Array.isArray(dischargeItems) || dischargeItems.length === 0) {
                                alert('Please add discharge summary items first.');
                                return;
                              }
                              
                              // CRITICAL FIX: Always save the latest discharge items before generating FAL
                              // This ensures FAL PDF uses updated content when items are added after approval
                              try {
                                await saveDischargeDraft(
                                  applicationId,
                                  user?.uid,
                                  { 
                                    items: dischargeItems, 
                                    comments: dischargeComments, 
                                    uploadedFiles: dischargeFiles, 
                                    falOverrides,
                                    falManualAdjustments,
                                    falComputedPreview,
                                    autoSaved: false 
                                  }
                                );
                              } catch (e) {
                                console.warn('Failed to save draft before FAL generation:', e);
                              }
                              
                              // Optional: persist approval for convenience (non-blocking)
                              try {
                                await setDischargeSettlementApproval(applicationId, {
                                  approved: true,
                                  by: user?.uid,
                                  byName: user?.profile?.name || user?.email || 'Doctor'
                                });
                              } catch (_) {}
                              setFalActionLoading((p) => ({ ...p, generate: true }));
                              try {
                                const overridesWithMeta = {
                                  ...falOverrides,
                                  copay: falOverrides?.copay?.enabled ? { ...falOverrides.copay, by: user?.uid, at: new Date().toISOString() } : undefined,
                                  copaySelection: falOverrides.copaySelection,
                                  sublimitSelection: falOverrides.sublimitSelection,
                                  sublimits: Array.isArray(falOverrides?.sublimits) ? falOverrides.sublimits.map((s) => ({ ...s, by: user?.uid, at: new Date().toISOString() })) : [],
                                };
                                const computeInput = {
                                  policy: application?.workflow?.policyVerification,
                                  dischargeItems,
                                  overrides: overridesWithMeta,
                                  manualAdjustments: falManualAdjustments,
                                  rounding: 'nearest',
                                };
                                const computed = computeFalSettlement(application, computeInput);

                                // CRITICAL FIX: Save computed settlement to workflow before generating letter
                                // This ensures the PDF generation reads the latest computed values
                                await saveComputedDischargeSettlement(applicationId, computed, {
                                  by: user?.uid,
                                  manualAdjustments: falManualAdjustments,
                                });

                                try {
                                  await setDischargeSettlementComments(applicationId, falRemarks);
                                } catch {}
                                
                                // CRITICAL FIX: Pass current discharge items as override to ensure PDF uses latest data
                                const res = await generateLetter(applicationId, 'approval', { 
                                  publishToWorkflowQuickLink: false, 
                                  dischargeItemsOverride: dischargeItems 
                                });
                                if (!res.success) throw new Error(res.error || 'Failed to generate');
                                setFalLetter({
                                  id: res.letterId,
                                  type: 'approval',
                                  title: 'Final Approval Letter',
                                  url: res.letterUrl,
                                  fileName: res.fileName,
                                  generatedAt: new Date(),
                                  generatedBy: user?.profile?.name || 'Doctor',
                                  hospitalAcknowledgement: { status: 'pending' }
                                });
                                alert('FAL generated successfully with latest items. You can now preview and approve it.');
                              } catch (e) {
                                alert(e.message || 'Failed to generate FAL');
                              } finally {
                                setFalActionLoading((p) => ({ ...p, generate: false }));
                              }
                            }}
                            disabled={falActionLoading.generate}
                            className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center space-x-2"
                          >
                            {falActionLoading.generate ? <Loader className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                            <span>Generate FAL</span>
                          </button>
                        )}

                        {/* Preview FAL */}
                        <button
                          onClick={() => {
                            if (!falLetter?.url && !application.workflow?.dischargeSettlement?.finalApprovalLetterUrl) {
                              alert('Please generate FAL first');
                              return;
                            }
                            setShowFALPreview(true);
                          }}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
                        >
                          <Eye className="h-4 w-4" />
                          <span>Preview FAL</span>
                        </button>

                        {/* Approve FAL */}
                        {!isFALPublished && falLetter?.id && (
                          <button
                            onClick={async () => {
                              // Require remarks before publishing to hospital
                              if (!String(falRemarks || '').trim()) {
                                const proceed = window.confirm('Remarks are empty. Do you want to proceed without remarks?');
                                if (!proceed) return;
                              }
                              setFalActionLoading((p) => ({ ...p, approve: true }));
                              try {
                                const res = await setSanctionApproval(applicationId, falLetter.id, {
                                  status: 'approved',
                                  reason: falRemarks,
                                  doctorId: user?.uid,
                                  doctorName: user?.profile?.name
                                });
                                if (!res.success) throw new Error(res.error || 'Failed');
                                alert('FAL approved and published to hospital');
                                await loadApplicationData();
                              } catch (e) {
                                alert(e.message || 'Failed to approve FAL');
                              } finally {
                                setFalActionLoading((p) => ({ ...p, approve: false }));
                              }
                            }}
                            disabled={falActionLoading.approve}
                            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                          >
                            {falActionLoading.approve ? <Loader className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                            <span>Approve & Publish FAL</span>
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Save Draft Actions */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={async () => {
                        try {
                          setActionLoading((p) => ({ ...p, draft: true }));
                          const res = await saveDischargeDraft(
                            applicationId,
                            user?.uid,
                            { 
                              items: dischargeItems, 
                              comments: dischargeComments, 
                              uploadedFiles: dischargeFiles, 
                              falOverrides,
                              falManualAdjustments,
                              falComputedPreview,
                              autoSaved: false 
                            }
                          );
                          if (!res?.success) throw new Error(res?.error || 'Failed to save draft');
                          setLastAutoSaveAt(new Date());
                          alert('Draft saved successfully. Other doctors can now see your work.');
                        } catch (e) {
                          alert(e.message || 'Failed to save draft');
                        } finally {
                          setActionLoading((p) => ({ ...p, draft: false }));
                        }
                      }}
                      disabled={actionLoading.draft || isFALPublished}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading.draft ? 'Saving…' : 'Save Draft'}
                    </button>
                  </div>
                  {lastAutoSaveAt && (
                    <div className="mt-2 text-right text-xs text-gray-500">Last saved at {lastAutoSaveAt.toLocaleTimeString()}</div>
                  )}
                </div>
              )}
            </div>
            {/* Review Attachments (moved under left content) */}
    {dischargeFiles.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">Review Attachments</h4>
                <div className="space-y-3">
                  {dischargeFiles.map((file, index) => (
                    <div key={index} className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          <File className="h-5 w-5 text-blue-500" />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{file.fileName}</p>
                            <p className="text-xs text-gray-500">Size: {(file.size / 1024).toFixed(1)} KB</p>
                            <div className="mt-2">
                              <input
                                type="text"
                                placeholder="Add description for this file..."
                                value={file.description || ""}
            onChange={(e) => { if (!isFALPublished) updateFileDescription(index, e.target.value); }}
                                className="w-full px-3 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
            disabled={isFALPublished}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button onClick={() => window.open(file.url, "_blank")} className="text-blue-600 hover:text-blue-800 p-1" title="View File">
                            <Eye className="h-4 w-4" />
                          </button>
          <button onClick={() => { if (!isFALPublished) removeDischargeFile(index); }} disabled={isFALPublished} className="text-red-600 hover:text-red-800 p-1 disabled:opacity-50" title="Remove File">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Discharge Summary Information */}
        {dischargeSummary && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Original Discharge Summary
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-600">Discharge Date:</span>
                  <p className="font-medium">{dischargeSummary.dischargeDate}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">
                    Total Hospital Bill:
                  </span>
                  <p className="font-medium text-teal-600">
                    ₹{dischargeSummary.totalBill?.toLocaleString("en-IN")}
                  </p>
                </div>
              </div>

              {dischargeSummary.summary && (
                <div>
                  <span className="text-sm text-gray-600">
                    Discharge Summary:
                  </span>
                  <p className="text-gray-900 mt-1">{dischargeSummary.summary}</p>
                </div>
              )}

              {dischargeSummary.documents &&
                dischargeSummary.documents.length > 0 && (
                  <div>
                    <span className="text-sm text-gray-600 block mb-2">
                      Discharge Documents:
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {dischargeSummary.documents.map((doc, index) => (
                        <button
                          key={index}
                          onClick={() => window.open(doc.url, "_blank")}
                          className="flex items-center space-x-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          <FileText className="h-4 w-4" />
                          <span className="text-sm">{doc.fileName}</span>
                          <Eye className="h-3 w-3" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Final Approval Section (after Discharge Summary) */}
        {application?.workflow?.medicalVerification?.dischargeReviewCompleted && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Final Approval (FAL)</h3>
              {falLetter?.hospitalAcknowledgement && (
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${falLetter.hospitalAcknowledgement.status === 'accepted' ? 'bg-green-100 text-green-700' : falLetter.hospitalAcknowledgement.status === 'challenged' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700'}`}>
                  Hospital: {falLetter.hospitalAcknowledgement.status}
                </span>
              )}
            </div>

            {/* Amount Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="text-sm text-gray-600">Doctor Approved Amount</div>
                <div className="text-2xl font-bold text-teal-700">₹{(application.workflow?.medicalVerification?.finalApprovedAmount || 0).toLocaleString('en-IN')}</div>
              </div>
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="text-sm text-gray-600">Finance Final Settlement</div>
                <div className="text-2xl font-bold text-blue-700">₹{(((application.workflow?.dischargeSettlement?.finalSettlementAmount) ?? (application.workflow?.medicalVerification?.finalApprovedAmount)) || 0).toLocaleString('en-IN')}</div>
              </div>
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="text-sm text-gray-600">Variance</div>
                <div className="text-2xl font-bold text-gray-800">₹{Math.abs(((((application.workflow?.dischargeSettlement?.finalSettlementAmount) ?? (application.workflow?.medicalVerification?.finalApprovedAmount)) || 0) - (application.workflow?.medicalVerification?.finalApprovedAmount || 0))).toLocaleString('en-IN')}</div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Remarks (will appear in FAL)</label>
              <textarea
                value={falRemarks}
                onChange={(e) => setFalRemarks(e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Any remarks or notes for Final Approval..."
              />
            </div>

            {/* Settlement Controls: Enhanced UI for better user experience */}
            <div className="mb-6 space-y-6">
              {/* Settlement Summary Card */}
              {falComputedPreview && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Calculator className="h-5 w-5 text-blue-600 mr-2" />
                      Settlement Calculation
                    </h4>
                    <div className="text-sm text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                      Live Preview
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">₹{(falComputedPreview.totals?.billed || 0).toLocaleString('en-IN')}</div>
                      <div className="text-xs text-gray-600">Total Billed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-700">₹{(falComputedPreview.totals?.eligible || 0).toLocaleString('en-IN')}</div>
                      <div className="text-xs text-gray-600">Eligible Amount</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">₹{((falComputedPreview.totals?.nonAdmissibles || 0) + (falComputedPreview.totals?.proportionate || 0) + (falComputedPreview.totals?.sublimits || 0) + (falComputedPreview.totals?.deductible || 0) + (falComputedPreview.totals?.copay || 0)).toLocaleString('en-IN')}</div>
                      <div className="text-xs text-gray-600">Total Deductions</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-teal-700">₹{(falComputedPreview.totals?.finalSettlement || 0).toLocaleString('en-IN')}</div>
                      <div className="text-xs text-gray-600">Final Settlement</div>
                    </div>
                  </div>

                  {/* Detailed Breakdown Toggle */}
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm font-medium text-blue-700 hover:text-blue-800">
                      View Detailed Breakdown
                    </summary>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {falComputedPreview.breakdown?.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center py-1 border-b border-blue-100">
                          <span className="text-gray-700">{item.label}</span>
                          <span className="font-medium text-gray-900">₹{item.amount.toLocaleString('en-IN')}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              )}

              {/* Co-pay Selection: Show Only Policy-Configured Copays */}
              {(() => {
                const cp = application?.workflow?.policyVerification?.coPayStructure;
                const applicableCopays = [];
                
                if (cp?.generalCoPayApplicable && cp.generalCoPayValue > 0) {
                  applicableCopays.push({
                    type: 'general',
                    label: 'General Co-pay',
                    percent: cp.generalCoPayValue,
                    description: 'Standard co-payment as per policy terms'
                  });
                }
                
                if (cp?.nonNetworkCoPayApplicable && cp.nonNetworkCoPayValue > 0) {
                  applicableCopays.push({
                    type: 'nonNetwork',
                    label: 'Non-Network Hospital Co-pay',
                    percent: cp.nonNetworkCoPayValue,
                    description: 'Additional co-pay for non-network hospitals'
                  });
                }
                
                if (cp?.locationBasedCoPayApplicable) {
                  if (cp.metroCoPayValue > 0) {
                    applicableCopays.push({
                      type: 'locationMetro',
                      label: 'Metro Location Co-pay',
                      percent: cp.metroCoPayValue,
                      description: 'Location-based co-pay for metro cities'
                    });
                  }
                  if (cp.nonMetroCoPayValue > 0) {
                    applicableCopays.push({
                      type: 'locationNonMetro',
                      label: 'Non-Metro Location Co-pay',
                      percent: cp.nonMetroCoPayValue,
                      description: 'Location-based co-pay for non-metro areas'
                    });
                  }
                }
                
                if (cp?.parentalCoPayApplicable && cp.parentalCoPayValue > 0) {
                  applicableCopays.push({
                    type: 'parental',
                    label: `Parental Co-pay (Age ${cp.parentalCoPayAgeThreshold || '60'}+)`,
                    percent: cp.parentalCoPayValue,
                    description: 'Age-based co-payment for elderly patients'
                  });
                }
                
                if (cp?.mandatoryCoPayApplicable && cp.mandatoryCoPayValue > 0) {
                  applicableCopays.push({
                    type: 'mandatory',
                    label: 'Mandatory Co-pay',
                    percent: cp.mandatoryCoPayValue,
                    description: 'Compulsory co-payment as per policy'
                  });
                }
                
                if (cp?.voluntaryCoPayApplicable && cp.voluntaryCoPayValue > 0) {
                  applicableCopays.push({
                    type: 'voluntary',
                    label: 'Voluntary Co-pay',
                    percent: cp.voluntaryCoPayValue,
                    description: 'Optional co-payment for premium reduction'
                  });
                }
                
                if (cp?.nonIntimationCoPayApplicable && cp.nonIntimationCoPayValue > 0) {
                  applicableCopays.push({
                    type: 'nonIntimation',
                    label: 'Non-Intimation Co-pay',
                    percent: cp.nonIntimationCoPayValue,
                    description: 'Penalty co-pay for non-intimation of claim'
                  });
                }

                return applicableCopays.length > 0 ? (
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                        <Percent className="h-5 w-5 text-purple-600 mr-2" />
                        Co-pay Selection
                      </h4>
                      <div className="text-sm text-gray-600">
                        {applicableCopays.filter(copay => falOverrides.copaySelection?.[copay.type]).length} of {applicableCopays.length} selected
                      </div>
                    </div>
                    
                    <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <Info className="h-4 w-4 text-purple-600 mt-0.5" />
                        <div className="text-sm text-purple-800">
                          <strong>Co-pay Rules:</strong> Select which co-pays apply to this claim. The system will add all selected applicable co-pay percentages together. You can also override the final co-pay amount below if needed.
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {applicableCopays.map((copay) => (
                        <div key={copay.type} className={`border rounded-lg p-4 transition-all ${
                          falOverrides.copaySelection?.[copay.type] 
                            ? 'border-purple-300 bg-purple-50' 
                            : 'border-gray-200 bg-gray-50'
                        }`}>
                          <div className="flex items-start space-x-3">
                            <input
                              type="checkbox"
                              id={`copay-${copay.type}`}
                              checked={falOverrides.copaySelection?.[copay.type] || false}
                              onChange={(e) => setFalOverrides(prev => ({
                                ...prev,
                                copaySelection: {
                                  ...prev.copaySelection,
                                  [copay.type]: e.target.checked
                                }
                              }))}
                              className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                            />
                            <div className="flex-1">
                              <label htmlFor={`copay-${copay.type}`} className="block text-sm font-medium text-gray-900 cursor-pointer">
                                {copay.label}
                              </label>
                              <div className="text-lg font-bold text-purple-700 mt-1">
                                {copay.percent}%
                              </div>
                              <p className="text-xs text-gray-600 mt-1">
                                {copay.description}
                              </p>
                              {falOverrides.copaySelection?.[copay.type] && falComputedPreview?.totals?.payableBeforeCopay && (
                                <div className="text-xs text-purple-700 mt-2 font-medium">
                                  Amount: ₹{(falComputedPreview.totals.payableBeforeCopay * copay.percent / 100).toLocaleString('en-IN')}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Global Co-pay Override */}
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-3 mb-3">
                        <input
                          type="checkbox"
                          id="copay-override"
                          checked={!!falOverrides.copay?.enabled}
                          onChange={(e) => setFalOverrides((prev) => ({
                            ...prev,
                            copay: { ...(prev.copay || {}), enabled: e.target.checked }
                          }))}
                          className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                        />
                        <label htmlFor="copay-override" className="text-sm font-medium text-gray-700">
                          Override Final Co-pay Amount
                        </label>
                      </div>
                      
                      {falOverrides.copay?.enabled && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Fixed Amount (₹)</label>
                            <input
                              type="number"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                              value={falOverrides.copay?.amountOverride || ''}
                              onChange={(e) => setFalOverrides((prev) => ({
                                ...prev,
                                copay: { ...(prev.copay || {}), amountOverride: e.target.value, percentOverride: '' }
                              }))}
                              placeholder="e.g., 5000"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Percentage Override (%)</label>
                            <input
                              type="number"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                              value={falOverrides.copay?.percentOverride || ''}
                              onChange={(e) => setFalOverrides((prev) => ({
                                ...prev,
                                copay: { ...(prev.copay || {}), percentOverride: e.target.value, amountOverride: '' }
                              }))}
                              placeholder="e.g., 15"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Reason for Override</label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                              value={falOverrides.copay?.reason || ''}
                              onChange={(e) => setFalOverrides((prev) => ({
                                ...prev,
                                copay: { ...(prev.copay || {}), reason: e.target.value }
                              }))}
                              placeholder="Reason for override"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
                    <Percent className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <div className="text-sm text-gray-600">No co-pays configured in policy verification</div>
                  </div>
                );
              })()}

              {/* Sublimit Overrides: Enhanced UI */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
                  <Shield className="h-5 w-5 text-indigo-600 mr-2" />
                  Sublimit Adjustments
                </h4>
                
                {Array.isArray(application?.workflow?.policyVerification?.deductiblesAndSublimits?.ailmentProcedureSublimits) && application.workflow.policyVerification.deductiblesAndSublimits.ailmentProcedureSublimits.length > 0 ? (
                  <div className="space-y-4">
                    <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <Info className="h-4 w-4 text-indigo-600 mt-0.5" />
                        <div className="text-sm text-indigo-800">
                          <strong>Sublimit Rules:</strong> These are the ailment/procedure-specific limits configured by the policy team. You can override the deduction amount if clinically justified.
                        </div>
                      </div>
                    </div>
                    
                    {application.workflow.policyVerification.deductiblesAndSublimits.ailmentProcedureSublimits.map((sl, idx) => {
                      const key = `sublimit_${idx}`;
                      const existing = (falOverrides.sublimits || []).find((s) => s.key === key) || null;
                      const hasOverride = !!existing?.amountOverride;
                      
                      return (
                        <div key={key} className={`border rounded-lg p-4 transition-all ${
                          hasOverride ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-gray-50'
                        }`}>
                          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start">
                            <div className="lg:col-span-1">
                              <div className="text-sm font-medium text-gray-900 mb-1">
                                {sl.ailmentProcedure || `Sublimit #${idx + 1}`}
                              </div>
                              <div className="text-xs text-gray-600">
                                Limit: ₹{(parseFloat(sl.sublimitAmount) || 0).toLocaleString('en-IN')}
                              </div>
                              {sl.description && (
                                <div className="text-xs text-gray-500 mt-1">{sl.description}</div>
                              )}
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Override Deduction (₹)</label>
                              <input
                                type="number"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                value={existing?.amountOverride || ''}
                                onChange={(e) => setFalOverrides((prev) => {
                                  const list = Array.isArray(prev.sublimits) ? [...prev.sublimits] : [];
                                  const idxFound = list.findIndex((x) => x.key === key);
                                  const updated = { key, amountOverride: e.target.value, reason: existing?.reason || '' };
                                  if (idxFound >= 0) list[idxFound] = updated; else list.push(updated);
                                  return { ...prev, sublimits: list };
                                })}
                                placeholder="Override amount"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Clinical Justification</label>
                              <input
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                value={existing?.reason || ''}
                                onChange={(e) => setFalOverrides((prev) => {
                                  const list = Array.isArray(prev.sublimits) ? [...prev.sublimits] : [];
                                  const idxFound = list.findIndex((x) => x.key === key);
                                  const updated = { key, amountOverride: existing?.amountOverride || '', reason: e.target.value };
                                  if (idxFound >= 0) list[idxFound] = updated; else list.push(updated);
                                  return { ...prev, sublimits: list };
                                })}
                                placeholder="Clinical reason for override"
                              />
                            </div>
                            
                            <div className="flex justify-center">
                              {hasOverride ? (
                                <button
                                  type="button"
                                  className="px-3 py-2 bg-red-100 text-red-700 border border-red-300 rounded-lg text-sm hover:bg-red-200 transition-colors"
                                  onClick={() => setFalOverrides((prev) => ({
                                    ...prev,
                                    sublimits: (prev.sublimits || []).filter((s) => s.key !== key)
                                  }))}
                                >
                                  Remove Override
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="px-3 py-2 bg-indigo-100 text-indigo-700 border border-indigo-300 rounded-lg text-sm hover:bg-indigo-200 transition-colors"
                                  onClick={() => setFalOverrides((prev) => ({
                                    ...prev,
                                    sublimits: [ ...(prev.sublimits || []), { key, amountOverride: '', reason: '' } ]
                                  }))}
                                >
                                  Add Override
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Shield className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <div className="text-sm text-gray-600">No sublimits configured in policy verification</div>
                  </div>
                )}
              </div>

              {/* Manual Adjustments: Enhanced UI */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                    <DollarSign className="h-5 w-5 text-green-600 mr-2" />
                    Manual Adjustments
                  </h4>
                  <button
                    type="button"
                    className="flex items-center space-x-2 px-3 py-2 bg-green-100 text-green-700 border border-green-300 rounded-lg text-sm hover:bg-green-200 transition-colors"
                    onClick={() => setFalManualAdjustments((prev) => ([...prev, { id: Date.now().toString(), label: '', amount: 0, reason: '', mode: 'add' }]))}
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add Adjustment</span>
                  </button>
                </div>
                
                {falManualAdjustments.length === 0 ? (
                  <div className="text-center py-6">
                    <DollarSign className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <div className="text-sm text-gray-600">No manual adjustments added</div>
                    <div className="text-xs text-gray-500 mt-1">Add goodwill gestures, waivers, or other adjustments</div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <Info className="h-4 w-4 text-green-600 mt-0.5" />
                        <div className="text-sm text-green-800">
                          <strong>Manual Adjustments:</strong> Use positive amounts to add to settlement, negative amounts for waivers or deductions. All adjustments require proper justification.
                        </div>
                      </div>
                    </div>
                    
                    {falManualAdjustments.map((adj) => (
                      <div key={adj.id} className="grid grid-cols-1 lg:grid-cols-6 gap-4 items-end p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Adjustment Label</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            value={adj.label}
                            onChange={(e) => setFalManualAdjustments((prev) => prev.map((x) => x.id === adj.id ? { ...x, label: e.target.value } : x))}
                            placeholder="e.g., Goodwill waiver"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            value={adj.mode || 'add'}
                            onChange={(e) => setFalManualAdjustments((prev) => prev.map((x) => x.id === adj.id ? { ...x, mode: e.target.value } : x))}
                          >
                            <option value="add">Add to total</option>
                            <option value="deduct">Deduct from total</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Amount (₹)</label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            value={adj.amount}
                            onChange={(e) => setFalManualAdjustments((prev) => prev.map((x) => x.id === adj.id ? { ...x, amount: e.target.value } : x))}
                            placeholder="e.g., -2000 for waiver"
                          />
                        </div>
                        <div className="lg:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Justification</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            value={adj.reason || ''}
                            onChange={(e) => setFalManualAdjustments((prev) => prev.map((x) => x.id === adj.id ? { ...x, reason: e.target.value } : x))}
                            placeholder="Business justification for adjustment"
                          />
                        </div>
                        <div className="flex justify-center">
                          <button
                            type="button"
                            className="px-3 py-2 bg-red-100 text-red-700 border border-red-300 rounded-lg text-sm hover:bg-red-200 transition-colors"
                            onClick={() => setFalManualAdjustments((prev) => prev.filter((x) => x.id !== adj.id))}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Approve Calculation Step */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-blue-900">Approve the calculated settlement</div>
                  <div className="text-xs text-blue-800">Lock copay/sublimits/adjustments to proceed with letter generation. Any change will un-approve and require re-approval.</div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // Require reasons on all adjustments
                    const missing = (falManualAdjustments || []).some(a => (a.amount || a.amount === 0) && !String(a.reason || '').trim());
                    if (missing) { alert('Please provide justification for all adjustments.'); return; }
                    setIsSettlementApproved(true);
                  }}
                  className={`px-4 py-2 rounded-lg text-white ${isSettlementApproved ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  {isSettlementApproved ? 'Approved' : 'Approve Calculation'}
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Generate FAL (draft) */}
              {!isFALPublished && Array.isArray(dischargeItems) && dischargeItems.length > 0 && (
              <button
                onClick={async () => {
                  // Only require non-empty items list
                  if (!Array.isArray(dischargeItems) || dischargeItems.length === 0) {
                    alert('Please add discharge summary items first.');
                    return;
                  }
                  
                  // CRITICAL FIX: Save draft before generating FAL
                  try {
                    await saveDischargeDraft(
                      applicationId,
                      user?.uid,
                      { 
                        items: dischargeItems, 
                        comments: dischargeComments, 
                        uploadedFiles: dischargeFiles, 
                        falOverrides,
                        falManualAdjustments,
                        falComputedPreview,
                        autoSaved: false 
                      }
                    );
                  } catch (e) {
                    console.warn('Failed to save draft before FAL generation:', e);
                  }
                  
                  // No strict amount/approval gating
                  setFalActionLoading((p) => ({ ...p, generate: true }));
                  try {
                      // 1) Compute settlement with robust engine
                      const overridesWithMeta = {
                        ...falOverrides,
                        copay: falOverrides?.copay?.enabled ? { ...falOverrides.copay, by: user?.uid, at: new Date().toISOString() } : undefined,
                        copaySelection: falOverrides.copaySelection,
                        sublimitSelection: falOverrides.sublimitSelection,
                        sublimits: Array.isArray(falOverrides?.sublimits) ? falOverrides.sublimits.map((s) => ({ ...s, by: user?.uid, at: new Date().toISOString() })) : [],
                      };
                      const computeInput = {
                        policy: application?.workflow?.policyVerification,
                        dischargeItems,
                        overrides: overridesWithMeta,
                        manualAdjustments: falManualAdjustments,
                        rounding: 'nearest',
                      };
                      const computed = computeFalSettlement(application, computeInput);

                      // 2) Persist computed numbers so FAL PDFs and preview reflect them
                      await saveComputedDischargeSettlement(applicationId, computed, {
                        by: user?.uid,
                        manualAdjustments: falManualAdjustments,
                      });

                    // Save remarks into settlement comments before generation so PDF reflects it
                    try {
                      await setDischargeSettlementComments(applicationId, falRemarks);
                    } catch {}
                    
                    // CRITICAL FIX: Pass current discharge items as override
                    const res = await generateLetter(applicationId, 'approval', { 
                      publishToWorkflowQuickLink: false, 
                      dischargeItemsOverride: dischargeItems 
                    });
                    if (!res.success) throw new Error(res.error || 'Failed to generate');
                    setFalLetter({
                      id: res.letterId,
                      type: 'approval',
                      title: 'Final Approval Letter',
                      url: res.letterUrl,
                      fileName: res.fileName,
                      generatedAt: new Date(),
                      generatedBy: user?.profile?.name || 'Doctor',
                      hospitalAcknowledgement: { status: 'pending' }
                    });
                    alert('FAL generated as draft with latest items. Preview before approving.');
                  } catch (e) {
                    alert(e.message || 'Failed to generate FAL');
                  } finally {
                    setFalActionLoading((p) => ({ ...p, generate: false }));
                  }
                }}
                disabled={falActionLoading.generate}
                className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {falActionLoading.generate ? <Loader className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                <span>Generate FAL (PDF)</span>
              </button>
              )}

              {/* Preview FAL (modal) */}
              <button
                onClick={() => {
                  if (!falLetter?.url && !application.workflow?.dischargeSettlement?.finalApprovalLetterUrl) {
                    alert('Please generate FAL first');
                    return;
                  }
                  // Ensure latest compute saved for preview
                  try {
                    const overridesWithMeta = {
                      ...falOverrides,
                      copay: falOverrides?.copay?.enabled ? { ...falOverrides.copay, by: user?.uid, at: new Date().toISOString() } : undefined,
                      copaySelection: falOverrides.copaySelection,
                      sublimitSelection: falOverrides.sublimitSelection,
                      sublimits: Array.isArray(falOverrides?.sublimits) ? falOverrides.sublimits.map((s) => ({ ...s, by: user?.uid, at: new Date().toISOString() })) : [],
                    };
                    const computeInput = {
                      policy: application?.workflow?.policyVerification,
                      dischargeItems,
                      overrides: overridesWithMeta,
                      manualAdjustments: falManualAdjustments,
                      rounding: 'nearest',
                    };
                    const computed = computeFalSettlement(application, computeInput);
                    saveComputedDischargeSettlement(applicationId, computed, {
                      by: user?.uid,
                      manualAdjustments: falManualAdjustments,
                    });
                  } catch (e) { /* non-blocking */ }
                  setShowFALPreview(true);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
              >
                <Eye className="h-4 w-4" />
                <span>Preview FAL</span>
              </button>

              {/* Approve/Reject only if draft exists and not already published */}
              {!isFALPublished && falLetter?.id && (
                <>
                  <button
                    onClick={async () => {
                      setFalActionLoading((p) => ({ ...p, approve: true }));
                      try {
                        const res = await setSanctionApproval(applicationId, falLetter.id, {
                          status: 'approved',
                          reason: falRemarks,
                          doctorId: user?.uid,
                          doctorName: user?.profile?.name
                        });
                        if (!res.success) throw new Error(res.error || 'Failed');
                        alert('FAL approved and published to hospital');
                        await loadApplicationData();
                      } catch (e) {
                        alert(e.message || 'Failed to approve FAL');
                      } finally {
                        setFalActionLoading((p) => ({ ...p, approve: false }));
                      }
                    }}
                    disabled={falActionLoading.approve}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                  >
                    {falActionLoading.approve ? <Loader className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                    <span>Approve & Publish</span>
                  </button>

                  <button
                    onClick={async () => {
                      const reason = prompt('Reason for rejection?') || '';
                      setFalActionLoading((p) => ({ ...p, reject: true }));
                      try {
                        const res = await setSanctionApproval(applicationId, falLetter.id, {
                          status: 'rejected',
                          reason,
                          doctorId: user?.uid,
                          doctorName: user?.profile?.name
                        });
                        if (!res.success) throw new Error(res.error || 'Failed');
                        alert('FAL rejected.');
                        await loadApplicationData();
                      } catch (e) {
                        alert(e.message || 'Failed to reject FAL');
                      } finally {
                        setFalActionLoading((p) => ({ ...p, reject: false }));
                      }
                    }}
                    disabled={falActionLoading.reject}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    Reject
                  </button>
                </>
              )}

              {/* Revoke Publication (visible only when published) */}
              {isFALPublished && (
                <button
                  onClick={async () => {
                    const confirmed = window.confirm('Revoke published FAL? Hospital access will be removed.');
                    if (!confirmed) return;
                    const reason = prompt('Reason for revocation (optional):') || '';
                    setFalActionLoading((p) => ({ ...p, revoke: true }));
                    try {
                      const res = await revokeFalPublication(applicationId, { reason, doctorId: user?.uid, doctorName: user?.profile?.name });
                      if (!res.success) throw new Error(res.error || 'Failed');
                      alert('FAL publication revoked. You can regenerate and republish after fixing.');
                      await loadApplicationData();
                    } catch (e) {
                      alert(e.message || 'Failed to revoke FAL');
                    } finally {
                      setFalActionLoading((p) => ({ ...p, revoke: false }));
                    }
                  }}
                  disabled={falActionLoading.revoke}
                  className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {falActionLoading.revoke ? <Loader className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  <span>Revoke Publication</span>
                </button>
              )}
            </div>

            {/* Hospital challenge banner (if any) */}
            {falLetter?.hospitalAcknowledgement?.status === 'challenged' && (
              <div className="mt-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-orange-900">Hospital challenged the FAL</span>
                    </div>
                    {falLetter.hospitalAcknowledgement?.comment && (
                      <p className="text-sm text-orange-800">Comment: {falLetter.hospitalAcknowledgement.comment}</p>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        onClick={async () => {
                          setFalActionLoading((p) => ({ ...p, respond: true }));
                          try {
                            const res = await respondToSanctionChallenge(applicationId, falLetter.id, { decision: 'accept_revision', comment: '', doctorId: user?.uid, doctorName: user?.profile?.name });
                            if (!res.success) throw new Error(res.error || 'Failed');
                            alert('Marked to accept and revise. You can regenerate and re-approve.');
                            await loadApplicationData();
                          } catch (e) {
                            alert(e.message || 'Failed to respond');
                          } finally {
                            setFalActionLoading((p) => ({ ...p, respond: false }));
                          }
                        }}
                        className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                      >
                        Accept & Revise
                      </button>
                      <button
                        onClick={async () => {
                          const comment = prompt('Add a comment (optional)') || '';
                          setFalActionLoading((p) => ({ ...p, respond2: true }));
                          try {
                            const res = await respondToSanctionChallenge(applicationId, falLetter.id, { decision: 'not_accept', comment, doctorId: user?.uid, doctorName: user?.profile?.name });
                            if (!res.success) throw new Error(res.error || 'Failed');
                            alert('Hospital challenge not accepted');
                            await loadApplicationData();
                          } catch (e) {
                            alert(e.message || 'Failed to respond');
                          } finally {
                            setFalActionLoading((p) => ({ ...p, respond2: false }));
                          }
                        }}
                        className="text-xs bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700"
                      >
                        Not Accept
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderStatusTimeline = () => {
    const items = timelineEvents || [];
    const getIconForType = (type) => {
      const mapping = {
        submission: FileText,
        approved_for_process: FastForward,
        medical_review: Stethoscope,
        policy_review: Shield,
        financial_review: DollarSign,
        kyc_verification: User,
        status_change: Activity,
        dispute_raised: AlertTriangle,
        acknowledged: Activity,
        under_review: Activity,
        dispute_resolved: CheckCircle,
        doc_query_open: Paperclip,
        doc_query_resolved: CheckCircle,
        default: Activity,
      };
      return mapping[type] || mapping.default;
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Application Status Timeline
          </h2>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(
              application.status
            )}`}
          >
            {application.status?.replace("_", " ").charAt(0).toUpperCase() +
              application.status?.replace("_", " ").slice(1)}
          </span>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="space-y-6">
            {items.length === 0 && (
              <div className="text-center text-sm text-gray-500">No timeline events yet.</div>
            )}
            {items.map((item, index) => {
              const Icon = getIconForType(item.type);
              const isCompleted = !!item.completed;
              const isActive = !isCompleted && index === items.findIndex((s) => !s.completed);

              return (
                <div key={`${item.id || item.type}-${index}`} className="relative">
                  {index < items.length - 1 && (
                    <div
                      className={`absolute left-4 top-8 w-0.5 h-12 ${isCompleted ? "bg-green-300" : "bg-gray-200"}`}
                    />
                  )}

                  <div className="flex items-start space-x-4">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${isCompleted
                        ? "bg-green-100 border-green-500"
                        : isActive
                          ? "bg-yellow-100 border-yellow-500"
                          : "bg-gray-100 border-gray-300"
                        }`}
                    >
                      <Icon
                        className={`h-4 w-4 ${isCompleted
                          ? "text-green-600"
                          : isActive
                            ? "text-yellow-600"
                            : "text-gray-400"
                          }`}
                      />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3
                          className={`font-medium ${isCompleted
                            ? "text-gray-900"
                            : isActive
                              ? "text-gray-900"
                              : "text-gray-500"
                            }`}
                        >
                          {item.title}
                        </h3>
                        {item.timestamp && (
                          <span className="text-sm text-gray-500">
                            {formatDate(item.timestamp)}
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <p
                        className={`text-sm ${isCompleted
                          ? "text-gray-600"
                          : isActive
                            ? "text-gray-600"
                            : "text-gray-400"
                          }`}
                      >
                          {item.description}
                        </p>
                      )}

                      {/* Actor / Team */}
                      {item.actor?.name && (
                        <div className="mt-2 text-xs text-gray-600">
                          <span className="px-2 py-0.5 rounded-full bg-gray-100 border text-gray-700">
                            {item.actor.name}{item.actor.team ? ` • ${item.actor.team}` : ''}
                          </span>
                        </div>
                      )}

                      {/* Attachments */}
                      {Array.isArray(item.attachments) && item.attachments.length > 0 && (
                        <div className="mt-2 flex gap-2 flex-wrap">
                          {item.attachments.map((att, i) => (
                            <a
                              key={i}
                              href={att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                            >
                              <Paperclip className="h-3 w-3" /> {att.name || 'Attachment'}
                            </a>
                          ))}
                        </div>
                      )}

                      {/* Workflow comments */}
                      {item.type === "medical_review" &&
                        application.workflow?.medicalVerification?.comments && (
                          <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                            <p className="text-sm text-green-800">
                              {
                                application.workflow.medicalVerification
                                  .comments
                              }
                            </p>
                          </div>
                        )}

                      {/* Approval for process comments */}
                      {item.type === "approved_for_process" &&
                        application.workflow?.medicalVerification?.approvalForProcessComments && (
                          <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                            <p className="text-sm text-green-800">
                              {
                                application.workflow.medicalVerification
                                  .approvalForProcessComments
                              }
                            </p>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const handleUnreadCountChange = (count) => {
    setUnreadCount(count);
  };

  const renderDiscussions = () => {
    return (
      <DiscussionComponent
        applicationId={applicationId}
        onUnreadCountChange={handleUnreadCountChange}
      />
    );
  };

  // Collapsible Application Overview for Doctor Review - Exact same as PolicyApplicationDetails
  const renderCollapsibleApplicationOverview = () => (
    <div className="space-y-4">
      {/* Patient Information Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          onClick={() => toggleSection('patient')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <div className="bg-teal-100 p-2 rounded-lg">
              <User className="h-4 w-4 text-teal-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Patient Information</h3>
          </div>
          {collapsedSections.patient ?
            <ChevronRight className="h-5 w-5 text-gray-400" /> :
            <ChevronDown className="h-5 w-5 text-gray-400" />
          }
        </button>

        {!collapsedSections.patient && (
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Patient Name</label>
                <p className="text-gray-900 font-semibold">{application.patient?.name || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Age & Gender</label>
                <p className="text-gray-900">{application.patient?.age || 'N/A'} years, {application.patient?.gender || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Contact</label>
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-3 w-3 text-gray-400" />
                    <p className="text-sm text-gray-900">{application.patient?.phone || 'N/A'}</p>
                  </div>
                  {application.patient?.email && (
                    <div className="flex items-center space-x-2">
                      <Mail className="h-3 w-3 text-gray-400" />
                      <p className="text-sm text-gray-900">{application.patient.email}</p>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Address</label>
                <p className="text-gray-900 text-sm">{application.patient?.address || 'N/A'}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Medical Information Section */}
      {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          onClick={() => toggleSection('medical')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Stethoscope className="h-4 w-4 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Medical Informations</h3>
          </div>
          {collapsedSections.medical ?
            <ChevronRight className="h-5 w-5 text-gray-400" /> :
            <ChevronDown className="h-5 w-5 text-gray-400" />
          }
        </button>

        {!collapsedSections.medical && (
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Diagnosis</label>
                <p className="text-gray-900 bg-blue-50 p-2 rounded">{application.medical?.diagnosis}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Treatment Type</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${application.medical?.treatmentType === "surgical"
                    ? "bg-red-100 text-red-800"
                    : "bg-blue-100 text-blue-800"
                    }`}>
                    {application.medical?.treatmentType?.charAt(0).toUpperCase() +
                      application.medical?.treatmentType?.slice(1)}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Estimated Cost</label>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatAmount(application.medical?.estimatedCost)}
                  </p>
                </div>
              </div>
              {application.medical?.symptoms && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Symptoms</label>
                  <p className="text-gray-900 text-sm">{application.medical.symptoms}</p>
                </div>
              )}
              {application.medical?.duration && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Duration</label>
                  <p className="text-gray-900 text-sm">{application.medical.duration}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div> */}

      {/* Policy Information Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          onClick={() => toggleSection('policy')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <Shield className="h-4 w-4 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Policy Information</h3>
          </div>
          {collapsedSections.policy ?
            <ChevronRight className="h-5 w-5 text-gray-400" /> :
            <ChevronDown className="h-5 w-5 text-gray-400" />
          }
        </button>

        {!collapsedSections.policy && (
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Policy Number</label>
                  <p className="text-gray-900 font-mono text-sm bg-gray-50 p-2 rounded">
                    {application.policy?.number}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Insurance Company</label>
                  <p className="text-gray-900 bg-gray-50 p-2 rounded">
                    {application.policy?.insuranceCompany}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Policy Type</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${application.policy?.type === "corporate"
                    ? "bg-purple-100 text-purple-800"
                    : "bg-blue-100 text-blue-800"
                    }`}>
                    {application.policy?.type?.charAt(0).toUpperCase() +
                      application.policy?.type?.slice(1)}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">TPA</label>
                  <p className="text-gray-900 bg-gray-50 p-2 rounded">
                    {application.policy?.tpaName || 'Inhouse'}
                  </p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Policy Period</label>
                <p className="text-gray-900 bg-gray-50 p-2 rounded">
                  {application.policy?.startDate} to {application.policy?.endDate}
                </p>
              </div>

              {/* Policy Holder Information */}
              <div className="border-t border-gray-100 pt-4 mt-2">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Policy Holder</h4>
                {application.policyHolder?.isSelf ? (
                  <div className="space-y-2">
                    <p className="text-xs inline-flex px-2 py-0.5 rounded-full bg-teal-100 text-teal-800">Self (Patient)</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-500">Name</label>
                        <p className="text-sm text-gray-900">{application.patient?.name || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Contact</label>
                        <p className="text-sm text-gray-900">{application.patient?.phone || '-'}</p>
                      </div>
                      {application.policyHolder?.dob && (
                        <div>
                          <label className="text-xs text-gray-500">Policy Holder DOB</label>
                          <p className="text-sm text-gray-900">{application.policyHolder?.dob || '-'}</p>
                        </div>
                      )}
                      {application.policyHolder?.alternatePhone && (
                        <div>
                          <label className="text-xs text-gray-500">Alternate Mobile</label>
                          <p className="text-sm text-gray-900">{application.policyHolder?.alternatePhone || '-'}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-xs text-gray-500">Email</label>
                        <p className="text-sm text-gray-900">{application.patient?.email || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Address</label>
                        <p className="text-sm text-gray-900 break-words">{application.patient?.address || '-'}</p>
                      </div>
                    </div>
                    {/* KYC (Policy Holder) */}
                    <div className="grid grid-cols-3 gap-4 mt-2">
                      <div>
                        <label className="text-xs text-gray-500">Aadhar</label>
                        <p className="text-sm text-gray-900">{application.policyHolder?.kyc?.aadharNumber || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">PAN</label>
                        <p className="text-sm text-gray-900">{application.policyHolder?.kyc?.panNumber || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Address Proof</label>
                        <p className="text-sm text-gray-900">{application.policyHolder?.kyc?.addressProof || '-'}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-500">Name</label>
                        <p className="text-sm text-gray-900">{application.policyHolder?.name || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Relationship</label>
                        <p className="text-sm text-gray-900">{application.policyHolder?.relationshipWithPatient || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Age/Gender</label>
                        <p className="text-sm text-gray-900">{`${application.policyHolder?.age ?? '-'} / ${application.policyHolder?.gender || '-'}`}</p>
                      </div>
                      {application.policyHolder?.dob && (
                        <div>
                          <label className="text-xs text-gray-500">Policy Holder DOB</label>
                          <p className="text-sm text-gray-900">{application.policyHolder?.dob || '-'}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-xs text-gray-500">Contact</label>
                        <p className="text-sm text-gray-900">{application.policyHolder?.phone || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Email</label>
                        <p className="text-sm text-gray-900">{application.policyHolder?.email || '-'}</p>
                      </div>
                      {application.policyHolder?.alternatePhone && (
                        <div>
                          <label className="text-xs text-gray-500">Alternate Mobile</label>
                          <p className="text-sm text-gray-900">{application.policyHolder?.alternatePhone || '-'}</p>
                        </div>
                      )}
                      <div className="col-span-2">
                        <label className="text-xs text-gray-500">Address</label>
                        <p className="text-sm text-gray-900 break-words">{application.policyHolder?.address || '-'}</p>
                      </div>
                    </div>
                    {/* KYC (Policy Holder) */}
                    <div className="grid grid-cols-3 gap-4 mt-2">
                      <div>
                        <label className="text-xs text-gray-500">Aadhar</label>
                        <p className="text-sm text-gray-900">{application.policyHolder?.kyc?.aadharNumber || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">PAN</label>
                        <p className="text-sm text-gray-900">{application.policyHolder?.kyc?.panNumber || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Address Proof</label>
                        <p className="text-sm text-gray-900">{application.policyHolder?.kyc?.addressProof || '-'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Nominee Information */}
              {(application.policyHolder?.nominee?.name || application.policyHolder?.nominee?.relationshipWithNominee || application.policyHolder?.nominee?.contact) && (
                <div className="border-t border-gray-100 pt-4 mt-2">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Nominee</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs text-gray-500">Name</label>
                      <p className="text-sm text-gray-900">{application.policyHolder?.nominee?.name || '-'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Relationship</label>
                      <p className="text-sm text-gray-900">{application.policyHolder?.nominee?.relationshipWithNominee || '-'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Contact</label>
                      <p className="text-sm text-gray-900">{application.policyHolder?.nominee?.contact || '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Employment Information */}
              {(application.employment?.status || application.employment?.monthlyIncome || application.employment?.workingCompanyName || application.employment?.designation || application.employment?.natureOfWork) && (
                <div className="border-t border-gray-100 pt-4 mt-2">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Employment</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500">Status</label>
                      <p className="text-sm text-gray-900">{application.employment?.status || '-'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Monthly Income</label>
                      <p className="text-sm text-gray-900">{application.employment?.monthlyIncome ? `₹${Number(application.employment.monthlyIncome).toLocaleString('en-IN')}` : '-'}</p>
                    </div>
                    {(application.employment?.status === 'employed') && (
                      <>
                        <div>
                          <label className="text-xs text-gray-500">Company</label>
                          <p className="text-sm text-gray-900">{application.employment?.workingCompanyName || '-'}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Designation</label>
                          <p className="text-sm text-gray-900">{application.employment?.designation || '-'}</p>
                        </div>
                      </>
                    )}
                    {(application.employment?.status === 'self-employed' || application.employment?.status === 'business') && (
                      <div className="col-span-2">
                        <label className="text-xs text-gray-500">Nature of Work</label>
                        <p className="text-sm text-gray-900">{application.employment?.natureOfWork || '-'}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Hospital Information Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          onClick={() => toggleSection('hospital')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <div className="bg-orange-100 p-2 rounded-lg">
              <Building className="h-4 w-4 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Hospital Information</h3>
          </div>
          {collapsedSections.hospital ?
            <ChevronRight className="h-5 w-5 text-gray-400" /> :
            <ChevronDown className="h-5 w-5 text-gray-400" />
          }
        </button>

        {!collapsedSections.hospital && (
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Hospital Name</label>
                <p className="text-gray-900 font-semibold">{application.hospital?.name || application.hospitalName || 'N/A'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Location</label>
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-3 w-3 text-gray-400" />
                    <p className="text-sm text-gray-900">{application.hospital?.location || 'N/A'}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Network</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${application.hospital?.isNetworkHospital
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                    }`}>
                    {application.hospital?.isNetworkHospital ? "Network" : "Non-Network"}
                  </span>
                </div>
              </div>
              {application.hospital?.doctor && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Attending Doctor</label>
                  <p className="text-gray-900">{application.hospital.doctor}</p>
                </div>
              )}

              {/* Hospital Tariff Documents Section */}
              <div className="border-t border-gray-100 pt-4 mt-4">
                <div className="flex items-center space-x-2 mb-3">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <label className="text-sm font-medium text-gray-500">Hospital Tariff Documents</label>
                </div>

                {loadingTariffDocuments && (
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <div className="w-3 h-3 border border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                    <span>Loading tariff documents...</span>
                  </div>
                )}

                {tariffError && (
                  <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                    {tariffError}
                  </div>
                )}

                {tariffDocuments && !loadingTariffDocuments && (
                  <div className="space-y-2">
                    {tariffDocuments.roomTariff && (
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-3 w-3 text-blue-600" />
                          <span className="text-sm text-gray-700">Room Tariff</span>
                          <span className="text-xs text-gray-500">
                            ({tariffDocuments.roomTariff.fileName})
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => openDocument(tariffDocuments.roomTariff)}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                            title="View Room Tariff"
                          >
                            <Eye className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => {
                              downloadFile(
                                tariffDocuments.roomTariff.url,
                                tariffDocuments.roomTariff.fileName
                              );
                            }}
                            className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
                            title="Download Room Tariff"
                          >
                            <Download className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    )}

                    {tariffDocuments.procedureTariff && (
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-3 w-3 text-blue-600" />
                          <span className="text-sm text-gray-700">Procedure Tariff</span>
                          <span className="text-xs text-gray-500">
                            ({tariffDocuments.procedureTariff.fileName})
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => openDocument(tariffDocuments.procedureTariff)}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                            title="View Procedure Tariff"
                          >
                            <Eye className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => {
                              downloadFile(
                                tariffDocuments.procedureTariff.url,
                                tariffDocuments.procedureTariff.fileName
                              );
                            }}
                            className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
                            title="Download Procedure Tariff"
                          >
                            <Download className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    )}

                    {!tariffDocuments.roomTariff && !tariffDocuments.procedureTariff && (
                      <div className="text-sm text-gray-500 bg-gray-50 p-2 rounded">
                        No tariff documents available for this hospital.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Financial Information Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          onClick={() => toggleSection('financial')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <DollarSign className="h-4 w-4 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Financial Information</h3>
          </div>
          {collapsedSections.financial ?
            <ChevronRight className="h-5 w-5 text-gray-400" /> :
            <ChevronDown className="h-5 w-5 text-gray-400" />
          }
        </button>

        {!collapsedSections.financial && (
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Claim Amount</label>
                <p className="text-lg font-semibold text-gray-900">
                  {formatAmount(application.financial?.claimAmount)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Approved Amount</label>
                <p className="text-lg font-semibold text-green-600">
                  {formatAmount(application.financial?.approvedAmount)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Co-pay</label>
                <p className="text-gray-900">
                  {application.financial?.copayPercentage}%
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Deductible</label>
                <p className="text-gray-900">
                  {formatAmount(application.financial?.deductible)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Documents Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          onClick={() => toggleSection('documents')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <FileText className="h-4 w-4 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Documents</h3>
          </div>
          {collapsedSections.documents ?
            <ChevronRight className="h-5 w-5 text-gray-400" /> :
            <ChevronDown className="h-5 w-5 text-gray-400" />
          }
        </button>

        {!collapsedSections.documents && (
          <div className="px-4 pb-4 border-t border-gray-100">
            {application.documents && Object.keys(application.documents).length > 0 ? (
              <div className="space-y-3 mt-4">
                {/* Policy Documents */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Policy Documents</h4>
                  <div className="space-y-2">
                    {Object.entries(application.documents)
                      .filter(([key]) => key.toLowerCase().includes('policy') ||
                        key.toLowerCase().includes('insurance') ||
                        key.toLowerCase().includes('id') ||
                        key.toLowerCase().includes('card'))
                      .map(([key, doc]) => {
                        const queries = documentQueries[key] || [];
                        const openQueries = queries.filter(q => q.status === 'open');
                        return (
                        <div
                          key={key}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <FileText className="h-4 w-4 text-blue-500" />
                            <div>
                              <h5 className="font-medium text-gray-900 text-sm">
                                {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                              </h5>
                              <p className="text-xs text-gray-600">{doc.fileName}</p>
                              {openQueries.length > 0 && (
                                <div className="mt-1">
                                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">
                                    {openQueries.length} open {openQueries.length === 1 ? 'query' : 'queries'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => openQueryModal(key)}
                              className="text-orange-600 hover:text-orange-800 p-1"
                              title="Raise Query"
                            >
                              <HelpCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openDocument(doc)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="View Document"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                downloadFile(doc.url, doc.fileName || key);
                              }}
                              className="text-green-600 hover:text-green-800 p-1"
                              title="Download Document"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Medical Documents */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Medical Documents</h4>
                  <div className="space-y-2">
                    {Object.entries(application.documents)
                      .filter(([key]) => !(key.toLowerCase().includes('policy') ||
                        key.toLowerCase().includes('insurance') ||
                        key.toLowerCase().includes('id') ||
                        key.toLowerCase().includes('card')))
                      .map(([key, doc]) => {
                        const queries = documentQueries[key] || [];
                        const openQueries = queries.filter(q => q.status === 'open');
                        return (
                        <div
                          key={key}
                          className="flex items-center justify-between p-2 bg-white rounded border border-gray-200 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-gray-500" />
                            <div>
                              <span className="text-sm text-gray-700">
                                {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                              </span>
                              {openQueries.length > 0 && (
                                <div className="mt-1">
                                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">
                                    {openQueries.length} open {openQueries.length === 1 ? 'query' : 'queries'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => openQueryModal(key)}
                              className="text-orange-600 hover:text-orange-800 p-1"
                              title="Raise Query"
                            >
                              <HelpCircle className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => openDocument(doc)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="View Document"
                            >
                              <Eye className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => {
                                downloadFile(doc.url, doc.fileName || key);
                              }}
                              className="text-green-600 hover:text-green-800 p-1"
                              title="Download Document"
                            >
                              <Download className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 mt-4">
                <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600">No documents available</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Render disputes management
  const renderDisputes = () => (
    <div className="p-6">
      <DisputeManager
        applicationId={applicationId}
        applicationData={application}
        onDisputeUpdate={handleDisputeUpdate}
        forceDepartment="medical"
      />
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "application":
        return renderApplicationReview();
      case "documents":
        return renderDocuments();
      case "disputes":
        return renderDisputes();
      case "discussions":
        return renderDiscussions();
      case "status":
        return renderStatusTimeline();
      default:
        return renderApplicationReview();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      {/* Header with Timer */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="mx-auto w-[95%] px-2">
          {/* Top Row: Search Bar */}
          <div className="py-3 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-white">
            <div className="max-w-3xl mx-auto px-4">
              <div className="flex items-center justify-center">
                <GlobalSearchBar userRole="doctor" className="w-full shadow-lg" />
              </div>
              <p className="text-center text-xs text-gray-500 mt-2">
                Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono">Ctrl</kbd> + <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono">K</kbd> to focus search or click anywhere to search
              </p>
            </div>
          </div>
          
          {/* Bottom Row: Main Header */}
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleQuitReview}
                disabled={actionLoading.pause}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading.pause ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span>Pausing...</span>
                  </>
                ) : (
                  <>
                    <ArrowLeft className="h-4 w-4" />
                    <span>Pause & Go Back</span>
                  </>
                )}
              </button>

              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Medical Review
                </h1>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-semibold shadow-sm ${caseBadgeConfig.cls}`}>
                    <span className="mr-1">
                      {caseBadgeConfig.icon}
                    </span>
                    {caseBadgeConfig.label}
                  </span>
                  <span>Application: {application.applicationId}</span>
                  <span>•</span>
                  <span>Patient: {application.patient?.name}</span>
                  {isApprovedForProcess && (
                    <>
                      <span>•</span>
                      <span className="text-green-600 font-medium">
                        ✓ Approved for Process
                      </span>
                    </>
                  )}
                  {disputeStatus.hasActiveDisputes && (
                    <>
                      <span>•</span>
                      <span className="text-red-600 font-medium animate-pulse">
                        ⚠️ {disputeStatus.activeDisputes.length} Active Query{disputeStatus.activeDisputes.length > 1 ? 's' : ''}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Timer and Actions */}
            <div className="flex items-center space-x-4">
              {/* Application Rejection Button - keep visible until FAL is accepted by hospital */}
              {(() => {
                const falId = application?.workflow?.dischargeSettlement?.finalApprovalLetterId;
                const falUrl = application?.workflow?.dischargeSettlement?.finalApprovalLetterUrl;
                const ack = falId ? application?.workflow?.acknowledgements?.[falId] : null;
                const falAccepted = Boolean(falUrl && ack?.status === 'accepted');
                const canShowReject = !falAccepted;
                return canShowReject ? (
                <button
                  onClick={async () => {
                    const reason = prompt('Please provide reason for rejecting this application:', '') || '';
                    if (reason.trim()) {
                      const confirmReject = window.confirm(
                        "Are you sure you want to reject this application? This action cannot be undone."
                      );
                      if (confirmReject) {
                        setActionLoading((prev) => ({ ...prev, reject: true }));
                        try {
                          const result = await rejectApplication(applicationId, user.uid, {
                            reason: reason.trim(),
                            doctorName: user.profile?.name || user.email
                          });
                          if (result.success) {
                            alert('Application rejected successfully.');
                            await confirmLeave('/doctor/dashboard');
                          } else {
                            alert("Error rejecting application: " + result.error);
                          }
                        } catch (error) {
                          console.error("Error rejecting application:", error);
                          alert("Failed to reject application");
                        } finally {
                          setActionLoading((prev) => ({ ...prev, reject: false }));
                        }
                      }
                    }
                  }}
                  disabled={actionLoading.reject}
                  className="px-4 py-2 text-white bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                  title="Reject this application"
                >
                  {actionLoading.reject ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Rejecting...</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4" />
                      <span>Reject Application</span>
                    </>
                  )}
                </button>
                ) : null;
              })()}

              <div className="bg-teal-50 border border-teal-200 rounded-lg px-4 py-2">
                <div className="flex items-center space-x-2">
                  <Timer
                    className={`h-5 w-5 ${isTimerActive ? "text-teal-600" : "text-gray-400"
                      }`}
                  />
                  <div className="text-center">
                    <div className="text-lg font-mono font-bold text-gray-900">
                      {formatTime(elapsedTime)}
                    </div>
                    <div className="text-xs text-gray-600">Review Time</div>
                  </div>
                </div>
              </div>

              {application.workflow?.medicalReview?.status === "in_progress" && (
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-green-600 font-medium">
                      Active Review
                    </span>
                  </div>

                  {/* Abandon Review Button */}
                  <button
                    onClick={async () => {
                      const confirmAbandon = window.confirm(
                        "Are you sure you want to completely abandon this review? This will release the application for other doctors to pick up immediately."
                      );
                      if (confirmAbandon) {
                        setActionLoading((prev) => ({ ...prev, quit: true }));
                        try {
                          const result = await quitApplicationReview(applicationId, user.uid);
                          if (result.success) {
                            stopTimer();
                            alert(`Review abandoned successfully. Time spent: ${result.timeSpent} minutes.`);
                            await confirmLeave('/doctor/dashboard');
                          } else {
                            alert("Error abandoning review: " + result.error);
                          }
                        } catch (error) {
                          console.error("Error abandoning review:", error);
                          alert("Failed to abandon review");
                        } finally {
                          setActionLoading((prev) => ({ ...prev, quit: false }));
                        }
                      }
                    }}
                    disabled={actionLoading.quit}
                    className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Completely abandon this review"
                  >
                    {actionLoading.quit ? "Abandoning..." : "Abandon"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Layout - Two Columns with Collapsible Sections */}
      <div className="mx-auto w-[95%] px-2 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" style={{ height: 'calc(100vh - 200px)' }}>
          {/* Left Side - Main Tab Content (60%) */}
          <div className="lg:col-span-3 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 pr-2" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              {/* Tab Navigation */}
              <div className="border-b border-gray-200">
                <div className="px-6">
                  <nav className="flex space-x-8 overflow-x-auto">
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      const isActive = activeTab === tab.id;
                      const isDisabled = tab.disabled;

                      return (
                        <button
                          key={tab.id}
                          onClick={() => !isDisabled && setActiveTab(tab.id)}
                          disabled={isDisabled}
                          className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${isActive
                            ? tab.alert ? "border-red-500 text-red-600" : "border-teal-500 text-teal-600"
                            : isDisabled
                              ? "border-transparent text-gray-400 cursor-not-allowed"
                              : tab.alert
                                ? "border-transparent text-red-500 hover:text-red-700 hover:border-red-300"
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                        >
                          <Icon className={`h-4 w-4 ${tab.alert ? 'animate-pulse' : ''}`} />
                          <span>{tab.label}</span>
                          {tab.count !== null && tab.count > 0 && (
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${tab.alert
                              ? 'bg-red-100 text-red-800'
                              : 'bg-teal-100 text-teal-800'
                              }`}>
                              {tab.count}
                            </span>
                          )}
                          {isDisabled && tab.id === "kyp" && (
                            <span className="text-xs text-gray-400">(Policy Required)</span>
                          )}
                          {isDisabled && tab.id === "discharge" && (
                            <span className="text-xs text-gray-400">(KYP Required)</span>
                          )}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {renderTabContent()}
              </div>
            </div>
          </div>

          {/* Right Side - Application Overview & Documents (40%) */}
          <div ref={rightPanelRef} className="lg:col-span-2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 pl-2" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            {showDocumentModal && selectedDocument ? (
              // Document Viewer Panel
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-full">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      Document Viewer
                    </h3>
                    <p className="text-sm text-gray-600 truncate">
                      {selectedDocument.fileName}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                  {selectedDocument.fileName.toLowerCase().endsWith(".pdf") && (
                    <div className="flex items-center space-x-1 mr-2">
                      <button
                        onClick={() => setPdfZoom((z) => Math.max(25, z - 10))}
                        className="px-2 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 flex items-center text-sm"
                        title="Zoom out"
                      >
                        <ZoomOut className="h-4 w-4" />
                      </button>
                      <span className="text-sm text-gray-700 w-12 text-center">{pdfZoom}%</span>
                      <button
                        onClick={() => setPdfZoom((z) => Math.min(300, z + 10))}
                        className="px-2 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 flex items-center text-sm"
                        title="Zoom in"
                      >
                        <ZoomIn className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => window.open(selectedDocument.url, '_blank')}
                        className="px-2 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 flex items-center text-sm"
                        title="Open PDF in new tab"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" /> Open
                      </button>
                    </div>
                  )}
                  {/\.(png|jpe?g|webp|gif)$/i.test(selectedDocument.fileName) && (
                    <div className="flex items-center space-x-1 mr-2">
                      <button
                        onClick={() => setImageZoom((z) => Math.max(25, z - 10))}
                        className="px-2 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 flex items-center text-sm"
                        title="Zoom out"
                      >
                        <ZoomOut className="h-4 w-4" />
                      </button>
                      <span className="text-sm text-gray-700 w-12 text-center">{imageZoom}%</span>
                      <button
                        onClick={() => setImageZoom((z) => Math.min(400, z + 10))}
                        className="px-2 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 flex items-center text-sm"
                        title="Zoom in"
                      >
                        <ZoomIn className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => window.open(selectedDocument.url, '_blank')}
                        className="px-2 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 flex items-center text-sm"
                        title="Open image in new tab"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" /> Open
                      </button>
                    </div>
                  )}
                    <button
                      onClick={() => downloadFile(selectedDocument.url, selectedDocument.fileName)}
                      className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2 text-sm"
                    >
                      <Download className="h-4 w-4" />
                      <span>Download</span>
                    </button>
                    <button
                      onClick={closeDocumentPanel}
                      className="text-gray-400 hover:text-gray-600 p-1"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="flex-1 p-4 overflow-hidden min-h-0">
                  {selectedDocument.fileName.toLowerCase().endsWith(".pdf") ? (
                    <iframe
                      src={`${selectedDocument.url}${selectedDocument.url.includes('#') ? '' : '#'}${selectedDocument.url.includes('zoom=') ? '' : 'zoom='}${pdfZoom}`}
                      className="w-full h-full border border-gray-300 rounded"
                      title="Document Viewer"
                    />
                  ) : selectedDocument.fileName.toLowerCase().match(/\.(xlsx?|xls|csv)$/i) ? (
                    // Inline Excel Viewer with SheetJS
                    <div className="w-full h-full flex flex-col">
                      <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded-lg flex-shrink-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium text-green-800">Excel Spreadsheet</span>
                            <span className="text-xs text-green-600">({selectedDocument.fileName})</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => window.open(selectedDocument.url, '_blank')}
                              className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                              title="Open in New Tab"
                            >
                              <ExternalLink className="h-3 w-3 inline mr-1" /> Open
                            </button>
                            <button
                              onClick={() => downloadFile(selectedDocument.url, selectedDocument.fileName)}
                              className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                              title="Download Excel File"
                            >
                              <Download className="h-3 w-3 inline mr-1" /> Download
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Sheet tabs if multiple sheets */}
                      {excelSheets.length > 1 && (
                        <div className="flex items-center space-x-2 px-2 pb-2 overflow-x-auto scrollbar-hide">
                          {excelSheets.map((sheet, index) => (
                            <button
                              key={index}
                              onClick={() => setActiveSheet(index)}
                              className={`px-3 py-1 text-xs rounded border whitespace-nowrap ${activeSheet === index
                                ? 'bg-green-600 text-white border-green-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                              title={`Switch to sheet: ${sheet.name}`}
                            >
                              {sheet.name}
                            </button>
                          ))}
                        </div>
                      )}

                      <div className="flex-1 overflow-hidden border border-gray-200 rounded bg-white relative min-h-0">
                        {excelLoading && (
                          <div className="h-full w-full flex items-center justify-center p-8">
                            <div className="text-center">
                              <RefreshCw className="h-6 w-6 animate-spin text-green-600 mx-auto mb-2" />
                              <div className="text-sm text-gray-600">Loading Excel file...</div>
                            </div>
                          </div>
                        )}

                        {excelError && (
                          <div className="h-full w-full flex items-center justify-center p-8">
                            <div className="text-center max-w-md">
                              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Excel File</h3>
                              <p className="text-sm text-red-600 mb-4">{excelError}</p>
                              <div className="flex justify-center space-x-2">
                                <button
                                  onClick={() => window.open(selectedDocument.url, '_blank')}
                                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                  Open in New Tab
                                </button>
                                <button
                                  onClick={() => downloadFile(selectedDocument.url, selectedDocument.fileName)}
                                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                                >
                                  Download File
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        {!excelLoading && !excelError && excelSheets.length > 0 && (
                          renderExcelTable(excelSheets[activeSheet]?.data)
                        )}

                        {!excelLoading && !excelError && excelSheets.length === 0 && (
                          <div className="h-full w-full flex items-center justify-center p-8 text-sm text-gray-600">
                            No sheets found in this Excel file.
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full bg-gray-50 rounded overflow-auto border border-gray-200">
                      <div
                        className="min-w-full min-h-full flex items-center justify-center"
                        style={{ padding: 16 }}
                      >
                      <img
                        src={selectedDocument.url}
                        alt={selectedDocument.fileName}
                          style={{ transform: `scale(${imageZoom / 100})`, transformOrigin: 'top left' }}
                          className="object-contain"
                      />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : showDiscussionPanel ? (
              // Discussion Panel - Improved Layout
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-full">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50 flex-shrink-0">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <MessageSquare className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Medical Discussion
                      </h3>
                      <p className="text-sm text-gray-600">
                        Communicate with the application team
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowDiscussionPanel(false)}
                      className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-white transition-colors"
                      title="Close Discussion Panel"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-hidden min-h-0">
                  <div className="h-full p-2">
                    <DiscussionComponent
                      applicationId={applicationId}
                      onUnreadCountChange={handleUnreadCountChange}
                      compact={true}
                      style={{
                        height: '100%',
                        maxHeight: 'calc(100vh - 300px)'
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              // Application Overview Sections with Discussion Toggle
              <div className="space-y-4">
                {/* Discussion Toggle Section */}
                {/* Internal Comments Section (Collapsible) */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4">
                  <button
                    onClick={() => toggleSection('internalComments')}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors border-b border-gray-200 bg-gradient-to-r from-yellow-50 to-orange-50"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="bg-yellow-100 p-2 rounded-lg">
                        <Edit className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          Internal Comments
                        </h3>
                        <p className="text-sm text-gray-600">
                          Private notes for medical team
                        </p>
                      </div>
                    </div>
                    {collapsedSections.internalComments ? (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </button>

                  {!collapsedSections.internalComments && (
                  <div className="p-4">
                    <div className="space-y-4">
                      {/* Display all doctor comments */}
                      {allInternalComments && allInternalComments.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Previous Comments from Medical Team:</h4>
                          <div className="space-y-3 max-h-60 overflow-y-auto">
                            {allInternalComments.map((comment, index) => (
                              <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                <div className="flex justify-between items-start mb-2">
                                  <span className="text-sm font-medium text-blue-600">
                                    Dr. {comment.doctorName}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {comment.timestamp?.toDate?.()?.toLocaleString() ||
                                      new Date(comment.timestamp).toLocaleString()}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.comments}</p>
                                {comment.lastModified && comment.lastModified !== comment.timestamp && (
                                  <p className="text-xs text-gray-400 mt-1">
                                    Last modified: {comment.lastModified?.toDate?.()?.toLocaleString() ||
                                      new Date(comment.lastModified).toLocaleString()}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Your Doctor Notes
                        </label>
                        <textarea
                          value={internalComments}
                          onChange={(e) => setInternalComments(e.target.value)}
                          rows="6"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 resize-none"
                          placeholder="Add your internal comments, observations, or notes about this case..."
                        />
                      </div>

                      {savedInternalComments && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <p className="text-sm font-medium text-green-800 mb-1">Your Last Saved Comments:</p>
                          <p className="text-sm text-green-700 whitespace-pre-wrap">{savedInternalComments}</p>
                        </div>
                      )}

                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => setInternalComments("")}
                          className="px-3 py-2 text-gray-600 hover:text-gray-800 text-sm"
                        >
                          Clear
                        </button>
                        <button
                          onClick={handleSaveInternalComments}
                          disabled={commentsSaving || !internalComments.trim()}
                          className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-sm"
                        >
                          {commentsSaving ? (
                            <>
                              <Loader className="h-4 w-4 animate-spin" />
                              <span>Saving...</span>
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4" />
                              <span>Save Notes</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  )}
                </div>

                {/* Medical Information Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <button
                    onClick={() => toggleSection('rightPanelMedical')}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <Stethoscope className="h-4 w-4 text-blue-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Medical Information</h3>
                    </div>
                    {collapsedSections.rightPanelMedical ? 
                      <ChevronRight className="h-5 w-5 text-gray-400" /> : 
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    }
                  </button>
                  
                  {!collapsedSections.rightPanelMedical && (
                    <div className="px-4 pb-4 border-t border-gray-100">
                      <div className="space-y-4 mt-4">
                        
                        {/* Basic Medical Information */}
                        {application.medical?.diagnosis && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">Diagnosis</label>
                            <p className="text-gray-900 bg-blue-50 p-2 rounded">{application.medical.diagnosis}</p>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-2 gap-4">
                          {application.medical?.treatmentType && (
                            <div>
                              <label className="text-sm font-medium text-gray-500">Treatment Type</label>
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                application.medical?.treatmentType === "surgical"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}>
                                {application.medical?.treatmentType?.charAt(0).toUpperCase() +
                                  application.medical?.treatmentType?.slice(1)}
                              </span>
                            </div>
                          )}
                          {application.medical?.estimatedCost && (
                            <div>
                              <label className="text-sm font-medium text-gray-500">Estimated Cost</label>
                              <p className="text-lg font-semibold text-gray-900">
                                {formatAmount(application.medical?.estimatedCost)}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Payment / Financing Preference */}
                        <div className="mt-2">
                          <label className="text-sm font-medium text-gray-500">Payment Preference</label>
                          <div className="mt-1">
                            <span
                              className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${
                                application.paymentPreference === 'pdc'
                                  ? 'bg-amber-100 text-amber-800 border-amber-200'
                                  : 'bg-indigo-100 text-indigo-800 border-indigo-200'
                              }`}
                              title={application.paymentPreference === 'pdc' ? 'Post Dated Cheques route' : 'Loan workflow route'}
                            >
                              {application.paymentPreference === 'pdc' ? 'PDC (Post Dated Cheques)' : 'Loan'}
                            </span>
                          </div>
                        </div>
                        
                        {application.medical?.symptoms && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">Symptoms</label>
                            <p className="text-gray-900 text-sm">{application.medical.symptoms}</p>
                          </div>
                        )}
                        
                        {application.medical?.duration && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">Duration</label>
                            <p className="text-gray-900 text-sm">{application.medical.duration}</p>
                          </div>
                        )}

                        {/* Treatments Section - Enhanced from DoctorReview logic */}
                        {application?.medical?.treatments && application.medical.treatments.length > 0 && (
                          <div className="mt-6">
                            <h6 className="font-medium text-gray-700 mb-3">
                              Treatment Details
                            </h6>
                            <div className="space-y-3">
                              {application.medical.treatments.map((treatment) => (
                                <div
                                  key={treatment.id}
                                  className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                                >
                                  <div className="flex justify-between items-start mb-2">
                                    <div className="flex-1">
                                      <h6 className="font-medium text-gray-800 mb-1">
                                        {treatment.diagnosisName || treatment.diagnosis || 'Diagnosis not specified'}
                                      </h6>
                                      
                                      {/* ICD Code if available */}
                                      {treatment.icdCode && (
                                        <p className="text-sm text-gray-600 mb-1">
                                          <span className="font-medium">ICD Code:</span> {treatment.icdCode}
                                          {treatment.icdDescription && (
                                            <span> - {treatment.icdDescription}</span>
                                          )}
                                        </p>
                                      )}
                                      
                                      {/* Treatment Type */}
                                      {treatment.treatmentType && (
                                        <p className="text-sm text-gray-600">
                                          <span className="font-medium">Type:</span> {treatment.treatmentType}
                                        </p>
                                      )}
                                      
                                      {/* Procedure Name */}
                                      {treatment.procedureName && (
                                        <p className="text-sm text-gray-600">
                                          <span className="font-medium">Procedure:</span> {treatment.procedureName}
                                        </p>
                                      )}
                                      
                                      {/* Description */}
                                      {treatment.description && (
                                        <p className="text-sm text-gray-600 mt-1">
                                          <span className="font-medium">Description:</span> {treatment.description}
                                        </p>
                                      )}
                                      
                                      {/* Cost if available */}
                                      {treatment.estimatedCost && (
                                        <p className="text-sm text-gray-600">
                                          <span className="font-medium">Estimated Cost:</span> {formatAmount(treatment.estimatedCost)}
                                        </p>
                                      )}
                                      
                                      {/* Review Information */}
                                      {treatment.reviewedBy && (
                                        <p className="text-xs text-gray-500 mt-2">
                                          Last reviewed by {treatment.reviewerName} on{" "}
                                          {new Date(treatment.reviewedAt?.seconds * 1000 || treatment.reviewedAt).toLocaleDateString()}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Treatment Documents */}
                                  {application?.documents?.treatmentDocs?.[treatment.id] && (
                                    <div className="border-t border-gray-200 pt-2 mt-2">
                                      <p className="text-xs text-gray-500 mb-1">Treatment Document:</p>
                                      <p className="text-sm text-blue-600">
                                        {application.documents.treatmentDocs[treatment.id].name}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* If no treatments but has medical data, show fallback */}
                        {(!application?.medical?.treatments || application.medical.treatments.length === 0) && 
                         (application.medical?.diagnosis || application.medical?.treatmentType) && (
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600">
                              Basic medical information available. Detailed treatment data not yet provided.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Discharge Documents (Collapsible) */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <button
                    onClick={() => toggleSection('dischargeDocs')}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="bg-green-100 p-2 rounded-lg">
                        <FileText className="h-4 w-4 text-green-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Discharge Documents</h3>
                      {Array.isArray(application?.dischargeDocuments) && application.dischargeDocuments.length > 0 && (
                        <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full bg-teal-100 text-teal-800">
                          {application.dischargeDocuments.length}
                        </span>
                      )}
                    </div>
                    {collapsedSections.dischargeDocs ?
                      <ChevronRight className="h-5 w-5 text-gray-400" /> :
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    }
                  </button>

                  {!collapsedSections.dischargeDocs && (
                    <div className="px-4 pb-4 border-t border-gray-100">
                      {Array.isArray(application?.dischargeDocuments) && application.dischargeDocuments.length > 0 ? (
                        <div className="space-y-2 mt-4">
                          {application.dischargeDocuments.map((doc, idx) => {
                            const docKey = `discharge_${doc.id || idx}`;
                            const queries = documentQueries[docKey] || [];
                            const openQueries = queries.filter(q => q.status === 'open');
                            return (
                            <div
                              key={doc.id || idx}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-center space-x-3 min-w-0">
                                <FileText className="h-4 w-4 text-green-600 flex-shrink-0" />
                                <div className="min-w-0">
                                  <h5 className="font-medium text-gray-900 text-sm truncate" title={doc.title || doc.fileName}>
                                    {doc.title || doc.fileName}
                                  </h5>
                                  <div className="flex items-center space-x-2 text-xs text-gray-600">
                                    {doc.type && (
                                      <span className="inline-flex px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                                        {doc.type}
                                      </span>
                                    )}
                                    <span className="truncate" title={doc.fileName}>
                                      {doc.fileName}
                                    </span>
                                  </div>
                                  {doc.description && (
                                    <p className="text-xs text-gray-600 mt-1 line-clamp-2" title={doc.description}>
                                      {doc.description}
                                    </p>
                                  )}
                                  {openQueries.length > 0 && (
                                    <div className="mt-1">
                                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">
                                        {openQueries.length} open {openQueries.length === 1 ? 'query' : 'queries'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center space-x-1 flex-shrink-0">
                                <button
                                  onClick={() => openQueryModal(docKey)}
                                  className="p-1 text-orange-600 hover:bg-orange-100 rounded transition-colors"
                                  title="Raise Query"
                                >
                                  <HelpCircle className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => openDocument(doc)}
                                  className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                                  title="View Document"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => downloadFile(doc.url, doc.fileName || 'discharge-document')}
                                  className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
                                  title="Download Document"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        </div>
                      ) : (
                        <div className="text-center py-8 mt-2">
                          <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-600 text-sm">No discharge documents uploaded yet</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Dispatch Documents Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4">
                  <button
                    onClick={() => toggleSection('dispatchDocs')}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="bg-purple-100 p-2 rounded-lg">
                        <FileText className="h-4 w-4 text-purple-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Dispatch Documents</h3>
                      {Array.isArray(application?.dispatchDocuments) && application.dispatchDocuments.length > 0 && (
                        <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
                          {application.dispatchDocuments.length}
                        </span>
                      )}
                    </div>
                    {collapsedSections.dispatchDocs ?
                      <ChevronRight className="h-5 w-5 text-gray-400" /> :
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    }
                  </button>

                  {!collapsedSections.dispatchDocs && (
                    <div className="px-4 pb-4 border-t border-gray-100">
                      {Array.isArray(application?.dispatchDocuments) && application.dispatchDocuments.length > 0 ? (
                        <div className="space-y-2 mt-4">
                          <div className="text-sm text-gray-600 mb-3">
                            Final dispatch documents (claim forms, KYC, ID proofs, etc.) uploaded by hospital for approval.
                          </div>
                          {application.dispatchDocuments.map((doc, idx) => {
                            const docKey = `dispatch_${doc.id || idx}`;
                            const queries = documentQueries[docKey] || [];
                            const openQueries = queries.filter(q => q.status === 'open');
                            return (
                            <div
                              key={doc.id || idx}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-center space-x-3 min-w-0">
                                <FileText className="h-4 w-4 text-purple-600 flex-shrink-0" />
                                <div className="min-w-0">
                                  <h5 className="font-medium text-gray-900 text-sm truncate" title={doc.title || doc.fileName}>
                                    {doc.title || doc.fileName}
                                  </h5>
                                  <div className="flex items-center space-x-2 text-xs text-gray-600">
                                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                      doc.dispatchStatus === 'approved' ? 'bg-green-100 text-green-800' :
                                      doc.dispatchStatus === 'rejected' ? 'bg-red-100 text-red-800' :
                                      'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {doc.dispatchStatus === 'approved' ? 'Approved' :
                                       doc.dispatchStatus === 'rejected' ? 'Rejected' : 'Pending Review'}
                                    </span>
                                    <span className="truncate" title={doc.fileName}>
                                      {doc.fileName}
                                    </span>
                                  </div>
                                  {doc.description && (
                                    <p className="text-xs text-gray-600 mt-1 line-clamp-2" title={doc.description}>
                                      {doc.description}
                                    </p>
                                  )}
                                  {openQueries.length > 0 && (
                                    <div className="mt-1">
                                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">
                                        {openQueries.length} open {openQueries.length === 1 ? 'query' : 'queries'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center space-x-1 flex-shrink-0">
                                <button
                                  onClick={() => openQueryModal(docKey)}
                                  className="p-1 text-orange-600 hover:text-orange-800 hover:bg-orange-100 rounded"
                                  title="Raise Query"
                                >
                                  <HelpCircle className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => openDocument(doc)}
                                  className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded"
                                  title="View Document"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => downloadFile(doc.url, doc.fileName)}
                                  className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded"
                                  title="Download Document"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        </div>
                      ) : (
                        <div className="text-center py-8 mt-2">
                          <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-600 text-sm">No dispatch documents uploaded yet</p>
                          <p className="text-xs text-gray-500 mt-1">Hospital will upload dispatch documents after FAL acceptance</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Medical Discussion */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4">
                  <button
                    onClick={() => setShowDiscussionPanel(true)}
                    className="w-full flex items-center justify-between p-4 text-left transition-all duration-200 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <MessageSquare className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          Medical Discussion
                        </h3>
                        <p className="text-sm text-gray-600">
                          Click to open communication panel
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                          {unreadCount}
                        </span>
                      )}
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </button>
                </div>

                {/* Application Overview Sections */}
                {renderCollapsibleApplicationOverview()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Discharge Item Modal */
      }
      {showAddItemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900">Add Discharge Item</h3>
              <button
                onClick={() => {
                  setShowAddItemModal(false);
                  setNewDischargeItem({ itemName: "", category: "medical", payableType: 'payable', unitRate: "", eligibleUnitRate: "", quantity: "", billedAmount: "", eligibleAmount: "", remarks: "", approved: true, _billedManual: false, _eligibleManual: false });
                }}
                className="text-gray-400 hover:text-gray-600"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Item/Service Name</label>
                <input
                  type="text"
                  value={newDischargeItem.itemName}
                  onChange={(e) => setNewDischargeItem((prev) => ({ ...prev, itemName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="e.g., Room Charges, Lab Test, Surgery"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newDischargeItem.category}
                    onChange={(e) => setNewDischargeItem((prev) => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="medical">Medical</option>
                    <option value="surgical">Surgical</option>
                    <option value="room">Room Charges</option>
                    <option value="diagnostic">Diagnostic</option>
                    <option value="pharmacy">Pharmacy</option>
                    <option value="consultation">Consultation</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payable Type</label>
                  <select
                    value={newDischargeItem.payableType}
                    onChange={(e) => setNewDischargeItem((prev) => ({ ...prev, payableType: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="payable">Payable</option>
                    <option value="non_payable">Non-Payable</option>
                    <option value="deduction">Is Deduction</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rate per Unit</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">₹</span>
                      <input
                        type="number"
                        step="0.01"
                        value={newDischargeItem.unitRate}
                        onChange={(e) => {
                          const val = e.target.value;
                          setNewDischargeItem((prev) => {
                            const qty = parseFloat(prev.quantity) || 0;
                            const unit = parseFloat(val) || 0;
                            const eligUnit = parseFloat(prev.eligibleUnitRate) || 0;
                            const next = { ...prev, unitRate: val };
                            if (!prev._billedManual) next.billedAmount = (unit * qty || 0).toString();
                            if (!prev._eligibleManual && !prev.eligibleUnitRate) next.eligibleAmount = (unit * qty || 0).toString();
                            return next;
                          });
                        }}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Eligible Rate per Unit</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">₹</span>
                      <input
                        type="number"
                        step="0.01"
                        value={newDischargeItem.eligibleUnitRate}
                        onChange={(e) => {
                          const val = e.target.value;
                          setNewDischargeItem((prev) => {
                            const qty = parseFloat(prev.quantity) || 0;
                            const eligUnit = parseFloat(val) || 0;
                            const unit = parseFloat(prev.unitRate) || 0;
                            const next = { ...prev, eligibleUnitRate: val };
                            if (!prev._eligibleManual) next.eligibleAmount = (eligUnit > 0 ? eligUnit : unit) * qty || 0;
                            if (!prev._eligibleManual) next.eligibleAmount = next.eligibleAmount.toString();
                            return next;
                          });
                        }}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newDischargeItem.quantity}
                    onChange={(e) => {
                      const val = e.target.value;
                      setNewDischargeItem((prev) => {
                        const qty = parseFloat(val) || 0;
                        const unit = parseFloat(prev.unitRate) || 0;
                        const eligUnit = parseFloat(prev.eligibleUnitRate) || 0;
                        const next = { ...prev, quantity: val };
                        if (!prev._billedManual) next.billedAmount = (unit * qty || 0).toString();
                        if (!prev._eligibleManual) next.eligibleAmount = ((eligUnit || unit) * qty || 0).toString();
                        return next;
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="e.g., 1, 2, 3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Billed (optional)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">₹</span>
                    <input
                      type="number"
                      step="0.01"
                      value={newDischargeItem.billedAmount}
                      onFocus={() => setNewDischargeItem((prev) => ({ ...prev, _billedManual: true }))}
                      onChange={(e) => setNewDischargeItem((prev) => ({ ...prev, billedAmount: e.target.value, _billedManual: true }))}
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Auto if left blank"
                    />
                  </div>
                  {itemErrors?.billedAmount && (
                    <p className="mt-1 text-xs text-red-600">{itemErrors.billedAmount}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Eligible (optional)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">₹</span>
                    <input
                      type="number"
                      step="0.01"
                      value={newDischargeItem.eligibleAmount}
                      onFocus={() => setNewDischargeItem((prev) => ({ ...prev, _eligibleManual: true }))}
                      onChange={(e) => setNewDischargeItem((prev) => ({ ...prev, eligibleAmount: e.target.value, _eligibleManual: true }))}
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Auto if left blank"
                    />
                  </div>
                  {itemErrors?.eligibleAmount && (
                    <p className="mt-1 text-xs text-red-600">{itemErrors.eligibleAmount}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks{(newDischargeItem.payableType === 'non_payable' || newDischargeItem.payableType === 'deduction') && <span className="text-red-500"> *</span>}</label>
                <textarea
                  value={newDischargeItem.remarks}
                  onChange={(e) => setNewDischargeItem((prev) => ({ ...prev, remarks: e.target.value }))}
                  rows="3"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${itemErrors?.remarks ? 'border-red-300' : 'border-gray-300'}`}
                  placeholder={newDischargeItem.payableType === 'non_payable' || newDischargeItem.payableType === 'deduction' ? 'Required: add reason for non-payable/deduction' : 'Any notes or comments about this item'}
                />
                {itemErrors?.remarks && (
                  <p className="mt-1 text-xs text-red-600">{itemErrors.remarks}</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  id="approved"
                  type="checkbox"
                  checked={!!newDischargeItem.approved}
                  onChange={(e) => setNewDischargeItem((prev) => ({ ...prev, approved: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <label htmlFor="approved" className="ml-2 text-sm text-gray-700">Mark as Approved</label>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowAddItemModal(false);
                  setNewDischargeItem({ itemName: "", category: "medical", payableType: 'payable', unitRate: "", eligibleUnitRate: "", quantity: "", billedAmount: "", eligibleAmount: "", remarks: "", approved: true, _billedManual: false, _eligibleManual: false });
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDischargeItem}
                disabled={!newDischargeItem.itemName?.trim()}
                className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Discharge Item Modal */}
      {showEditItemModal && editDischargeItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900">Edit Discharge Item</h3>
              <button
                onClick={() => { setShowEditItemModal(false); setEditDischargeItem(null); }}
                className="text-gray-400 hover:text-gray-600"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Item/Service Name</label>
                <input
                  type="text"
                  value={editDischargeItem.itemName}
                  onChange={(e) => setEditDischargeItem((prev) => ({ ...prev, itemName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="e.g., Room Charges, Lab Test, Surgery"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={editDischargeItem.category}
                    onChange={(e) => setEditDischargeItem((prev) => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="medical">Medical</option>
                    <option value="surgical">Surgical</option>
                    <option value="room">Room Charges</option>
                    <option value="diagnostic">Diagnostic</option>
                    <option value="pharmacy">Pharmacy</option>
                    <option value="consultation">Consultation</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payable Type</label>
                  <select
                    value={editDischargeItem.payableType}
                    onChange={(e) => setEditDischargeItem((prev) => ({ ...prev, payableType: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="payable">Payable</option>
                    <option value="non_payable">Non-Payable</option>
                    <option value="deduction">Is Deduction</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rate per Unit</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">₹</span>
                      <input
                        type="number"
                        step="0.01"
                        value={editDischargeItem.unitRate}
                        onChange={(e) => setEditDischargeItem((prev) => ({ ...prev, unitRate: e.target.value }))}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Eligible Rate per Unit</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">₹</span>
                      <input
                        type="number"
                        step="0.01"
                        value={editDischargeItem.eligibleUnitRate}
                        onChange={(e) => setEditDischargeItem((prev) => ({ ...prev, eligibleUnitRate: e.target.value }))}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editDischargeItem.quantity}
                    onChange={(e) => setEditDischargeItem((prev) => ({ ...prev, quantity: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="e.g., 1, 2, 3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Billed (optional)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">₹</span>
                    <input
                      type="number"
                      step="0.01"
                      value={editDischargeItem.billedAmount}
                      onChange={(e) => setEditDischargeItem((prev) => ({ ...prev, billedAmount: e.target.value }))}
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Auto if left blank"
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Auto: ₹{(((parseFloat(editDischargeItem.unitRate) || 0) * (parseFloat(editDischargeItem.quantity) || 0)) || 0).toLocaleString('en-IN')}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Eligible (optional)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">₹</span>
                    <input
                      type="number"
                      step="0.01"
                      value={editDischargeItem.eligibleAmount}
                      onChange={(e) => setEditDischargeItem((prev) => ({ ...prev, eligibleAmount: e.target.value }))}
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Auto if left blank"
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Auto: ₹{((((parseFloat(editDischargeItem.eligibleUnitRate) || 0) || (parseFloat(editDischargeItem.unitRate) || 0)) * (parseFloat(editDischargeItem.quantity) || 0)) || 0).toLocaleString('en-IN')}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks{(editDischargeItem.payableType === 'non_payable' || editDischargeItem.payableType === 'deduction') && <span className="text-red-500"> *</span>}</label>
                <textarea
                  value={editDischargeItem.remarks}
                  onChange={(e) => setEditDischargeItem((prev) => ({ ...prev, remarks: e.target.value }))}
                  rows="3"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${itemErrors?.remarks ? 'border-red-300' : 'border-gray-300'}`}
                  placeholder={editDischargeItem.payableType === 'non_payable' || editDischargeItem.payableType === 'deduction' ? 'Required: add reason for non-payable/deduction' : 'Any notes or comments about this item'}
                />
                {itemErrors?.remarks && (
                  <p className="mt-1 text-xs text-red-600">{itemErrors.remarks}</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  id="approvedEdit"
                  type="checkbox"
                  checked={!!editDischargeItem.approved}
                  onChange={(e) => setEditDischargeItem((prev) => ({ ...prev, approved: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <label htmlFor="approvedEdit" className="ml-2 text-sm text-gray-700">Mark as Approved</label>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => { setShowEditItemModal(false); setEditDischargeItem(null); }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEditDischargeItem}
                disabled={!editDischargeItem.itemName?.trim()}
                className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KYP Preview Modal */}
      {showKYPPreview && kypPreviewData && (
        <KYPPreviewModal
          isOpen={showKYPPreview}
          onClose={() => {
            setShowKYPPreview(false);
            setKYPPreviewData(null);
          }}
          application={kypPreviewData.application}
          kypData={kypPreviewData.kypData}
          leadGeneratorData={kypPreviewData.leadGeneratorData}
          actions={[
            // Show quick submit for revised KYP only when a reassignment exists and no approval is set yet
            ...(application?.workflow?.medicalVerification?.kypReassignment && !application?.workflow?.medicalVerification?.kypApproval
              ? [
                  {
                    label: 'Approve & Send to Hospital',
                    variant: 'success',
                    disabled: kypApprovalLoading,
                    onClick: async () => {
                      if (!window.confirm('Approve revised KYP and notify hospital team for acknowledgement?')) return;
                      try {
                        setKypApprovalLoading(true);
                        const res = await setKYPApproval(applicationId, user.uid, {
                          status: 'approved',
                          doctorName: user.profile?.name || user.email,
                          approvedAt: new Date()
                        });
                        if (!res?.success) throw new Error(res?.error || 'Failed');
                        await loadApplicationData();
                        setShowKYPPreview(false);
                      } catch (e) {
                        alert(e.message || 'Failed to approve revised KYP');
                      } finally {
                        setKypApprovalLoading(false);
                      }
                    }
                  },
                  {
                    label: 'Reject Revised KYP',
                    variant: 'danger',
                    disabled: kypApprovalLoading,
                    onClick: async () => {
                      const reason = prompt('Please provide reason for rejecting the revised KYP:', '') || '';
                      if (reason === '' && !window.confirm('Reject without a reason?')) return;
                      try {
                        setKypApprovalLoading(true);
                        const res = await setKYPApproval(applicationId, user.uid, {
                          status: 'rejected',
                          reason,
                          doctorName: user.profile?.name || user.email,
                          rejectedAt: new Date()
                        });
                        if (!res?.success) throw new Error(res?.error || 'Failed');
                        await loadApplicationData();
                        setShowKYPPreview(false);
                      } catch (e) {
                        alert(e.message || 'Failed to reject revised KYP');
                      } finally {
                        setKypApprovalLoading(false);
                      }
                    }
                  }
                ]
              : [])
          ]}
        />
      )}

      {/* FAL Preview Modal */}
      {showFALPreview && application && (
        <FALPreviewModal
          isOpen={showFALPreview}
          onClose={() => setShowFALPreview(false)}
          application={application}
          remarks={falRemarks}
          canDownload={true}
          leadGeneratorData={leadGeneratorData}
        />
      )}

      {/* Document Query Modal */}
      {showQueryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-orange-100 p-2 rounded-lg">
                    <HelpCircle className="h-5 w-5 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Raise Document Query</h3>
                </div>
                <button
                  onClick={() => setShowQueryModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document
                </label>
                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                  {queryDocumentKey ? queryDocumentKey.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()) : ''}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comment / Query <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={queryComment}
                  onChange={(e) => setQueryComment(e.target.value)}
                  placeholder="Enter your query or concern about this document..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  rows={4}
                />
              </div>

              {documentQueries[queryDocumentKey]?.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Previous Queries</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {documentQueries[queryDocumentKey].map((q) => (
                      <div key={q.id} className="bg-gray-50 p-2 rounded text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-900">{q.raisedBy?.name}</span>
                          <span className={`px-2 py-0.5 rounded-full ${
                            q.status === 'open' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {q.status}
                          </span>
                        </div>
                        <p className="text-gray-700">{q.comment}</p>
                        {q.replies?.length > 0 && (
                          <p className="mt-1 text-gray-600">{q.replies.length} {q.replies.length === 1 ? 'reply' : 'replies'}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowQueryModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRaiseQuery}
                disabled={!queryComment.trim() || actionLoading['raise_query']}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {actionLoading['raise_query'] && <Loader className="h-4 w-4 animate-spin" />}
                <span>Raise Query</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorApplicationReview;